import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { money } from '../lib/format';
import type {
  AIArchetype,
  AIArchetypeChoice,
  AuctionState,
  AttentionState,
  BlackSwanEvent,
  BlackSwanState,
  BotBroadcast,
  CreditRating,
  District,
  EmergencySettlement,
  GameEvent,
  GameStateTurnPhase,
  InsolvencyDesk,
  InsurancePolicy,
  Investment,
  InvestmentTier,
  LobbyConfig,
  Loan,
  LoanStructure,
  MarketPhase,
  MarketState,
  MoneyTransfer,
  OfferChoice,
  Player,
  PlayerFinancialStats,
  PropertyTile,
  ProposalState,
  ShareholderRecord,
  TileType,
  TradeOffer,
  TradeToast,
  TradeVerdictReason,
  VictoryCondition,
} from '../types/gameTypes';
import {
  DISTRICT_GROUPS,
  MAX_DISTRICT_TILES,
  MIN_DISTRICT_TILES,
  DEFAULT_RENT_YIELD,
  MIN_RENT_YIELD,
  MAX_RENT_YIELD,
  yieldRateOf,
  LOAN_BASE_RATE,
  BALLOON_RISK_PREMIUM,
  PREPAYMENT_FEE,
  INVESTMENT_EXIT_PENALTY,
  LOAN_STRUCTURE_LABEL,
  INVESTMENT_TIER_META,
  INSURANCE_POLICY_META,
  CREDIT_RATING_META,
  DEFAULT_CREDIT_SCORE,
  REFINANCE_CREDIT_PENALTY,
  REFINANCE_RATE_PENALTY,
  FORECLOSURE_PENALTY,
  creditRatingOf,
  emptyFinancialStats,
  CREDIT_SCORE_IMPROVEMENT_PER_PAYMENT,
  REFINANCE_CREDIT_PENALTY as REFINANCE_CREDIT_PENALTY_TYPE,
  ASSET_SALE_CREDIT_PENALTY,
} from '../types/gameTypes';
import {
  baseInterestRateFor,
  earlyPayoffOf,
  investmentExitPenalty,
  quoteLoan,
  rollInvestmentYield,
} from '../lib/finance';
import { t as tr, tTile } from '../i18n';
import { profileOf, purchaseBudgetOf } from '../lib/archetypes';
import type { ArchetypeProfile } from '../lib/archetypes';
import {
  CARTEL_SWEETHEART_RATIO,
  LEADER_EMBARGO_MULTIPLIER,
  detectMarketLeader,
  findSynergyDeals,
  insuranceScanOf,
  insuranceTriggered,
  planGrowthLoan,
  planTreasury,
  resolveCounterOffer,
  shouldBotTrade,
} from '../lib/botTrading';
import type { BotDeal, BotTradeOutcome } from '../lib/botTrading';

export type {
  AuctionState,
  BotBroadcast,
  District,
  EmergencySettlement,
  GameEvent,
  GameStateTurnPhase,
  LobbyConfig,
  MarketPhase,
  MarketState,
  MoneyTransfer,
  OfferChoice,
  Player,
  PropertyTile,
  ProposalState,
  ShareholderRecord,
  TileType,
  TradeToast,
  VictoryCondition,
};

export const BASE_SHARES = 100;
export const START_PASS_PAYROLL = 2000;
export const IDLE_PENALTY_PER_STEP = 100;
export const CRASH_RENT_MULTIPLIER = 2;
export const MARGIN_CALL_DISCOUNT = 0.25;
export const EXIT_FEE = 0.05;
export const FIRE_SALE_DISCOUNT = 0.1;
export const DEBT_CEILING_RATIO = 0.5;
export const WIN_NET_WORTH = 100000;
export const INITIAL_CASH = 2500;
/** How long a money-transfer event stays alive for the flying-cash animation. */
export const MONEY_TRANSFER_TTL_MS = 1600;
/** Sentinel id for the board / bank (no player card to fly from or to). */
export const BANK_ID = 'BANK';

/** Financial Desk tabs. */
export type FinancialDeskTab = 'LOANS' | 'INVESTMENTS' | 'INSURANCE';

/**
 * Complete, serializable match state written to Firestore under
 * `users/{uid}/games/activeGame` — players, board/tiles, market cycle, round, auction
 * and ledger all travel together so a resumed game is bit-identical.
 */
export interface CloudGameSnapshot {
  schema: string;
  version: number;
  savedAt: number;
  gameStarted: boolean;
  lobby: LobbyConfig;
  victory: VictoryCondition;
  board: PropertyTile[];
  players: Player[];
  market: MarketState;
  currentPlayerIndex: number;
  turnPhase: GameStateTurnPhase;
  events: GameEvent[];
  loans: Loan[];
  investments: Investment[];
  auction: AuctionState;
  pendingOffer: OfferChoice | null;
  emergency: EmergencySettlement | null;
  proposal: ProposalState;
  takeoverTileId: string | null;
  lastTakeoverRound: number;
  expansionPending: boolean;
  gameOver: boolean;
  winnerId: string | null;
  metroRiddenThisTurn: boolean;
  selectedTileIndex: number | null;
  movementPoints: number;
  pushLuckUsed: boolean;
  crashRiskArmed: boolean;
  idlePenalty: number;
  reachableIndices: number[];
}

/** Version of the cloud snapshot schema (bump when a field is renamed/removed). */
export const CLOUD_SNAPSHOT_VERSION = 2;


/** Monotonic id source for structured loans. */
let loanSeq = 0;
/** One bot-to-bot negotiation per round across the whole table. */
let lastBotBotTradeRound = -1;
/** One atmospheric cartel-intel line per round (avoids ticker spam). */
let lastCartelIntelRound = -1;
/** Monotonic id source for annexed parcels (ids must stay unique even mid-round). */
let expansionSeq = 0;

type TileSeed = [string, District, TileType, number, number];

// 28 tile loop: 1 payroll, 1 audit, 4 transit, 2 financial centers, 20 commercial.
// The loop is rendered as an 8x8 ring, so tiles 0 / 7 / 14 / 21 land on the four corners:
// Payroll Plaza (GO), Audit & Tax Bureau (tax), Investment Bank and Securities Exchange (financial).
// Colour groups are balanced 5/5/5/5 (every commercial district holds between
// MIN_DISTRICT_TILES and MAX_DISTRICT_TILES properties — see `rebalanceDistricts`).
export const BOARD_SEEDS: TileSeed[] = [
  ['Payroll Plaza', 'SPECIAL', 'SPECIAL', 0, 0], // corner 1 — GO
  ['Townhouse', 'DISTRICT_A', 'COMMERCIAL', 500, 25],
  ['Corner Shop', 'DISTRICT_A', 'COMMERCIAL', 550, 30],
  ['Station North', 'TRANSIT', 'TRANSIT', 0, 0],
  ['Bakery', 'DISTRICT_A', 'COMMERCIAL', 600, 35],
  ['Cafe', 'DISTRICT_A', 'COMMERCIAL', 650, 40],
  ['Bookstore', 'DISTRICT_A', 'COMMERCIAL', 700, 45],
  ['Audit & Tax Bureau', 'SPECIAL', 'SPECIAL', 0, 0], // corner 2 — progressive asset tax
  ['Bank Building', 'DISTRICT_B', 'COMMERCIAL', 800, 60],
  ['Investment Firm', 'DISTRICT_B', 'COMMERCIAL', 850, 65],
  ['Station East', 'TRANSIT', 'TRANSIT', 0, 0],
  ['Stock Exchange', 'DISTRICT_B', 'COMMERCIAL', 900, 70],
  ['Insurance HQ', 'DISTRICT_B', 'COMMERCIAL', 950, 75],
  ['Startup Incubator', 'DISTRICT_C', 'COMMERCIAL', 1100, 90],
  ['Investment Bank', 'FINANCIAL', 'FINANCIAL', 0, 0], // corner 3 — financial desk
  ['Tech Hub', 'DISTRICT_C', 'COMMERCIAL', 1150, 95],
  ['Station South', 'TRANSIT', 'TRANSIT', 0, 0],
  ['AI Lab', 'DISTRICT_C', 'COMMERCIAL', 1200, 100],
  ['Bio Center', 'DISTRICT_C', 'COMMERCIAL', 1250, 110],
  // Harbour used to carry six properties; this surplus tile was reassigned to the
  // adjacent Metropolis cluster so both groups sit at exactly five.
  ['Metropolis Tower', 'DISTRICT_D', 'COMMERCIAL', 1450, 140],
  ['Skyscraper', 'DISTRICT_D', 'COMMERCIAL', 1600, 180],
  ['Securities Exchange', 'FINANCIAL', 'FINANCIAL', 0, 0], // corner 4 — financial desk
  ['Station West', 'TRANSIT', 'TRANSIT', 0, 0],
  ['Metropolis Mall', 'DISTRICT_D', 'COMMERCIAL', 1750, 210],
  ['Capital Center', 'DISTRICT_D', 'COMMERCIAL', 2000, 260],
  ['Luxury Tower', 'DISTRICT_D', 'COMMERCIAL', 1500, 150],
  ['Suburb Terraces', 'DISTRICT_B', 'COMMERCIAL', 750, 50],
  ['Growth Quarter', 'DISTRICT_C', 'COMMERCIAL', 1300, 120],
];

export const buildBoard = (): PropertyTile[] =>
  rebalanceDistricts(
    BOARD_SEEDS.map((seed, index) => {
      const [name, district, tileType, baseCost, baseRent] = seed;
      const valuation = tileType === 'COMMERCIAL' ? baseCost : 0;
      const rentYieldRate = tileType === 'COMMERCIAL' ? DEFAULT_RENT_YIELD : 0;
      return {
        id: `tile-${index}`,
        name,
        district,
        tileType,
        baseCost,
        baseRent,
        upgradeLevel: 0,
        totalShares: BASE_SHARES,
        shares: {} as ShareholderRecord,
        upgradeCost: tileType === 'COMMERCIAL' ? Math.round(baseCost * 0.5) : 0,
        valuation,
        valuationDeltaPct: 0,
        isFrozen: false,
        isExpansionTile: false,
        rentYieldRate,
        rent: Math.round(valuation * rentYieldRate),
      };
    }),
  );

const INITIAL_MARKET: MarketState = {
  currentRound: 1,
  maxRounds: 0, // 0 = endless mode, no round cap
  phase: 'STAGNATION',
  rentMultiplier: 1,
  assetValuationMultiplier: 1,
  interestRate: 0.05,
};

export const DISTRICT_SHARE_PRICE: Record<string, number> = {
  DISTRICT_A: 20,
  DISTRICT_B: 40,
  DISTRICT_C: 80,
  DISTRICT_D: 130,
  TRANSIT: 40,
  FINANCIAL: 60,
  SPECIAL: 20,
};

/** Tiered ladder: Brown 20 / LightBlue 40 / Pink 60 / Orange 80 / Red 100 / Yellow 130 / Green 160 / DarkBlue 200. Mapped onto the 4 in-game districts + specials. */
export const pricePerShareOf = (tile: PropertyTile): number => {
  if (tile.tileType !== 'COMMERCIAL') return 0;
  return DISTRICT_SHARE_PRICE[tile.district] ?? Math.max(1, Math.round(tile.baseCost / tile.totalShares));
};

/** Voluntary secondary-market sale: 5% city exit fee, shares return to float. */
export const sellQuoteOf = (tile: PropertyTile, shares: number): { gross: number; net: number; unit: number } => {
  const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
  const gross = shares * unit;
  return { gross: Math.round(gross), net: Math.round(gross * (1 - EXIT_FEE)), unit };
};
/** Emergency fire-sale quote at 10% distress discount. */
export const fireSaleQuoteOf = (tile: PropertyTile, shares: number): { gross: number; net: number; unit: number } => {
  const unit = (valuationLiveOf(tile) / Math.max(1, tile.totalShares)) * (1 - FIRE_SALE_DISCOUNT);
  const gross = shares * unit;
  return { gross: Math.round(gross), net: Math.round(gross), unit };
};
export const floatSharesOf = (tile: PropertyTile): number =>
  Math.max(0, tile.totalShares - Object.values(tile.shares).reduce((sum, v) => sum + v, 0));

// ---- District sets (colour groups) — sizes are ALWAYS derived from the live board ----
/** Every commercial tile of a colour group, straight from the active board (no magic numbers). */
export const getDistrictTiles = (tiles: PropertyTile[], district: District): PropertyTile[] =>
  tiles.filter((t) => t.district === district && t.tileType === 'COMMERCIAL');

/** How many properties the colour group actually contains on this board. */
export const getDistrictTotal = (tiles: PropertyTile[], district: District): number =>
  getDistrictTiles(tiles, district).length;

// ---- Colour-group balancing: every commercial district holds 3–5 properties ----
/** Structural ceiling of the board: 6 clusters × 5 properties. */
export const MAX_COMMERCIAL_TILES = MAX_DISTRICT_TILES * DISTRICT_GROUPS.length;

export interface DistrictBalanceEntry {
  district: District;
  total: number;
  /** True when the group sits inside the 3–5 corridor. */
  balanced: boolean;
}

/** Audit of the live board: which colour groups break the 3–5 corridor. */
export const auditDistrictBalance = (tiles: PropertyTile[]): DistrictBalanceEntry[] =>
  DISTRICT_GROUPS.map((district) => {
    const total = getDistrictTotal(tiles, district);
    return { district, total, balanced: total === 0 || (total >= MIN_DISTRICT_TILES && total <= MAX_DISTRICT_TILES) };
  }).filter((entry) => entry.total > 0);

/**
 * Enforces the 3–5 property corridor on every commercial colour group.
 *
 * Targets are chosen so the corridor is met while preserving existing membership:
 * the number of clusters only grows when the board physically needs another one
 * (annexations), and surplus tiles always land in the emptiest cluster — which is
 * how the balanced secondary clusters (Expansion Belt / Outer Rim) come alive.
 */
export const rebalanceDistricts = (tiles: PropertyTile[]): PropertyTile[] => {
  const commercial = tiles.filter((t) => t.tileType === 'COMMERCIAL');
  const count = commercial.length;
  if (count === 0) return tiles;

  const activeGroups = DISTRICT_GROUPS.filter((d) => commercial.some((t) => t.district === d)).length;
  let groups = Math.max(activeGroups, Math.ceil(count / MAX_DISTRICT_TILES));
  if (count < groups * MIN_DISTRICT_TILES) groups = Math.max(1, Math.floor(count / MIN_DISTRICT_TILES));
  if (groups <= 0 || groups > DISTRICT_GROUPS.length) return tiles; // board outgrew the cluster pool

  const pool = DISTRICT_GROUPS.slice(0, groups);
  const base = Math.floor(count / groups);
  const remainder = count % groups;
  const targets: Record<string, number> = {};
  pool.forEach((district, index) => {
    targets[district] = base + (index < remainder ? 1 : 0);
  });

  const assigned: Record<string, District> = {};
  const remaining: Record<string, number> = { ...targets };

  // Pass 1 — a tile stays in its own group while that group still has quota.
  commercial.forEach((tile) => {
    const quota = remaining[tile.district];
    if (quota !== undefined && quota > 0) {
      assigned[tile.id] = tile.district;
      remaining[tile.district] = quota - 1;
    }
  });

  // Pass 2 — surplus tiles move to the cluster that is still below target.
  commercial
    .filter((tile) => assigned[tile.id] === undefined)
    .forEach((tile) => {
      const target = pool.find((district) => (remaining[district] ?? 0) > 0);
      if (!target) return;
      assigned[tile.id] = target;
      remaining[target] -= 1;
    });

  if (commercial.every((tile) => !assigned[tile.id] || assigned[tile.id] === tile.district)) return tiles;
  return tiles.map((tile) => {
    const next = assigned[tile.id];
    return next && next !== tile.district ? { ...tile, district: next } : tile;
  });
};

/** Guard used by Urban Expansion: annex only while the corridor can still hold it. */
export const canAnnexCommercialTiles = (tiles: PropertyTile[], extra = 1): boolean =>
  tiles.filter((t) => t.tileType === 'COMMERCIAL').length + extra <= MAX_COMMERCIAL_TILES;

export interface DistrictSetProgress {
  district: District;
  tiles: PropertyTile[];
  total: number;
  /** Tiles the player controls (>50 % of the issued shares). */
  owned: number;
  /** Share of the colour group controlled, 0–100. */
  pct: number;
  /** True when the player controls 100 % of the ACTUAL tiles in the group. */
  complete: boolean;
}

export const districtSetOf = (tiles: PropertyTile[], playerId: string, district: District): DistrictSetProgress => {
  const group = getDistrictTiles(tiles, district);
  // Monopoly = player owns >50 % of ALL shares in the district combined.
  const totalShares = group.reduce((sum, t) => sum + t.totalShares, 0);
  const playerShares = group.reduce((sum, t) => sum + (t.shares[playerId] ?? 0), 0);
  const pct = totalShares > 0 ? (playerShares / totalShares) * 100 : 0;
  return { district, tiles: group, total: totalShares, owned: playerShares, pct, complete: pct > 50 };
};

/** All colour groups a player holds at least one share in, ordered by board appearance. */
export const districtsHeldBy = (tiles: PropertyTile[], playerId: string): District[] => {
  const held: District[] = [];
  for (const tile of tiles) {
    if (tile.tileType !== 'COMMERCIAL') continue;
    if ((tile.shares[playerId] ?? 0) <= 0) continue;
    if (!held.includes(tile.district)) held.push(tile.district);
  }
  return held;
};

export const maxAffordableShares = (tile: PropertyTile, cash: number): number => {
  const price = pricePerShareOf(tile);
  if (price <= 0) return 0;
  return Math.min(floatSharesOf(tile), Math.floor(cash / price));
};
const freshPlayerFields = () => ({
  insuranceTurnsRemaining: 0,
  insuranceCoverageRate: 0,
  insurancePolicy: null as Player['insurancePolicy'],
  creditScore: DEFAULT_CREDIT_SCORE,
  financialStats: emptyFinancialStats(),
  ledger: [] as Player['ledger'],
});

export const buildPlayers = (): Player[] => [
  { id: 'player-1', name: 'You (Human)', isAI: false, cash: INITIAL_CASH, debt: 0, currentTileIndex: 0, color: '#ff6b6b', isBankrupt: false, ...freshPlayerFields() },
  { id: 'player-2', name: 'Venture Bot', isAI: true, aiArchetype: 'AGGRESSIVE', cash: INITIAL_CASH, debt: 0, currentTileIndex: 0, color: '#4ecdc4', isBankrupt: false, ...freshPlayerFields() },
  { id: 'player-3', name: 'Prudence Bot', isAI: true, aiArchetype: 'CONSERVATIVE', cash: INITIAL_CASH, debt: 0, currentTileIndex: 0, color: '#ffe66d', isBankrupt: false, ...freshPlayerFields() },
  { id: 'player-4', name: 'Vulture Bot', isAI: true, aiArchetype: 'OPPORTUNISTIC', cash: INITIAL_CASH, debt: 0, currentTileIndex: 0, color: '#118ab2', isBankrupt: false, ...freshPlayerFields() },
];

export interface GameStore {
  // Board + world state
  board: PropertyTile[];
  players: Player[];
  market: MarketState;
  currentPlayerIndex: number;
  turnPhase: GameStateTurnPhase;
  events: GameEvent[];

  // Interaction state
  selectedTileIndex: number | null;
  /** Tile whose inspector modal is open (can be driven from the sidebar portfolio). */
  inspectTileIndex: number | null;
  setInspectTile: (index: number | null) => void;
  movementPoints: number;
  pushLuckUsed: boolean;
  crashRiskArmed: boolean;
  idlePenalty: number;
  reachableIndices: number[];
  auction: AuctionState;
  pendingOffer: OfferChoice | null;
  emergency: EmergencySettlement | null;
  /** Insolvency Resolution Desk — pauses the loop while a HUMAN debtor decides. */
  insolvency: InsolvencyDesk | null;
  fastTravelFrom: number | null;
  metroRiddenThisTurn: boolean;
  takeoverTileId: string | null;
  lastTakeoverRound: number;
  blackSwan: BlackSwanState;
  /** P2P bilateral trade negotiation in flight. */
  tradeOffer: TradeOffer | null;
  /** Attention Intercept gate — freezes the game loop. */
  attention: AttentionState | null;
  /** FSM phase suspended by the attention / trade gate, restored on acknowledgement. */
  resumePhase: GameStateTurnPhase | null;
  /** Play-by-play bot turn broadcast (store-owned: no writes from render effects). */
  botStatus: BotBroadcast | null;
  /** Live cash-movement telemetry driving the flying-cash animation (never persisted). */
  moneyTransfers: MoneyTransfer[];
  /** Toast raised whenever cash or shares change hands after an accepted trade. */
  tradeToast: TradeToast | null;
  /** Brief floating toast announced whenever a bot buys shares. */
  botPurchaseToast: { id: number; text: string; color: string } | null;
  /** Spotlight queue: freshly zoned expansion properties awaiting the player's ack. */
  newPropertyAlerts: PropertyTile[];
  /** Tile id glowing on the board after its spotlight alert was dismissed (5 s). */
  spotlightTileId: string | null;
  /** Last settled rent that credited another player (drives tile pulse + cash toast). */
  lastRentCredit: { tileId: string; tileIndex: number; payerId: string; payerName: string; total: number; credits: { playerId: string; playerName: string; amount: number }[]; seq: number } | null;
  /** Monotonic id for lastRentCredit so UI effects fire once per settlement. */
  rentSeq: number;
  /** One bilateral bot proposal per bot per round, so the intercept cannot loop. */
  lastBotTradeRound: number;
  proposal: ProposalState;
  expansionPending: boolean;
  gameOver: boolean;
  winnerId: string | null;
  /** Bumped on reset so the board remounts and pawns snap back to the start. */
  epoch: number;
  /** Pre-game lobby gate: when false the setup screen replaces the board. */
  gameStarted: boolean;
  /** Last configuration used/edited in the lobby. */
  lobby: LobbyConfig;
  /** Victory condition chosen in the lobby. */
  victory: VictoryCondition;
  /** Pass-the-device interstitial payload (hotseat): who is next + what they missed. */
  handover: { playerId: string; summary: string[] } | null;
  /** Hotseat human→human trade awaiting review by the receiving player at the same PC. */
  hotseatReview: TradeOffer | null;

  // Action points
  setTurnPhase: (phase: GameStateTurnPhase) => void;
  nextTurn: () => void;
  /** 🛟 Watchdog fail-safe: force-resolve a stalled bot turn and advance. */
  botForceAdvance: () => void;
  rollMovementPoints: (max: number) => void;
  selectStopTile: (tileIndex: number) => void;
  confirmStop: () => void;
  pushYourLuck: () => void;
  finishTurn: () => void;
  resolveTile: () => void;
  previewTileImpact: (tileIndex: number) => { distance: number; impact: number; label: string; safeChance: number };

  // Market + expansion
  updateMarketCycle: () => void;
  checkExpansion: () => void;
  triggerExpansion: () => void;
  /** Game-length setting: 0 = Endless / target $100k, >0 = classic round cap. */
  setGameLength: (maxRounds: number) => void;

  // Auctions
  offerShares: (propertyId: string, tranche: number) => void;
  clearOffer: () => void;
  openAuction: (propertyId: string, tranche: number) => void;
  placeBid: (playerId: string, amount: number) => void;
  foldHumanAuction: () => void;
  setThinking: (playerId: string | null) => void;
  tickAuction: () => void;
  endAuction: () => void;

  // Governance
  proposeUpgrade: (propertyId: string, proposerId: string, targetLevel: number) => void;
  voteOnProposal: (playerId: string, vote: 'FOR' | 'AGAINST') => void;
  executeUpgrade: (propertyId: string, targetLevel: number, cost: number, newShares: number) => void;
  resetProposal: () => void;

  // Equity + finance helpers
  addShares: (playerId: string, propertyId: string, shares: number, pricePerShare: number) => void;
  removeShares: (playerId: string, propertyId: string, shares: number, pricePerShare: number) => void;
  /** Forced sale for bankruptcy/foreclosure — allowed to sell to public float. */
  forceSellShares: (playerId: string, propertyId: string, shares: number) => void;
  /** Player-initiated sale: must be to another player (trade) or auction. No void sales. */
  sellShares: (playerId: string, propertyId: string, shares: number) => boolean;
  transferCash: (fromId: string, toId: string, amount: number) => void;
  addDebt: (playerId: string, amount: number) => void;
  borrow: (playerId: string, amount: number) => void;
  /** Safe in-auction borrowing: grants cash within the debt ceiling without touching auction state or timers. */
  takeLoan: (playerId: string, amount: number) => void;
  collectDividend: (playerId: string, amount: number) => void;
  setBankrupt: (playerId: string) => void;
  runMarginCalls: () => void;
  // Emergency insolvency ladder: swap → fire-sale → true bankruptcy.
  raiseEmergency: (debtorId: string, amountDue: number, creditorShares: EmergencySettlement['creditorShares'], label: string, tileId: string | null) => boolean;
  /** Rent squeeze Option A: draw a credit line to cover the deficit (within the ceiling). */
  emergencyBorrow: () => void;
  /** Rent squeeze Option B: withdraw treasury funds to cover the deficit. */
  emergencyWithdraw: (tier: InvestmentTier) => void;
  /** Rent squeeze CTA: once cash covers the amount, pay the liability in cash to creditors. */
  emergencyPayCash: () => void;
  clearEmergency: () => void;
  emergencyFireSale: () => void;
  emergencySwap: (allocations: { tileId: string; shares: number; creditorId: string }[]) => void;
  autoResolveEmergency: () => void;
  /** Finish the paused resolveTile flow after the human settles an emergency. */
  resumeAfterEmergency: () => void;
  checkBankruptcies: () => void;
  netWorthOf: (playerId: string) => number;
  portfolioValueOf: (playerId: string) => number;
  debtCeilingOf: (playerId: string) => number;

  // ---- Player Financial Dossier ----
  /** Player whose dossier modal is open (null = closed). */
  inspectedPlayerId: string | null;
  setInspectedPlayer: (playerId: string | null) => void;
  /** Append a colour-coded line to a player's lifetime cash-flow telemetry + audit log. */
  recordFinancial: (
    playerId: string,
    kind: 'INCOME' | 'EXPENSE',
    category: keyof PlayerFinancialStats,
    label: string,
    amount: number,
  ) => void;

  // Corporate finance — Financial Desk (structured loans + treasury investments)
  loans: Loan[];
  investments: Investment[];
  /** UI gate for the interactive Financial Desk modal (never persisted). */
  financialDeskOpen: boolean;
  financialDeskTab: FinancialDeskTab;
  openFinancialDesk: (tab?: FinancialDeskTab) => void;
  closeFinancialDesk: () => void;
  /** Draw a structured loan: annuity, interest-only or balloon / zero-coupon. */
  takeStructuredLoan: (playerId: string, principal: number, termRounds: number, structure: LoanStructure, rateOverride?: number) => void;
  /** Quote for clearing a loan today (outstanding + accrued interest + 2.5 % fee). */
  loanPayoffOf: (loanId: string) => number;
  repayLoanEarly: (loanId: string) => void;
  /** Round-end servicing of every active loan (payments + maturity settlement). */
  processLoans: () => void;
  /**
   * Strict maturity settlement — a matured loan NEVER silently disappears:
   * 1) emergency refinancing (restructured loan + credit-rating penalty),
   * 2) bank foreclosure / asset seizure (+20 % liquidation penalty),
   * 3) insolvency → bankruptcy.
   * Returns 'SETTLED' | 'REFINANCED' | 'FORECLOSED' | 'INSOLVENT'.
   */
  resolveLoanMaturity: (loanId: string) => 'SETTLED' | 'REFINANCED' | 'FORECLOSED' | 'INSOLVENT' | 'PENDING';
  /**
   * Insolvency Resolution Desk (human debtors only): the matured loan stays parked
   * while the player covers the shortfall through the modal. 'PENDING' means the
   * desk owns the settlement now.
   */
  raiseInsolvency: (loanId: string) => void;
  /** Option A: refinance the shortfall as a fresh distress annuity (credit penalty). */
  insolvencyRefinance: () => void;
  /** Option B: withdraw treasury funds — penalty-aware, one click covers the shortfall. */
  insolvencyWithdraw: (tier: InvestmentTier) => void;
  /** Option C: controlled sale of chosen shares back to the bank (5 % exit fee). */
  insolvencySellShares: (tileId: string, shares: number) => void;
  /** CTA: pay the matured amount in full from cash and resume the game. */
  settleInsolvency: () => void;
  /** Last resort: net worth cannot cover the payment — declare bankruptcy. */
  declareInsolvencyBankruptcy: () => void;
  /** Outstanding principal across a player's structured loans. */
  loanExposureOf: (playerId: string) => number;
  investCash: (playerId: string, tier: InvestmentTier, amount: number) => void;
  withdrawInvestment: (playerId: string, tier: InvestmentTier, amount: number) => void;
  /** Round-end mark-to-market of every treasury position. */
  processInvestments: () => void;
  investmentValueOf: (playerId: string) => number;
  investmentOf: (playerId: string, tier: InvestmentTier) => Investment | undefined;

  // Special tile guide (interactive explainer popover)
  /** Tile whose "how this works" guide is open. */
  guideTileId: string | null;
  openTileGuide: (tileId: string) => void;
  closeTileGuide: () => void;

  // Bot tactical execution (localized ticker output)
  /** Apply a bot purchase and emit the exact numbers to the ticker. */
  botExecutePurchase: (playerId: string, tileId: string, shares: number, structure?: BotPurchaseStructure, note?: string) => number;

  // Cloud saves (Firebase)
  /** Serialize the complete match for `users/{uid}/games/activeGame`. */
  snapshotForCloud: () => CloudGameSnapshot;
  /** Replace the live match with a cloud snapshot (epoch bump remounts the board). */
  hydrateFromCloud: (snapshot: CloudGameSnapshot) => void;
  /** Archive-only helper: current snapshot is pushed before starting a new match. */
  hasActiveMatch: () => boolean;

  // Transit Metro fast travel
  rideMetro: (destinationIndex: number) => void;
  skipMetro: () => void;
  // Rent insurance
  buyInsurance: (playerId: string, policy: InsurancePolicy) => void;
  // Hostile takeover
  startTakeover: (tileId: string) => void;
  executeTakeover: (buyerId: string, tileId: string) => void;
  // City Hall lobbying
  enactZoningFreeze: (district: District) => void;
  triggerAuditOnLeader: () => void;
  skipLobby: () => void;
  dismissBlackSwan: () => void;
  // Bilateral P2P trading
  proposeTrade: (offer: TradeOffer) => void;
  respondToTrade: (accept: boolean) => void;
  respondToTradeInternal: (offer: TradeOffer, note: string) => void;
  /** Drop an already-settled bot verdict (accepted/rejected) so it never gates the game loop. */
  dismissTradeVerdict: () => void;
  botProposeTradeToHuman: () => void;
  /** Set-collection deal: bot offers a tile the human needs to complete a district,
   *  for a sensible price (tile value + premium). One clear Accept/Decline choice. */
  botProposeDistrictTrade: () => void;
  /** FSM → TRADE_NEGOTIATION while the human is in the deal builder. */
  beginTradeNegotiation: () => void;
  /** Restore the suspended phase after the deal builder closes. */
  endTradeNegotiation: () => void;
  clearTradeToast: () => void;
  clearBotPurchaseToast: () => void;
  /** Queue a freshly zoned property for the New Property Spotlight alert. */
  setNewPropertyAlert: (tile: PropertyTile) => void;
  /** Acknowledge the current spotlight card → the tile starts glowing on the board. */
  dismissNewPropertyAlert: () => void;
  /** Stop the board glow (auto-fired 5 s after dismissal). */
  clearSpotlight: () => void;
  // Attention Intercept
  raiseAttention: (a: AttentionState) => void;
  clearAttention: () => void;
  /** Clears the gate, restores the suspended phase and resumes deferred bot votes. */
  acknowledgeAttention: () => void;
  /** Deferred shareholder votes for a proposal tabled behind the attention gate. */
  botVoteOnProposal: (tileId: string, explicitCost?: number) => void;
  /** Stage an auction behind the attention gate; timer starts only on entry. */
  stageAuction: (tileId: string, tranche: number, actorName?: string) => void;
  enterStagedAuction: () => void;
  setBotStatus: (broadcast: BotBroadcast | null) => void;
  /** Fire a money-transfer event for the flying-cash overlay (payer → recipient / BANK). */
  triggerMoneyTransfer: (transfer: { fromId: string; toId: string; amount: number; reason: string }) => void;
  clearMoneyTransfers: () => void;

  // AI Bot heuristics (advanced strategic mechanics)
  /** Bots buy their archetype's rent policy when an upcoming rival rent exceeds 35% of their cash. */
  aiPurchaseInsurance: (playerId: string) => void;
  /** Bots autonomously park / drain treasury funds per ARCHETYPE_PROFILES.treasury. */
  aiManageTreasury: (playerId: string) => void;
  /** Aggressive/Opportunistic bots draw a structured loan for a live purchase / upgrade gap. */
  aiConsiderGrowthLoan: (playerId: string) => void;
  /** Full Financial Desk routine: treasury parking + proactive leverage + rent insurance. */
  botFinancePhase: (playerId: string) => void;
  /** Cartel City Hall tactic: freeze the market leader's prime district or audit the hegemon. */
  aiCityHallTactic: (playerId: string) => void;
  /**
   * Autonomous B2B negotiation: scans complementary districts for a win-win swap, lets the
   * partner counter with a cash sweetener and settles the accepted deal in the store.
   * `options.force` skips the 35 %/75 % dice roll (verification), `options.deal` seeds a deal.
   */
  executeBotTradingPhase: (playerId: string, options?: { force?: boolean; deal?: BotDeal | null }) => BotTradeOutcome | null;
  /** Proactive bot-to-bot diversification swap (back-compat wrapper around executeBotTradingPhase). */
  aiBotToBotTrade: (playerId: string) => void;
  /** Aggressive bots execute hostile takeovers whenever excess cash is above $5,000. */
  aiInitiateHostileTakeover: (playerId: string) => void;

  // Lifecycle
  logEvent: (message: string, kind: GameEvent['kind']) => void;
  /** Lobby: build the roster from the config and start a fresh match. */
  startGame: (config: LobbyConfig) => void;
  /** Return to the pre-game setup lobby (New Game button). */
  openLobby: () => void;
  /** Handover CTA: the waiting human acknowledged the controls. */
  beginHandoverTurn: () => void;
  /** Hotseat turn-based auction: fold/pass for the player whose bid slot it is. */
  foldAuction: (playerId: string) => void;
  /** Advance to the next eligible bidder; ends the auction when nobody can challenge. */
  advanceAuctionTurn: () => void;
  /** Hotseat review CTA: the receiving human accepted/declined at the same PC. */
  respondHotseatTrade: (accept: boolean) => void;
  resetGame: () => void;
}

const emptyAuction: AuctionState = {
  propertyId: '',
  shareTranche: 25,
  currentBid: 0,
  highestBidderId: null,
  timeRemaining: 10,
  activePlayerIds: [],
  isOpen: false,
  bidHistory: [],
  thinkingBidderId: null,
  humanFolded: false,
  turnOrder: [],
  turnIndex: 0,
  foldedPlayerIds: [],
};

/** Default lobby: classic 1 human + 3 bots configuration. */
const DEFAULT_LOBBY: LobbyConfig = {
  startingCash: INITIAL_CASH,
  victory: 'NET_WORTH_100K',
  players: [
    { id: 'slot-1', name: 'Player 1', isAI: false, archetype: 'RANDOM', color: '#3b82f6' },
    { id: 'slot-2', name: 'Venture Bot', isAI: true, archetype: 'AGGRESSIVE', color: '#10b981' },
    { id: 'slot-3', name: 'Prudence Bot', isAI: true, archetype: 'CONSERVATIVE', color: '#f59e0b' },
    { id: 'slot-4', name: 'Vulture Bot', isAI: true, archetype: 'OPPORTUNISTIC', color: '#f43f5e' },
  ],
};

/** Resolve the lobby's RANDOM archetype to a concrete bot persona. */
const archetypeOf = (choice: AIArchetypeChoice): AIArchetype => {
  if (choice !== 'RANDOM') return choice;
  const pool: AIArchetype[] = ['AGGRESSIVE', 'CONSERVATIVE', 'OPPORTUNISTIC'];
  return pool[Math.floor(Math.random() * pool.length)];
};

/** Build the live player roster from the lobby configuration. */
const buildPlayersFromConfig = (config: LobbyConfig): Player[] =>
  config.players.map((slot, index) => ({
    id: `player-${index + 1}`,
    name: slot.name.trim() || (slot.isAI ? `Bot ${index + 1}` : `Player ${index + 1}`),
    isAI: slot.isAI,
    aiArchetype: slot.isAI ? archetypeOf(slot.archetype) : undefined,
    cash: config.startingCash,
    debt: 0,
    currentTileIndex: 0,
    color: slot.color,
    isBankrupt: false,
    ...freshPlayerFields(),
  }));

const emptyProposal: ProposalState = {
  propertyId: '',
  proposerId: '',
  targetUpgradeLevel: 0,
  totalCost: 0,
  newSharesIssued: 0,
  votesFor: 0,
  votesAgainst: 0,
  votedPlayerIds: [],
  status: 'PENDING',
};

const withCash = (players: Player[], id: string, delta: number): Player[] =>
  players.map((p) => (p.id === id ? { ...p, cash: p.cash + delta } : p));

/**
 * Suspends the FSM behind a gate (attention / trade negotiation). The very first
 * suspended phase is kept, so chained gates all resume to the same origin.
 */
const suspensionOf = (
  state: Pick<GameStore, 'turnPhase' | 'resumePhase'>,
  gate: 'ATTENTION_INTERCEPT' | 'TRADE_NEGOTIATION',
): Pick<GameStore, 'turnPhase' | 'resumePhase'> => {
  const gated = state.turnPhase === 'ATTENTION_INTERCEPT' || state.turnPhase === 'TRADE_NEGOTIATION';
  return {
    turnPhase: gate,
    resumePhase: gated ? state.resumePhase ?? 'TURN_END' : state.turnPhase,
  };
};

/** Releases a gate and restores the suspended FSM phase. */
const releaseOf = (state: Pick<GameStore, 'resumePhase'>): Pick<GameStore, 'turnPhase' | 'resumePhase'> => ({
  turnPhase: state.resumePhase ?? 'TURN_END',
  resumePhase: null,
});

/** "What's at stake" breakdown rendered inside the amber Attention splash. */
const stakeBreakdownOf = (
  players: Player[],
  tile: PropertyTile,
  phase: MarketPhase,
  tranche?: number,
): { label: string; value: string }[] => {
  const rent = rentFor(tile, phase);
  const shareholders = Object.entries(tile.shares)
    .filter(([, s]) => s > 0)
    .map(([id, s]) => `${players.find((p) => p.id === id)?.name ?? id} · ${s} sh`);
  return [
    { label: 'Current tile value', value: money(valuationLiveOf(tile)) },
    { label: 'Total rent / visit', value: `${money(rent)} (tier ${tile.upgradeLevel})` },
    { label: 'Shares at stake', value: tranche === undefined ? '—' : `${tranche} shares` },
    { label: 'Float remaining', value: `${floatSharesOf(tile)} shares` },
    { label: 'Current shareholders', value: shareholders.length > 0 ? shareholders.join(' · ') : 'none (fresh float)' },
  ];
};

// ---- Bilateral trade helpers (pure; state passed in) ----
export const unitValueOf = (tile: PropertyTile): number => valuationLiveOf(tile) / Math.max(1, tile.totalShares);

export const lotValue = (board: PropertyTile[], lots: { tileId: string; shares: number }[]): number =>
  lots.reduce((sum, lot) => {
    const tile = board.find((t) => t.id === lot.tileId);
    return tile ? sum + lot.shares * unitValueOf(tile) : sum;
  }, 0);

export const describeTrade = (board: PropertyTile[], players: Player[], offer: TradeOffer): string => {
  const sender = players.find((p) => p.id === offer.senderId);
  const fmt = (lots: { tileId: string; shares: number }[]) =>
    lots.map((l) => `${l.shares} sh ${board.find((t) => t.id === l.tileId)?.name ?? l.tileId}`).join(' + ') || '—';
  return `${sender?.name ?? 'Rival'} offers: ${money(offer.offeredCash)} + ${fmt(offer.offeredShares)} — and requests: ${money(offer.requestedCash)} + ${fmt(offer.requestedShares)}.`;
};

/** Threshold (incoming/outgoing) each archetype needs before saying yes. */
export const acceptThresholdOf = (a?: AIArchetype) => profileOf(a).trade.acceptRatio;

export interface TradeCore {
  /** What the receiver effectively gains, in strategist-adjusted dollars. */
  botReceives: number;
  /** What the receiver effectively gives up (context penalties included). */
  botGives: number;
  /** Cash-only exchange rate applied to offeredCash (liquidity premium / equity preference). */
  cashFactorIn: number;
  completesBotSet: boolean;
  senderGetsMajority: boolean;
  senderGetsMonopoly: boolean;
  breaksOwnStake: boolean;
  cashAfter: number;
  /** Market (unweighted) figures, for barometers and shortfall maths. */
  rawReceived: number;
  rawGiven: number;
}

/** Shared strategic valuation: Monopoly synergy, threat prevention, situational cash needs. */
export const evaluateTradeCore = (board: PropertyTile[], receiver: Player, offer: TradeOffer): TradeCore => {
  const profile = profileOf(receiver.aiArchetype);
  const senderId = offer.senderId;
  const sitsIn = (tile: PropertyTile, id: string) => (tile.shares[id] ?? 0) / Math.max(1, tile.totalShares);
  const controls = (tile: PropertyTile, id: string) => (tile.shares[id] ?? 0) > tile.totalShares / 2;
  const districtTilesOf = (district: District) => getDistrictTiles(board, district);

  // ---- A) Threat: what the RECEIVER (bot) hands over to the SENDER ----
  let givenValue = offer.requestedCash;
  let senderGetsMajority = false;
  let senderGetsMonopoly = false;
  let breaksOwnStake = false;
  const rawGiven = offer.requestedCash + lotValue(board, offer.requestedShares);
  for (const lot of offer.requestedShares) {
    const tile = board.find((t) => t.id === lot.tileId);
    if (!tile) continue;
    const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
    let mult = 1;
    const afterSender = sitsIn(tile, senderId) + lot.shares / Math.max(1, tile.totalShares);
    if (afterSender > 0.5) {
      // Handing the sender a majority here: archetype-priced strategic surcharge
      // (AGGRESSIVE charges the steepest premium for majority equity).
      mult += profile.trade.majoritySurcharge;
      senderGetsMajority = true;
    }
    const district = districtTilesOf(tile.district);
    const senderWouldControlAll = district.length > 0 && district.every((t) => (t.id === tile.id ? afterSender : sitsIn(t, senderId)) > 0.5);
    if (senderWouldControlAll) {
      // Full colour monopoly for the sender: archetype denial premium
      // (OPPORTUNISTIC charges exactly 2.2×, CONSERVATIVE the harshest 2.5×).
      mult = profile.trade.monopolyDenial;
      senderGetsMonopoly = true;
    }
    // Breaking the bot's own majority / blocking stake (≥25 %) costs 50 % more.
    const botBefore = sitsIn(tile, receiver.id);
    const botAfter = botBefore - lot.shares / Math.max(1, tile.totalShares);
    if ((botBefore > 0.5 && botAfter <= 0.5) || (botBefore >= 0.25 && botAfter < 0.25)) {
      mult *= 1.5;
      breaksOwnStake = true;
    }
    givenValue += lot.shares * unit * mult;
  }

  // ---- B) The bot's own strategic value: what the RECEIVER gains ----
  let receivedValue = 0;
  let completesBotSet = false;
  for (const lot of offer.offeredShares) {
    const tile = board.find((t) => t.id === lot.tileId);
    if (!tile) continue;
    const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
    let mult = 1;
    const afterBot = sitsIn(tile, receiver.id) + lot.shares / Math.max(1, tile.totalShares);
    if (afterBot > 0.5) mult = 1.5; // incoming shares secure a majority here
    const district = districtTilesOf(tile.district);
    const botWouldControlAll = district.length > 0 && district.every((t) => (t.id === tile.id ? afterBot : sitsIn(t, receiver.id)) > 0.5);
    if (botWouldControlAll) {
      mult = 1.8; // completes the bot's own colour set
      completesBotSet = true;
    }
    receivedValue += lot.shares * unit * mult;
  }

  // ---- C) Liquidity & situational need ----
  // Cash-starved bots pay a premium for liquidity; cash-rich bots prefer property equity.
  const cashFactorIn = (() => {
  const cashBuffer = Math.max(300, profileOf(receiver.aiArchetype).trade.cashBuffer);
  if (receiver.cash < cashBuffer / 2) return 1.5;
  if (receiver.cash < cashBuffer) return 1.3;
  if (receiver.cash < 300) return 1.3;
  if (receiver.cash > 2500) return 0.7;
  return 1;
})();
  const rawReceived = offer.offeredCash + lotValue(board, offer.offeredShares);
  return {
    botReceives: offer.offeredCash * cashFactorIn + receivedValue,
    botGives: givenValue,
    cashFactorIn,
    completesBotSet,
    senderGetsMajority,
    senderGetsMonopoly,
    breaksOwnStake,
    cashAfter: receiver.cash + offer.offeredCash - offer.requestedCash,
    rawReceived,
    rawGiven,
  };
};

export const evaluateBotTrade = (
  board: PropertyTile[],
  players: Player[],
  offer: TradeOffer,
  leaderId?: string | null,
): { accept: boolean; note: string; reason: TradeVerdictReason; ratio: number } => {
  const receiver = players.find((p) => p.id === offer.receiverId);
  if (!receiver) return { accept: false, note: 'invalid counterpart', reason: 'BALANCE', ratio: 0 };
  const profile = profileOf(receiver.aiArchetype);
  const core = evaluateTradeCore(board, receiver, offer);
  const ratio = core.botGives > 0 ? core.botReceives / core.botGives : 99;
  const threshold = acceptThresholdOf(receiver.aiArchetype);
  // Nobody hands over a completed colour set unless the cash itself is a massive
  // compensation (≥1.5× the market value of the stakes surrendered).
  const monopolyPayoff = offer.offeredCash >= lotValue(board, offer.requestedShares) * 1.5;

  // ---- ANTI-LEADER CARTEL EMBARGO ------------------------------------------
  // A trailing bot never sells the market hegemon anything that completes a district
  // or increases its majority, and charges an extortionate 3.5× markup on neutral trades.
  if (leaderId && offer.senderId === leaderId && offer.receiverId !== leaderId) {
    if (core.senderGetsMonopoly || core.senderGetsMajority) {
      return {
        accept: false,
        reason: 'CARTEL_EMBARGO',
        ratio,
        note: '🕵️ Kartel: lídrovi neprodáme akcie, které mu dokončí distrikt nebo zpečetí většinu.',
      };
    }
    if (ratio < LEADER_EMBARGO_MULTIPLIER) {
      return {
        accept: false,
        reason: 'CARTEL_EMBARGO',
        ratio,
        note: `🕵️ Kartelní embargo: lídrovi obchodujeme jen za ${LEADER_EMBARGO_MULTIPLIER}× tržní ceny.`,
      };
    }
  }

  if (core.senderGetsMonopoly && !core.completesBotSet && !monopolyPayoff) {
    return {
      accept: false,
      reason: 'BLOCK_MONOPOLY',
      ratio,
      note: 'Odmítnuto: Tímhle bys získal celý barevný distrikt, to ti zadarmo nedám!',
    };
  }
  if (receiver.aiArchetype === 'CONSERVATIVE' && core.cashAfter < profile.trade.cashBuffer && offer.offeredCash <= offer.requestedCash) {
    return {
      accept: false,
      reason: 'BUFFER',
      ratio,
      note: `Odmítnuto: Po obchodě bych měl jen ${money(core.cashAfter)} hotovosti — rezervu si držím.`,
    };
  }
  if (core.breaksOwnStake && ratio < threshold + 0.35) {
    return {
      accept: false,
      reason: 'KEEP_MAJORITY',
      ratio,
      note: 'Odmítnuto: Tyto akcie mi drží většinu, potřebuji mnohem víc peněz.',
    };
  }
  if (ratio >= threshold) {
    if (core.completesBotSet) {
      return { accept: true, reason: 'ACCEPT_SET', ratio, note: 'Přijato: Tyto akcie mi kompletují distrikt!' };
    }
    if (receiver.cash < 300 && offer.offeredCash > 0) {
      return { accept: true, reason: 'ACCEPT_CASH', ratio, note: 'Přijato: Nutně potřebuji hotovost na nájmy, beru to.' };
    }
    return { accept: true, reason: 'ACCEPT_VALUE', ratio, note: `Přijato: poměr ${ratio.toFixed(2)}× je pro mě výhodný.` };
  }
  return {
    accept: false,
    reason: 'BALANCE',
    ratio,
    note: core.senderGetsMajority
      ? 'Odmítnuto: Dal bys tím soupeři většinu na tomto pozemku — přidej víc.'
      : `Odmítnuto: Poměr ${ratio.toFixed(2)}× je slabý, chci víc.`,
  };
};

/** Extra cash the sender must add for the receiver to reach its accept threshold. */
export const tradeShortfall = (board: PropertyTile[], players: Player[], offer: TradeOffer): number => {
  const receiver = players.find((p) => p.id === offer.receiverId);
  if (!receiver || !receiver.isAI) return 0;
  const core = evaluateTradeCore(board, receiver, offer);
  const threshold = acceptThresholdOf(receiver.aiArchetype);
  const gap = threshold * core.botGives - core.botReceives;
  if (gap <= 0) return 0;
  // Monopoly sales are never "just a bit more cash" territory.
  const penalty = core.senderGetsMonopoly ? 1.5 : 1;
  return Math.ceil((gap / core.cashFactorIn) * penalty);
};

export const describeDealMeter = (board: PropertyTile[], offer: TradeOffer): { offered: number; requested: number; ratio: number } => {
  // From the RECEIVER's (bot's) perspective: `offered*` is what it receives,
  // `requested*` is what it gives up. ratio = offered / requested, so ratio ≥ 1
  // means the deal is profitable/favorable for the receiver.
  const offered = offer.offeredCash + lotValue(board, offer.offeredShares);
  const requested = offer.requestedCash + lotValue(board, offer.requestedShares);
  return { offered, requested, ratio: requested > 0 ? offered / requested : 99 };
};

// ---- Bot dynamic share-sizing (archetype-driven tranches, never a fixed default) ----
const HIGH_TIER_DISTRICTS = ['DISTRICT_C', 'DISTRICT_D'];
const LOW_VOL_DISTRICTS = ['DISTRICT_A', 'TRANSIT'];

/**
 * Tactical sizing intents. Every branch of `botEvaluatePurchase` declares one, so the UI
 * can explain WHY a bot bought 65 shares instead of falling back to a fixed 25-share cap.
 */
export type BotPurchaseStructure =
  | 'BUYOUT_100'
  | 'MAJORITY_51'
  | 'BLOCKING_STAKE'
  | 'CONSOLIDATE_MAJORITY'
  | 'DISTRICT_TRANCHE'
  | 'MAX_AFFORDABLE'
  | 'PREMIUM_BITE'
  | 'SAFE_TRANCHE'
  | 'DISTRESSED'
  | 'NONE';

/** i18n keys explaining each sizing intent (localized ticker line + bot speech bubble). */
export const BOT_STRUCTURE_REASON_KEY: Record<BotPurchaseStructure, string> = {
  BUYOUT_100: 'reason.buyout100',
  MAJORITY_51: 'reason.majority51',
  BLOCKING_STAKE: 'reason.blockingStake',
  CONSOLIDATE_MAJORITY: 'reason.consolidate',
  DISTRICT_TRANCHE: 'reason.districtSet',
  MAX_AFFORDABLE: 'reason.maxAffordable',
  PREMIUM_BITE: 'reason.premium',
  SAFE_TRANCHE: 'reason.tranche',
  DISTRESSED: 'reason.distressed',
  NONE: 'reason.auction',
};

export interface BotPurchaseDecision {
  /** Shares to buy straight from the float (0 = do not buy). */
  shares: number;
  cost: number;
  /** True → the bot gambles on the public auction instead of buying. */
  wantAuction: boolean;
  /** English note for logs / legacy tests. */
  note: string;
  /** Machine-readable sizing intent (drives the localized ticker + verification). */
  structure: BotPurchaseStructure;
  /** Resulting stake after the purchase, in % of the issued shares. */
  targetPct: number;
}


// ---- Tycoon Advisor: one plain-language tactical hint per situation ----
export const advisorFor = (board: PropertyTile[], player: Player, tile: PropertyTile | null): string | null => {
  if (!tile || tile.tileType !== 'COMMERCIAL') return null;
  const districtLabel = districtMetaOf(tile.district);
  const owned = tile.shares[player.id] ?? 0;
  const districtTiles = getDistrictTiles(board, tile.district);
  const total = getDistrictTotal(board, tile.district);

  // 1) Completing the player's own colour set (100 % of the ACTUAL tiles in the group).
  if (owned > 0 || districtTiles.some((t) => (t.shares[player.id] ?? 0) > 0)) {
    const progress = districtSetOf(board, player.id, tile.district);
    if (progress.owned === total - 1) {
      return `💡 Tento pozemek ti zkompletuje ${districtLabel} distrikt (${total}/${total}) — nájmy v něm výrazně porostou.`;
    }
  }
  // 2) Blocking a rival who is one tile from a monopoly in this colour.
  const rivalNearMonopoly = districtTiles.some((t) => {
    if (t.id === tile.id) return false;
    return Object.entries(t.shares).some(([id, shares]) => id !== player.id && shares > t.totalShares / 2);
  });
  if (rivalNearMonopoly) {
    return `💡 Pozor: soupeř má v tomto distriktu skoro vše — koupíš-li toto, zablokuješ mu monopol.`;
  }
  // 3) Danger: a rival is building big on this tile.
  const topHolder = Object.entries(tile.shares).filter(([id]) => id !== player.id).sort((a, b) => b[1] - a[1])[0];
  if (topHolder && topHolder[1] > tile.totalShares / 2 && tile.upgradeLevel >= 3) {
    const building = tile.upgradeLevel >= 4 ? 'mrakodrap' : 'výškovou budovu';
    return `💡 Na tomto pozemku roste ${building} (tier ${tile.upgradeLevel}) — nájem je tu vysoký, zvaž obezličku metrem.`;
  }
  return null;
};
const districtMetaOf = (district: string): string =>
  ({ DISTRICT_A: 'hnědý', DISTRICT_B: 'modrý', DISTRICT_C: 'oranžový', DISTRICT_D: 'zlatý', TRANSIT: 'tranzitní', FINANCIAL: 'finanční', SPECIAL: 'speciální' } as Record<string, string>)[district] ?? district;

export const botEvaluatePurchase = (board: PropertyTile[], player: Player, tile: PropertyTile): BotPurchaseDecision => {
  // Archetype compliance table: every number below comes from the persona profile.
  const profile = profileOf(player.aiArchetype);
  const float = floatSharesOf(tile);
  const unit = Math.max(1, pricePerShareOf(tile));
  const owned = tile.shares[player.id] ?? 0;
  const archetype = player.aiArchetype ?? 'OPPORTUNISTIC';
  const districtOwned = board.some((t) => t.district === tile.district && (t.shares[player.id] ?? 0) > 0);
  const isHighTier = HIGH_TIER_DISTRICTS.includes(tile.district);
  const isLowVol = LOW_VOL_DISTRICTS.includes(tile.district);
  // Persona budget: AGGRESSIVE spends down to $0, CONSERVATIVE keeps a hard $800 floor.
  const budgetShares = purchaseBudgetOf(player, unit);
  const maxAffordable = Math.min(float, budgetShares);
  const buy = (shares: number, structure: BotPurchaseStructure, note: string): BotPurchaseDecision => ({
    shares,
    cost: shares * unit,
    wantAuction: false,
    note,
    structure,
    targetPct: ((owned + shares) / Math.max(1, tile.totalShares)) * 100,
  });
  const abstain = (wantAuction: boolean, note: string, structure: BotPurchaseStructure = 'NONE'): BotPurchaseDecision => ({
    shares: 0,
    cost: 0,
    wantAuction,
    note,
    structure,
    targetPct: (owned / Math.max(1, tile.totalShares)) * 100,
  });

  // ---- Monopoly-style set collection: the ONE clear goal is owning the whole colour ----
  // Every count below is derived from the live board, so a 4/5/6-tile colour always
  // requires exactly its own number of properties.
  const districtTiles = getDistrictTiles(board, tile.district);
  const controls = (t: PropertyTile, id: string) => (t.shares[id] ?? 0) > t.totalShares / 2;
  const othersInDistrict = (id: string) => districtTiles.filter((t) => t.id !== tile.id && controls(t, id)).length;
  const botSetTiles = districtTiles.filter((t) => controls(t, player.id)).length;
  const dominantNeeded = Math.ceil(districtTiles.length / 2); // majority of the colour
  const oneAwayNeeded = districtTiles.length - 1; // every colour tile but this one
  const rivalIds = [...new Set(board.flatMap((t) => Object.keys(t.shares)).filter((id) => id !== player.id))];
  const bestRivalTiles = rivalIds.reduce((max, id) => Math.max(max, othersInDistrict(id)), 0);
  // Blocking: a rival is one tile from the full colour, OR already dominates the colour
  // while we hold nothing there (scales the classic "2 of 3" rule to 4–6 tile districts).
  const threatRival = rivalIds.find(
    (id) => othersInDistrict(id) >= oneAwayNeeded || (othersInDistrict(id) >= dominantNeeded && !districtOwned),
  );
  const completingOwnSet = botSetTiles >= oneAwayNeeded; // one tile from the full colour
  const collectingHere = districtOwned; // already in this colour → keep collecting
  const blocking = Boolean(threatRival);
  // Strictly ABOVE half — exactly 50 % is not control.
  const needControl = Math.max(1, Math.floor(tile.totalShares / 2) + 1 - owned);
  const majorityShares = Math.min(float, needControl);
  const majorityAffordable = maxAffordable >= majorityShares;

  // ============================================================================
  // AGGRESSIVE — Venture Tycoon: 100 % buyout → 51 % majority → biggest affordable
  // bite (cash floor $0). NEVER declines while it can afford `minBite` shares.
  // ============================================================================
  if (archetype === 'AGGRESSIVE') {
    if (float <= 0) return abstain(false, 'float exhausted', 'NONE');
    if (profile.purchase.targetBuyout && float === tile.totalShares && player.cash >= float * unit) {
      return buy(float, 'BUYOUT_100', 'buying out the entire float');
    }
    if (completingOwnSet || blocking) {
      if (majorityAffordable) {
        return buy(
          majorityShares,
          blocking ? 'BLOCKING_STAKE' : owned + majorityShares >= tile.totalShares ? 'BUYOUT_100' : 'MAJORITY_51',
          blocking ? 'blocking a rival monopoly' : 'completing the district set',
        );
      }
      if (maxAffordable >= profile.purchase.minBite) {
        return buy(maxAffordable, 'MAX_AFFORDABLE', blocking ? 'blocking at all costs' : 'chasing the district set');
      }
      return abstain(true, 'cannot fund control — gambling at auction');
    }
    if (majorityAffordable) return buy(majorityShares, 'MAJORITY_51', 'securing majority control');
    if (maxAffordable >= profile.purchase.minBite) {
      return buy(maxAffordable, isHighTier ? 'PREMIUM_BITE' : 'MAX_AFFORDABLE', 'buying the maximum affordable tranche');
    }
    // Under the min bite the tycoon rolls the dice instead of taking pocket change.
    return abstain(profile.purchase.gambleAtAuction, 'too thin to buy — gambling at auction');
  }

  // ============================================================================
  // CONSERVATIVE — Prudence Banker: HARD $800 floor, 25–40 share tranches, favours
  // Transit hubs / cheap utilities, never gambles at auction.
  // ============================================================================
  if (archetype === 'CONSERVATIVE') {
    if (player.cash - profile.purchase.cashFloor < profile.purchase.minBite * unit) {
      return abstain(false, 'reserve would break the $800 floor — decline', 'SAFE_TRANCHE');
    }
    // A finishing piece is worth taking, but the hard floor still rules.
    if (completingOwnSet && majorityAffordable) {
      return buy(majorityShares, 'MAJORITY_51', 'completing the district set within reserve rules');
    }
    if (blocking && maxAffordable >= profile.purchase.blockingMin) {
      return buy(
        Math.min(maxAffordable, profile.purchase.blockingMax),
        'BLOCKING_STAKE',
        'blocking a rival monopoly within reserve rules',
      );
    }
    const bandTop = isLowVol ? profile.purchase.lowVolTrancheMax : profile.purchase.trancheMax;
    // Low appetite for expensive Luxury/Tech tiles: the band is trimmed on premium districts.
    const premiumTrim = profile.purchase.dislikesPremium && isHighTier ? Math.floor(bandTop / 2) : bandTop;
    const tranche = Math.min(maxAffordable, Math.max(profile.purchase.minBite, premiumTrim));
    if (tranche >= profile.purchase.minBite && player.cash - tranche * unit >= profile.purchase.cashFloor) {
      return buy(
        tranche,
        'SAFE_TRANCHE',
        isLowVol ? 'low-volatility utility position with reserve kept' : 'steady accumulation with $800 reserve',
      );
    }
    return abstain(false, 'prudently passes to protect the reserve', 'SAFE_TRANCHE');
  }

  // ============================================================================
  // OPPORTUNISTIC — Vulture Raider: owns the colour → 51 %; a rival holding ≥ 2 tiles
  // of the colour → blocking stake 26–35; otherwise an opportunistic 26–35 tranche.
  // ============================================================================
  const ownsDistrictStake = collectingHere || owned > 0;
  if (ownsDistrictStake && profile.purchase.targetMajority) {
    const majorityGap = Math.max(0, Math.ceil(tile.totalShares * 0.51) - owned);
    if (majorityGap > 0 && float > 0) {
      const target = Math.min(majorityGap, float);
      if (player.cash - target * unit >= profile.purchase.cashFloor) {
        return buy(target, 'CONSOLIDATE_MAJORITY', 'consolidating majority in a known district');
      }
      if (maxAffordable >= profile.purchase.minBite) return buy(maxAffordable, 'MAX_AFFORDABLE', 'partial step toward majority');
      return abstain(true, 'majority out of reach — auction gamble');
    }
  }
  // Blocker: an opponent already holds ≥ `rivalTilesToBlock` tiles of this colour.
  if (bestRivalTiles >= profile.purchase.rivalTilesToBlock && float > 0) {
    const blockSpread = profile.purchase.blockingMax - profile.purchase.blockingMin + 1;
    const block = Math.min(float, maxAffordable, profile.purchase.blockingMin + Math.floor(Math.random() * blockSpread));
    if (block >= profile.purchase.blockingMin) return buy(block, 'BLOCKING_STAKE', 'blocking minority stake to deny the monopoly');
    if (maxAffordable >= profile.purchase.minBite) return buy(maxAffordable, 'MAX_AFFORDABLE', 'partial blocking stake');
    return abstain(true, 'blocking stake out of reach — auction gamble');
  }
  // Otherwise: opportunistic accumulation inside the persona's tranche band.
  const trancheSpread = profile.purchase.trancheMax - profile.purchase.trancheMin + 1;
  const opportunistic = Math.min(float, maxAffordable, profile.purchase.trancheMin + Math.floor(Math.random() * trancheSpread));
  if (opportunistic >= profile.purchase.minBite) return buy(opportunistic, 'DISTRICT_TRANCHE', 'opportunistic accumulation stake');
  if (maxAffordable >= profile.purchase.minBite) return buy(maxAffordable, 'MAX_AFFORDABLE', 'discounted entry stake');
  return abstain(profile.purchase.gambleAtAuction, 'hunting a bargain at auction');
};

// ============================================================================
// AUCTION POLICY — one pure function per archetype rule set (see ARCHETYPE_PROFILES):
//  • AGGRESSIVE   ceiling 1.35× fair value (1.5× when the win is decisive), borrows
//                 250/500 chunks from the credit line to stay in the bid war.
//  • CONSERVATIVE hard 0.85× ceiling, folds the moment bidding reaches fair value,
//                 never borrows.
//  • OPPORTUNISTIC 1.05× on neutral tiles, escalates to 1.4× only when the win blocks
//                 an opponent's complete colour monopoly.
// ============================================================================
export interface BotAuctionInput {
  board: PropertyTile[];
  bot: Player;
  tile: PropertyTile;
  tranche: number;
  currentBid: number;
  highestBidderId: string | null;
  /** Head-room left on the credit line (`debtCeilingOf - debt`). */
  creditAvailable: number;
}

export interface BotAuctionDecision {
  action: 'BID' | 'BORROW_AND_BID' | 'FOLD';
  /** Bid to place (0 when folding). */
  amount: number;
  /** Credit to draw before bidding (borrow-and-bid only). */
  borrow: number;
  ceiling: number;
  fairValue: number;
  /** Win secures the bot's own monopoly run or blocks a rival's complete colour. */
  decisive: boolean;
  reason: string;
}

export const botAuctionDecision = ({
  board,
  bot,
  tile,
  tranche,
  currentBid,
  highestBidderId,
  creditAvailable,
}: BotAuctionInput): BotAuctionDecision => {
  const profile = profileOf(bot.aiArchetype);
  const total = Math.max(1, tile.totalShares);
  const unitValue = valuationLiveOf(tile) / total;
  const fairValue = unitValue * Math.max(0, tranche);
  const owned = tile.shares[bot.id] ?? 0;
  const districtTiles = getDistrictTiles(board, tile.district);
  const controls = (t: PropertyTile, id: string) => (t.shares[id] ?? 0) > t.totalShares / 2;
  // Would winning this tranche hand the bot control of the tile?
  const majorityReach = owned + tranche > total / 2;
  // Decisive = the bot extends its own monopoly arc OR stops a rival's complete colour.
  const botsAfter = districtTiles.filter((t) => (t.id === tile.id ? majorityReach : controls(t, bot.id))).length;
  const ownArc = districtTiles.length > 0 && botsAfter >= Math.ceil(districtTiles.length / 2);
  const rivalIds = [...new Set(board.flatMap((t) => Object.keys(t.shares)).filter((id) => id !== bot.id))];
  const blocksRivalMonopoly = rivalIds.some((id) => districtTiles.every((t) => t.id === tile.id || controls(t, id)));
  // Decisive = the bot extends its own monopoly arc (needs the majority) OR the win
  // stops a rival's complete colour (blocking premium applies even without a majority).
  const decisive = (majorityReach && ownArc) || blocksRivalMonopoly;
  const ceiling = decisive ? profile.auction.monopolyCeiling : profile.auction.ceiling;
  const maxWtp = Math.round(fairValue * ceiling);
  const spendable = Math.max(0, bot.cash - profile.auction.bidReserve);
  const step = currentBid + profile.auction.bidStep;
  const outbid = Boolean(highestBidderId) && highestBidderId !== bot.id;
  const base = { amount: 0, borrow: 0, ceiling, fairValue: Math.round(fairValue), decisive };

  // CONSERVATIVE: folds the moment bidding reaches fair value — never overpays.
  if (profile.auction.foldAboveFairValue && currentBid >= fairValue) {
    return { ...base, action: 'FOLD', reason: 'bidding at/above fair value — Prudence Banker folds' };
  }
  if (step <= maxWtp && step <= spendable) {
    return { ...base, action: 'BID', amount: step, reason: decisive ? 'bidding to decide the district' : 'bidding inside the ceiling' };
  }
  // AGGRESSIVE emergency leverage: draw 250/500 credit to stay in the bid war when the
  // win would secure a majority (or decide the colour).
  if (profile.auction.borrowToBid && outbid && (majorityReach || decisive) && creditAvailable > 0) {
    const shortfall = step - spendable;
    const chunk = profile.auction.borrowChunks.find((value) => value >= shortfall && value <= creditAvailable);
    if (chunk && step <= maxWtp + chunk) {
      return {
        ...base,
        action: 'BORROW_AND_BID',
        amount: step,
        borrow: chunk,
        reason: `drawing $${chunk} credit to outbid for control`,
      };
    }
  }
  return {
    ...base,
    action: 'FOLD',
    reason: step > maxWtp ? 'bid would exceed the archetype ceiling' : 'insufficient cash inside the bid reserve',
  };
};

// ============================================================================
// UPGRADE / GOVERNANCE POLICY — archetype reserves (see ARCHETYPE_PROFILES.upgrade):
//  • AGGRESSIVE   upgrades any majority stake with only a $100 cash reserve.
//  • CONSERVATIVE needs 3× the build cost still in reserve (and votes the same way).
//  • OPPORTUNISTIC prioritises high-traffic board positions (next to Transit hubs /
//                 high-rent corridors) with a $1,000 cushion.
// ============================================================================
export interface BotUpgradeIntent {
  tileId: string;
  tileName: string;
  targetLevel: number;
  cost: number;
  reserveRequired: number;
  cashAfter: number;
  traffic: number;
  reason: string;
}

/** High-traffic score: Transit hops around the ring + the rent the tile already earns. */
export const tileTrafficScore = (board: PropertyTile[], tile: PropertyTile): number => {
  const index = board.findIndex((entry) => entry.id === tile.id);
  if (index < 0) return 0;
  const ring = (offset: number) => board[(index + offset + board.length) % board.length];
  let score = 0;
  for (const offset of [-1, 1]) {
    const neighbour = ring(offset);
    if (neighbour?.tileType === 'TRANSIT') score += 3; // right before/after a Metro stop
    else if (neighbour?.tileType === 'COMMERCIAL') score += 1;
  }
  for (const offset of [-2, 2]) {
    if (ring(offset)?.tileType === 'TRANSIT') score += 1;
  }
  score += Math.round(valuationLiveOf(tile) / 500); // high-rent corridor bonus
  return score;
};

/** Best upgrade move for this persona right now (null = nothing compliant to build). */
export const evaluateBotUpgrade = (board: PropertyTile[], player: Player): BotUpgradeIntent | null => {
  const profile = profileOf(player.aiArchetype);
  const candidates = board
    .filter((tile) => tile.tileType === 'COMMERCIAL' && !tile.isFrozen && tile.upgradeLevel < 5)
    .filter((tile) => ownershipPct(tile, player.id) > 50)
    .map((tile) => {
      const cost = tile.upgradeCost;
      const reserveRequired = profile.upgrade.fixedReserve + Math.round(cost * profile.upgrade.costMultiplier);
      const traffic = tileTrafficScore(board, tile);
      const rent = rentFor(tile, 'STAGNATION');
      return { tile, cost, reserveRequired, traffic, rent };
    })
    .filter((entry) => player.cash - entry.cost >= entry.reserveRequired);
  if (candidates.length === 0) return null;

  const ranked = [...candidates].sort((a, b) => {
    if (profile.upgrade.rankBy === 'COST') return a.cost - b.cost;
    if (profile.upgrade.rankBy === 'TRAFFIC') {
      const score = (entry: typeof a) => entry.traffic * profile.upgrade.trafficWeight + entry.tile.upgradeLevel * 2;
      return score(b) - score(a);
    }
    const score = (entry: typeof a) => entry.rent * (1 + entry.traffic * profile.upgrade.trafficWeight * 0.05);
    return score(b) - score(a);
  });
  const best = ranked[0];
  const reason =
    profile.upgrade.rankBy === 'COST'
      ? 'cheapest safe build with a 3× cost reserve'
      : profile.upgrade.rankBy === 'TRAFFIC'
        ? `high-traffic position (score ${best.traffic}) with a full cushion`
        : 'majority stake upgraded immediately ($100 reserve)';
  return {
    tileId: best.tile.id,
    tileName: best.tile.name,
    targetLevel: best.tile.upgradeLevel + 1,
    cost: best.cost,
    reserveRequired: best.reserveRequired,
    cashAfter: player.cash - best.cost,
    traffic: best.traffic,
    reason,
  };
};

/** Vote rule for a capital-call proposal: same reserve doctrine as building. */
export const botUpgradeVote = (player: Player, tile: PropertyTile, cost: number): 'FOR' | 'AGAINST' => {
  const profile = profileOf(player.aiArchetype);
  const bill = Math.max(1, Math.round(cost * ((tile.shares[player.id] ?? 0) / Math.max(1, tile.totalShares))));
  const reserve = profile.upgrade.voteReserve + Math.round(bill * profile.upgrade.voteCostMultiplier);
  return player.cash - bill >= reserve ? 'FOR' : 'AGAINST';
};

const sharesOf = (tile: PropertyTile, playerId: string): number => tile.shares[playerId] ?? 0;

export const ownershipPct = (tile: PropertyTile, playerId: string): number =>
  (sharesOf(tile, playerId) / Math.max(1, tile.totalShares)) * 100;

export const valuationLiveOf = (tile: PropertyTile): number =>
  tile.valuation > 0 ? tile.valuation : tile.baseCost;

/** Each building tier lifts the cash-on-cash yield by 3 percentage points. */
export const TIER_YIELD_BONUS = 0.03;
/** Hard ceiling for the effective (tier-boosted) yield. */
export const MAX_EFFECTIVE_RENT_YIELD = 0.65;

/** Effective yield at a hypothetical tier: drifting base rate + the tier ladder bonus. */
export const rentYieldAtLevel = (tile: PropertyTile, level: number): number => {
  const stored = tile.rentYieldRate;
  const base = typeof stored === 'number' && Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_RENT_YIELD;
  const anchored = Math.min(MAX_RENT_YIELD, Math.max(MIN_RENT_YIELD, base));
  return Math.min(MAX_EFFECTIVE_RENT_YIELD, anchored + Math.max(0, level) * TIER_YIELD_BONUS);
};

/** Live yield of the tile at its current tier. */
export const rentYieldOf = (tile: PropertyTile): number => rentYieldAtLevel(tile, tile.upgradeLevel);

/** Independent random drift of the yield inside the 10 %–25 % corridor (±2 pts/round). */
export const driftRentYield = (current: number | undefined): number => {
  const anchor =
    typeof current === 'number' && Number.isFinite(current) && current > 0 ? current : DEFAULT_RENT_YIELD;
  return Math.min(MAX_RENT_YIELD, Math.max(MIN_RENT_YIELD, anchor + (Math.random() * 0.04 - 0.02)));
};

/** Base rent at the current valuation & live yield, scaled by the tier uplift (1 + tier·0.4). */
export const baseRentOf = (tile: PropertyTile): number =>
  Math.round(valuationLiveOf(tile) * rentYieldOf(tile) * (1 + tile.upgradeLevel * 0.4));

/** Live rent = dynamic base rent × market phase multiplier. Frozen tiles pay nothing.
 *  Monopoly bonus (+25 %) applies when a player owns >50 % of total district shares. */
export const rentFor = (tile: PropertyTile, phase: MarketPhase): number => {
  if (tile.isFrozen) return 0;
  const phaseMul = phase === 'BOOM' ? 1.5 : phase === 'CRISIS' ? 0.5 : 1;
  let rent = Math.round(baseRentOf(tile) * phaseMul);
  return rent;
};

/** Rent including monopoly bonus for a specific owner in a district.
 *  Monopoly benefits:
 *  - +25% rent on all tiles in the district
 *  - +20% valuation on all tiles in the district
 *  - Free upgrades on one tile per round
 *  - Immunity from rent on tiles in the district owned by the player
 *  - +10% credit score boost when holding a monopoly
 */
export const rentWithMonopolyBonus = (tile: PropertyTile, phase: MarketPhase, board: PropertyTile[], ownerId: string): number => {
  let rent = rentFor(tile, phase);
  const districtProgress = districtSetOf(board, ownerId, tile.district);
  if (districtProgress.complete) {
    rent = Math.round(rent * 1.25); // +25% rent bonus
  }
  return rent;
};

/** Check if a player has monopoly in a district. */
export const hasMonopoly = (board: PropertyTile[], playerId: string, district: District): boolean => {
  const progress = districtSetOf(board, playerId, district);
  return progress.complete;
};

/** Monopoly benefits for a player in a district.
 *  - +25% rent on all tiles in the district
 *  - +20% valuation on all tiles in the district
 *  - Free upgrade on one tile per round
 *  - +10% credit score boost when holding a monopoly
 */
export const monopolyBenefits = (board: PropertyTile[], playerId: string, district: District) => {
  const progress = districtSetOf(board, playerId, district);
  return {
    hasMonopoly: progress.complete,
    rentMultiplier: progress.complete ? 1.25 : 1.0,
    valuationMultiplier: progress.complete ? 1.2 : 1.0,
    freeUpgrade: progress.complete,
    creditScoreBoost: progress.complete ? 10 : 0,
  };
};

export const valuationOf = (tile: PropertyTile, phase: MarketPhase): number => {
  const phaseMul = phase === 'BOOM' ? 1.25 : phase === 'CRISIS' ? 0.7 : 1;
  // Legacy anchor: upgrades still lift the anchor value for AI heuristics.
  let valuation = Math.round(valuationLiveOf(tile) * phaseMul * (1 + tile.upgradeLevel * 0.35));
  // Monopoly valuation bonus: +20% valuation when a player holds monopoly in the district
  // (checked by the caller via hasMonopoly)
  return valuation;
};

/** Per-round stochastic drift: +1.5–3% secular + ±2–4% noise + cycle modifier. */
export const driftValuation = (current: number, phase: MarketPhase): { next: number; deltaPct: number } => {
  const drift = 0.015 + Math.random() * 0.015; // +1.5% to +3.0%
  const noise = (Math.random() * 0.08) - 0.04; // -4% to +4%
  const cycleModifier = phase === 'BOOM' ? 0.03 + Math.random() * 0.02 : phase === 'CRISIS' ? -(0.05 + Math.random() * 0.05) : 0;
  const netDelta = drift + noise + cycleModifier;
  const next = Math.max(10, Math.round(current * (1 + netDelta)));
  return { next, deltaPct: netDelta * 100 };
};

const phaseMultipliers = (phase: MarketPhase) => {
  switch (phase) {
    case 'BOOM':
      return { rent: 1.5, valuation: 1.25, interest: 0.03 };
    case 'CRISIS':
      return { rent: 0.5, valuation: 0.7, interest: 0.08 };
    default:
      return { rent: 1, valuation: 1, interest: 0.05 };
  }
};

const SAVE_KEY = 'equity-tycoon-save-v1';

const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
  board: buildBoard(),
  players: buildPlayers(),
  market: { ...INITIAL_MARKET },
  currentPlayerIndex: 0,
  turnPhase: 'TURN_START',
  events: [{ id: 1, round: 1, message: 'Market opens in Stagnation. Endless mode — play to $100,000 or last-tycoon-standing.', kind: 'INFO' }],

  selectedTileIndex: null,
  movementPoints: 0,
  pushLuckUsed: false,
  crashRiskArmed: false,
  idlePenalty: 0,
  reachableIndices: [],
  inspectTileIndex: null,
  auction: { ...emptyAuction },
  pendingOffer: null,
  emergency: null,
  fastTravelFrom: null,
  metroRiddenThisTurn: false,
  takeoverTileId: null,
  lastTakeoverRound: -1,
  blackSwan: { event: null, title: '', description: '' },
  tradeOffer: null,
  attention: null,
  resumePhase: null,
  botStatus: null,
  moneyTransfers: [],
  insolvency: null,
  tradeToast: null,
  botPurchaseToast: null,
  newPropertyAlerts: [],
  spotlightTileId: null,
  lastRentCredit: null,
  rentSeq: 0,
  lastBotTradeRound: -1,
  proposal: { ...emptyProposal },
  expansionPending: false,
  gameOver: false,
  winnerId: null,
  epoch: 1,
  gameStarted: false,
  lobby: { ...DEFAULT_LOBBY, players: DEFAULT_LOBBY.players.map((p) => ({ ...p })) },
  victory: 'NET_WORTH_100K',
  handover: null,
  hotseatReview: null,
  loans: [],
  investments: [],
  financialDeskOpen: false,
  financialDeskTab: 'LOANS',
  guideTileId: null,
  inspectedPlayerId: null,

  logEvent: (message, kind) => {
    const { events, market } = get();
    const next: GameEvent = { id: events.length + 1, round: market.currentRound, message, kind };
    set({ events: [...events, next].slice(-60) });
  },

  setTurnPhase: (phase) => set({ turnPhase: phase }),

  setInspectTile: (index) => set({ inspectTileIndex: index }),

  setBotStatus: (broadcast) => {
    const current = get().botStatus;
    // Identity guard: an identical broadcast must not create new state (avoids render churn).
    if (
      current === broadcast ||
      (current &&
        broadcast &&
        current.playerId === broadcast.playerId &&
        current.phase === broadcast.phase &&
        current.text === broadcast.text)
    ) {
      return;
    }
    set({ botStatus: broadcast });
  },

  /** Public dispatch; the automatic cash-diff watcher below uses the same path. */
  triggerMoneyTransfer: ({ fromId, toId, amount, reason }) => {
    const signed = Number(amount);
    if (!Number.isFinite(signed)) return;
    const rounded = Math.round(Math.abs(signed));
    // A negative amount means the flow runs the other way — flip the endpoints instead
    // of inventing money, and ignore zero / self-directed moves entirely.
    const from = signed < 0 ? toId : fromId;
    const to = signed < 0 ? fromId : toId;
    if (rounded <= 0 || from === to) return;
    const transfer: MoneyTransfer = { id: ++moneyTransferSeq, fromId: from, toId: to, amount: rounded, reason, ts: Date.now() };
    set({ moneyTransfers: pruneMoneyTransfers([...get().moneyTransfers, transfer]) });
  },

  clearMoneyTransfers: () => set({ moneyTransfers: [] }),

  clearTradeToast: () => set({ tradeToast: null }),

  clearBotPurchaseToast: () => set({ botPurchaseToast: null }),

  // ---- New Property Spotlight: freshly zoned annexes get a clear announcement ----
  setNewPropertyAlert: (tile) => set({ newPropertyAlerts: [...get().newPropertyAlerts, tile] }),

  dismissNewPropertyAlert: () => {
    const { newPropertyAlerts } = get();
    const [current, ...rest] = newPropertyAlerts;
    if (!current) {
      set({ spotlightTileId: null });
      return;
    }
    // Acknowledged → the tile starts glowing on the board so the player can find it.
    set({ newPropertyAlerts: rest, spotlightTileId: current.id });
  },

  clearSpotlight: () => set({ spotlightTileId: null }),

  /** Internal trade execution (already validated). */
  respondToTradeInternal: (offer, note) => {
    const { board, players } = get();
    let nextBoard = [...board];
    const nextPlayers = [...players];
    const moveLots = (fromId: string, toId: string, lots: { tileId: string; shares: number }[]) => {
      lots.forEach((lot) => {
        const index = nextBoard.findIndex((t) => t.id === lot.tileId);
        if (index === -1) return;
        const tile = nextBoard[index];
        const owned = tile.shares[fromId] ?? 0;
        const move = Math.min(lot.shares, owned);
        const nextShares = { ...tile.shares };
        nextShares[fromId] = owned - move;
        nextShares[toId] = (nextShares[toId] ?? 0) + move;
        nextBoard[index] = { ...tile, shares: nextShares };
      });
    };
    moveLots(offer.senderId, offer.receiverId, offer.offeredShares);
    moveLots(offer.receiverId, offer.senderId, offer.requestedShares);
    const settled = withCash(withCash(nextPlayers, offer.senderId, offer.requestedCash - offer.offeredCash), offer.receiverId, offer.offeredCash - offer.requestedCash);
    set({ board: nextBoard, players: settled, tradeOffer: { ...offer, status: 'ACCEPTED' } });
    const sender = players.find((p) => p.id === offer.senderId);
    const receiver = players.find((p) => p.id === offer.receiverId);
    get().logEvent(`TRADE EXECUTED: ${sender?.name} ⇄ ${receiver?.name} (${note}).`, 'GAIN');

    // Settlement toast: itemise every cash / share movement that just changed hands.
    const nameOf = (tileId: string) => board.find((t) => t.id === tileId)?.name ?? tileId;
    const lines: string[] = [];
    if (offer.offeredCash > 0) lines.push(`${sender?.name ?? 'Sender'} paid ${money(offer.offeredCash)} cash.`);
    if (offer.requestedCash > 0) lines.push(`${receiver?.name ?? 'Receiver'} paid ${money(offer.requestedCash)} cash.`);
    offer.offeredShares.forEach((lot) => lines.push(`${lot.shares} × ${nameOf(lot.tileId)} shares → ${receiver?.name ?? 'receiver'}.`));
    offer.requestedShares.forEach((lot) => lines.push(`${lot.shares} × ${nameOf(lot.tileId)} shares → ${sender?.name ?? 'sender'}.`));
    if (lines.length === 0) lines.push('No assets changed hands.');
    set({
      tradeToast: {
        id: Date.now(),
        title: `🤝 TRADE EXECUTED · ${sender?.name ?? 'Rival'} ⇄ ${receiver?.name ?? 'Rival'}`,
        lines,
      },
    });
  },

  raiseAttention: (a) => set({ ...suspensionOf(get(), 'ATTENTION_INTERCEPT'), attention: a }),

  clearAttention: () => set({ attention: null }),

  /** Modal CTA: unfreeze the loop, restore the suspended phase, resume deferred bot ballots. */
  acknowledgeAttention: () => {
    const { attention, proposal, resumePhase, turnPhase } = get();
    if (!attention) return;
    const fallback = turnPhase === 'ATTENTION_INTERCEPT' ? 'TURN_END' : turnPhase;
    set({ attention: null, ...releaseOf({ resumePhase: resumePhase ?? fallback }) });
    // A shareholder vote tabled behind the gate now collects the bots' ballots.
    if (proposal.status === 'PENDING') get().botVoteOnProposal(proposal.propertyId);
  },

  /** FSM → TRADE_NEGOTIATION while the human is in the deal builder. */
  beginTradeNegotiation: () => set({ ...suspensionOf(get(), 'TRADE_NEGOTIATION') }),

  /** Restore the suspended phase once the deal builder closes. */
  endTradeNegotiation: () => {
    const { turnPhase, resumePhase } = get();
    if (turnPhase !== 'TRADE_NEGOTIATION') return;
    set({ ...releaseOf({ resumePhase }) });
  },

  stageAuction: (tileId, tranche, actorName) => {
    const { board, market, players } = get();
    const tile = board.find((t) => t.id === tileId);
    if (!tile) return;
    const rent = rentFor(tile, market.phase);
    const actor = actorName ?? players.find((p) => p.isAI)?.name ?? 'A bot';
    set({
      ...suspensionOf(get(), 'ATTENTION_INTERCEPT'),
      botStatus: null,
      attention: {
        kind: 'AUCTION',
        title: '⚠️ ATTENTION REQUIRED: Public Auction',
        body: `${actor} landed on ${tile.name} and declined direct purchase. ${tranche} shares of ${tile.name} are now heading to public auction! Rent ${money(rent)}/visit — the countdown starts only when you enter.`,
        tileId,
        tranche,
        stake: stakeBreakdownOf(players, tile, market.phase, tranche),
      },
    });
  },

  enterStagedAuction: () => {
    const { attention } = get();
    if (!attention || attention.kind !== 'AUCTION' || !attention.tileId) return;
    // The countdown only starts here — never while the human is reading the alert.
    set({ attention: null, resumePhase: null });
    get().openAuction(attention.tileId, attention.tranche ?? 25);
  },

  proposeTrade: (offer) => {
    const { board, players } = get();
    const receiver = players.find((p) => p.id === offer.receiverId);
    const sender = players.find((p) => p.id === offer.senderId);
    if (!receiver || !sender) return;

    if (receiver.isAI) {
      // Anti-leader cartel: the detected market hegemon never gets set-completing paper.
      const leader = detectMarketLeader(board, players, get().netWorthOf);
      const verdict = evaluateBotTrade(board, players, offer, leader?.isLeader ? leader.id : undefined);
      if (verdict.accept) {
        get().respondToTradeInternal(offer, verdict.note);
        // Keep the verdict on the offer so the deal builder can show WHY it was taken.
        set({ tradeOffer: { ...offer, status: 'ACCEPTED', verdictKind: verdict.reason, verdictNote: verdict.note } });
      } else {
        set({ tradeOffer: { ...offer, status: 'REJECTED', verdictKind: verdict.reason, verdictNote: verdict.note } });
        get().logEvent(`${receiver.name} REJECTED your trade: ${verdict.note}`, 'RISK');
      }
      return;
    }

    // Human → human (hotseat): freeze the loop and hand the controls over to the
    // receiving player at the same PC — no bot verdict, no attention auto-flow.
    if (!sender.isAI) {
      set({
        ...suspensionOf(get(), 'TRADE_NEGOTIATION'),
        tradeOffer: { ...offer, status: 'PENDING' },
        hotseatReview: { ...offer, status: 'PENDING' },
      });
      get().logEvent(`${sender.name} sent a trade proposal to ${receiver.name} — hand over the controls.`, 'INFO');
      return;
    }

    // Bot → human: reviewed inside the TRADE_NEGOTIATION FSM state, behind the
    // hard-pause attention gate (all background timers freeze until it is answered).
    const humanId = players.find((p) => !p.isAI)?.id ?? '';
    const nameOf = (tileId: string) => board.find((t) => t.id === tileId)?.name ?? tileId;
    // Plain-language summary: what the SENDER gives vs. what they ask in return.
    const give = [
      ...(offer.offeredCash > 0 ? [`${money(offer.offeredCash)} hotovosti`] : []),
      ...offer.offeredShares.map((lot) => `${lot.shares} × ${nameOf(lot.tileId)}`),
    ];
    const take = [
      ...(offer.requestedCash > 0 ? [`${money(offer.requestedCash)} hotovosti`] : []),
      ...offer.requestedShares.map((lot) => `${lot.shares} × ${nameOf(lot.tileId)}`),
    ];
    const stake = [...offer.offeredShares, ...offer.requestedShares]
      .map((lot) => {
        const tile = board.find((t) => t.id === lot.tileId);
        if (!tile) return null;
        return {
          label: `${tile.name} · ${lot.shares} sh`,
          value: `${money(lotValue(board, [lot]))} (you hold ${tile.shares[humanId] ?? 0} sh)`,
        };
      })
      .filter((entry): entry is { label: string; value: string } => entry !== null);
    set({
      ...suspensionOf(get(), 'TRADE_NEGOTIATION'),
      tradeOffer: { ...offer, status: 'PENDING' },
      attention: {
        kind: 'TRADE',
        title: '⚠️ ATTENTION REQUIRED: Incoming Trade Offer',
        body: describeTrade(board, players, offer),
        offerId: offer.id,
        tradeSummary: { give, take, fromName: sender.name, note: 'Přijmeš-li, usadí se hned. Odmítneš-li, obchod skončí bez následků.' },
        stake: [
          { label: 'Cash you receive', value: money(offer.offeredCash) },
          { label: 'Cash you pay', value: money(offer.requestedCash) },
          { label: 'Deal meter (for them)', value: `${describeDealMeter(board, offer).ratio.toFixed(2)}×` },
          ...stake.slice(0, 4),
        ],
      },
    });
  },

  respondToTrade: (accept) => {
    const { tradeOffer, resumePhase, turnPhase } = get();
    if (!tradeOffer) return;
    if (accept) {
      get().respondToTradeInternal(tradeOffer, 'human accepted the deal');
    } else {
      get().logEvent('Trade offer declined — the rival keeps their book.', 'INFO');
      set({ tradeToast: { id: Date.now(), title: '🚫 Trade declined', lines: ['No cash or shares changed hands.'] } });
    }
    const fallback = turnPhase === 'TRADE_NEGOTIATION' ? 'TURN_END' : turnPhase;
    set({ tradeOffer: null, attention: null, ...releaseOf({ resumePhase: resumePhase ?? fallback }) });
  },

  /** Clears a settled verdict only — a PENDING offer stays reviewable. */
  dismissTradeVerdict: () => {
    const { tradeOffer } = get();
    if (tradeOffer && tradeOffer.status !== 'PENDING') set({ tradeOffer: null });
  },

  botProposeTradeToHuman: () => {
    const { board, players, currentPlayerIndex, market, lastBotTradeRound } = get();
    const bot = players[currentPlayerIndex];
    const human = players.find((p) => !p.isAI);
    if (!bot || !bot.isAI || !human || bot.isBankrupt || human.isBankrupt) return;
    // One proposal per bot per round: the gate can never re-fire in a loop.
    if (lastBotTradeRound >= market.currentRound) return;

    const botHoldings = board
      .map((tile) => ({ tile, owned: tile.shares[bot.id] ?? 0 }))
      .filter((h) => h.owned > 0)
      .sort((a, b) => a.tile.valuation / a.tile.totalShares - b.tile.valuation / b.tile.totalShares);
    const humanHoldings = board
      .map((tile) => ({ tile, owned: tile.shares[human.id] ?? 0 }))
      .filter((h) => h.owned > 0)
      .sort((a, b) => b.tile.valuation / b.tile.totalShares - a.tile.valuation / a.tile.totalShares);
    if (botHoldings.length === 0 || humanHoldings.length === 0) return;

    // Archetype targeting: aggressive bots chase majority control, opportunistic bots
    // hunt blocking minorities in busy districts, conservative bots stay defensive.
    const target =
      bot.aiArchetype === 'AGGRESSIVE'
        ? humanHoldings.find((h) => (h.tile.shares[bot.id] ?? 0) > 0) ?? humanHoldings[0]
        : humanHoldings[0];
    const giveLot = bot.aiArchetype === 'CONSERVATIVE' ? botHoldings[0] : botHoldings[botHoldings.length - 1];

    const giveShares = Math.max(1, Math.ceil(giveLot.owned * (bot.aiArchetype === 'OPPORTUNISTIC' ? 0.5 : 0.6)));
    const takeShares = Math.max(1, Math.ceil(target.owned * (bot.aiArchetype === 'AGGRESSIVE' ? 0.5 : 0.4)));
    const unitGive = valuationLiveOf(giveLot.tile) / Math.max(1, giveLot.tile.totalShares);
    const unitTake = valuationLiveOf(target.tile) / Math.max(1, target.tile.totalShares);
    const reserve = bot.aiArchetype === 'CONSERVATIVE' ? 1000 : 200;
    // Sweeten just above par (~1.05×) so the human's deal meter reads "Fair".
    const offeredCash = Math.max(
      0,
      Math.min(Math.max(0, bot.cash - reserve), Math.round(takeShares * unitTake * 1.05 - giveShares * unitGive)),
    );
    const requestedCash = Math.max(0, Math.round(giveShares * unitGive * 0.75));

    const offer: TradeOffer = {
      id: `trade-${Date.now()}`,
      senderId: bot.id,
      receiverId: human.id,
      offeredCash,
      offeredShares: [{ tileId: giveLot.tile.id, shares: giveShares }],
      requestedCash,
      requestedShares: [{ tileId: target.tile.id, shares: takeShares }],
      status: 'PENDING',
    };

    set({ lastBotTradeRound: market.currentRound });
    get().setBotStatus({ playerId: bot.id, phase: 'GOVERN', text: `${bot.name} is tabling a bilateral trade proposal…` });
    get().logEvent(`${bot.name} tables a bilateral trade proposal to you.`, 'INFO');
    get().proposeTrade(offer);
  },

  /** District set-collection deal builder. Scenarios (in priority order):
   *  1) HUMAN is 1 tile short of a district monopoly and the bot holds a blocking
   *     stake there → bot SELLS the missing stake for tile value + 35 % premium.
   *  2) BOT is 1 tile short of its own district monopoly and the human holds the
   *     missing stake → bot OFFERS tile value + 25 % premium in cash.
   *  Otherwise no trade is tabled — bots no longer make random confusing offers. */
  botProposeDistrictTrade: () => {
    const { board, players, currentPlayerIndex, market, lastBotTradeRound } = get();
    const bot = players[currentPlayerIndex];
    const human = players.find((p) => !p.isAI);
    if (!bot || !bot.isAI || !human || bot.isBankrupt || human.isBankrupt) return;
    if (lastBotTradeRound >= market.currentRound) return;

    const districts = [...new Set(board.filter((t) => t.tileType === 'COMMERCIAL').map((t) => t.district))];
    const majorityPctOf = (tile: PropertyTile, id: string) => (tile.shares[id] ?? 0) > tile.totalShares / 2;

    // Scenario 1 — human needs the missing colour, bot sells its blocking stake.
    for (const district of districts) {
      const tiles = getDistrictTiles(board, district);
      const humanMajorityTiles = tiles.filter((t) => majorityPctOf(t, human.id));
      if (humanMajorityTiles.length !== tiles.length - 1) continue; // only when 1 tile is missing
      const missing = tiles.find((t) => !majorityPctOf(t, human.id) && (t.shares[bot.id] ?? 0) > 0);
      if (!missing) continue;
      const botStake = missing.shares[bot.id] ?? 0;
      const humanStake = missing.shares[human.id] ?? 0;
      const giveShares = Math.min(botStake, Math.max(1, Math.ceil(missing.totalShares / 2) - humanStake));
      if (giveShares <= 0) continue;
      const unit = valuationLiveOf(missing) / Math.max(1, missing.totalShares);
      const price = Math.round(giveShares * unit * 1.35);
      if (human.cash < price * 0.5) continue; // human cannot plausibly pay
      const offer: TradeOffer = {
        id: `trade-${Date.now()}`,
        senderId: bot.id,
        receiverId: human.id,
        offeredCash: 0,
        offeredShares: [{ tileId: missing.id, shares: giveShares }],
        requestedCash: price,
        requestedShares: [],
        status: 'PENDING',
      };
      set({ lastBotTradeRound: market.currentRound });
      get().setBotStatus({ playerId: bot.id, phase: 'GOVERN', text: `${bot.name} offers the missing ${missing.name} stake for ${money(price)}` });
      get().logEvent(`${bot.name} offers to sell ${giveShares} × ${missing.name} (completes your ${district} set) for ${money(price)}.`, 'INFO');
      get().proposeTrade(offer);
      return;
    }

    // Scenario 2 — bot completes its own colour, human holds the missing stake.
    for (const district of districts) {
      const tiles = getDistrictTiles(board, district);
      const botMajorityTiles = tiles.filter((t) => majorityPctOf(t, bot.id));
      if (botMajorityTiles.length !== tiles.length - 1) continue;
      const missing = tiles.find((t) => !majorityPctOf(t, bot.id) && (t.shares[human.id] ?? 0) > 0);
      if (!missing) continue;
      const humanStake = missing.shares[human.id] ?? 0;
      const botStake = missing.shares[bot.id] ?? 0;
      const takeShares = Math.min(humanStake, Math.max(1, Math.ceil(missing.totalShares / 2) - botStake));
      if (takeShares <= 0) continue;
      const unit = valuationLiveOf(missing) / Math.max(1, missing.totalShares);
      const price = Math.round(takeShares * unit * 1.25);
      if (bot.cash < price) continue;
      const offer: TradeOffer = {
        id: `trade-${Date.now()}`,
        senderId: bot.id,
        receiverId: human.id,
        offeredCash: price,
        offeredShares: [],
        requestedCash: 0,
        requestedShares: [{ tileId: missing.id, shares: takeShares }],
        status: 'PENDING',
      };
      set({ lastBotTradeRound: market.currentRound });
      get().setBotStatus({ playerId: bot.id, phase: 'GOVERN', text: `${bot.name} bids ${money(price)} for the missing ${missing.name} stake` });
      get().logEvent(`${bot.name} offers ${money(price)} for ${takeShares} × ${missing.name} (completes its ${district} set).`, 'INFO');
      get().proposeTrade(offer);
      return;
    }
    // No logical deal available — stay quiet instead of confusing the player.
  },

  rideMetro: (destinationIndex) => {
    const state = get();
    const { players, currentPlayerIndex, board, fastTravelFrom } = state;
    const player = players[currentPlayerIndex];
    if (!player || fastTravelFrom === null) return;
    const dest = destinationIndex % board.length;
    const destTile = board[dest];
    if (destTile.tileType !== 'TRANSIT' || dest === fastTravelFrom) return;
    if (player.cash < 200) {
      get().logEvent('Not enough cash for the metro fare ($200).', 'RISK');
      return;
    }
    set({
      players: withCash(players, player.id, -200).map((p) => (p.id === player.id ? { ...p, currentTileIndex: dest } : p)),
      fastTravelFrom: null,
      metroRiddenThisTurn: true,
      selectedTileIndex: dest,
      turnPhase: 'TILE_RESOLUTION',
      botStatus: player.isAI
        ? { playerId: player.id, phase: 'MOVE', text: `${player.name} riding the Metro to ${destTile.name} (−$200)…` }
        : null,
    });
    get().logEvent(`${player.name} rode the Metro to ${destTile.name} (−$200).`, 'INFO');
    get().resolveTile();
  },

  skipMetro: () => {
    const { fastTravelFrom, board, players, currentPlayerIndex } = get();
    if (fastTravelFrom === null) return;
    set({ fastTravelFrom: null, turnPhase: 'TURN_END', selectedTileIndex: null, movementPoints: 0, reachableIndices: [], crashRiskArmed: false, idlePenalty: 0 });
    get().logEvent(`${players[currentPlayerIndex]?.name ?? 'Player'} stayed at ${board[fastTravelFrom]?.name}.`, 'INFO');
  },

  buyInsurance: (playerId, policy) => {
    const { players } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player || player.insuranceTurnsRemaining > 0) return;
    const meta = INSURANCE_POLICY_META[policy];
    const cost = Math.round(get().netWorthOf(playerId) * meta.premiumPct);
    if (cost <= 0 || player.cash < cost) {
      get().logEvent(`Insurance premium ${money(cost)} unaffordable.`, 'RISK');
      return;
    }
    set({
      players: withCash(players, playerId, -cost).map((p) =>
        p.id === playerId
          ? { ...p, insuranceTurnsRemaining: meta.turns, insuranceCoverageRate: meta.coverage, insurancePolicy: policy }
          : p,
      ),
    });
    get().logEvent(
      `${player.name} activated ${meta.label} (${meta.turns} rounds, covers ${Math.round(meta.coverage * 100)}% of rent${meta.takeoverImmunity ? ' + hostile-takeover immunity' : ''}) for ${money(cost)}.`,
      'INFO',
    );
    get().recordFinancial(playerId, 'EXPENSE', 'insurancePremiums', `${meta.label} — pojistné`, cost);
  },

  dismissBlackSwan: () => set({ blackSwan: { event: null, title: '', description: '' } }),

  portfolioValueOf: (playerId) => {
    const { board, market } = get();
    return board.reduce((sum, tile) => sum + (sharesOf(tile, playerId) / Math.max(1, tile.totalShares)) * valuationOf(tile, market.phase), 0);
  },

  investmentValueOf: (playerId) =>
    get()
      .investments.filter((entry) => entry.playerId === playerId)
      .reduce((sum, entry) => sum + entry.value, 0),

  investmentOf: (playerId, tier) =>
    get().investments.find((entry) => entry.playerId === playerId && entry.tier === tier),

  loanExposureOf: (playerId) =>
    get()
      .loans.filter((loan) => loan.playerId === playerId)
      .reduce((sum, loan) => sum + loan.outstanding, 0),

  netWorthOf: (playerId) => {
    const { players } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player) return 0;
    // Treasury funds count as assets, structured-loan principal as liabilities.
    return Math.round(
      player.cash + get().portfolioValueOf(playerId) + get().investmentValueOf(playerId) - player.debt - get().loanExposureOf(playerId),
    );
  },

  debtCeilingOf: (playerId) => {
    const { players } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player) return 0;
    const liquid = Math.max(
      0,
      player.cash + get().portfolioValueOf(playerId) + get().investmentValueOf(playerId) - player.debt - get().loanExposureOf(playerId),
    );
    // Lenders shrink the line for distressed grades (C → 80 %, D → 50 %).
    const ratingMultiplier = CREDIT_RATING_META[creditRatingOf(player.creditScore)].ceilingMultiplier;
    return Math.round(liquid * DEBT_CEILING_RATIO * ratingMultiplier);
  },

  // ---- Player Financial Dossier ---------------------------------------------------

  setInspectedPlayer: (playerId) => set({ inspectedPlayerId: playerId }),

  recordFinancial: (playerId, kind, category, label, amount) => {
    const value = Math.round(amount);
    if (!Number.isFinite(value) || value <= 0) return;
    const { players, market } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player) return;
    const stats: PlayerFinancialStats = { ...(player.financialStats ?? emptyFinancialStats()) };
    stats[category] = (stats[category] ?? 0) + value;
    const entry = {
      id: (player.ledger?.length ? player.ledger[0].id : 0) + 1 + Math.floor(Math.random() * 1000),
      round: market.currentRound,
      kind,
      category,
      label,
      amount: value,
    };
    // Newest first, capped so the audit log stays a rolling 40-line window.
    const ledger = [entry, ...(player.ledger ?? [])].slice(0, 40);
    set({ players: players.map((p) => (p.id === playerId ? { ...p, financialStats: stats, ledger } : p)) });
  },

  // ---- Financial Desk: structured loans -------------------------------------------

  openFinancialDesk: (tab) => set({ financialDeskOpen: true, financialDeskTab: tab ?? get().financialDeskTab }),
  closeFinancialDesk: () => set({ financialDeskOpen: false }),

  // ---- Special tile guide (interactive explainer) ----
  openTileGuide: (tileId) => set({ guideTileId: tileId }),
  closeTileGuide: () => set({ guideTileId: null }),

  // ---- Bot tactical execution: applies the tranche AND reports exact numbers ----
  botExecutePurchase: (playerId, tileId, shares, structure = 'MAX_AFFORDABLE', note) => {
    const { board, players } = get();
    const player = players.find((p) => p.id === playerId);
    const tile = board.find((entry) => entry.id === tileId);
    if (!player || !tile) return 0;
    const float = floatSharesOf(tile);
    const quantity = Math.max(0, Math.min(Math.floor(shares), float));
    if (quantity <= 0) return 0;
    const unit = Math.max(1, pricePerShareOf(tile));
    const cost = quantity * unit;

    get().addShares(playerId, tileId, quantity, 0);
    // Exact numbers into the ticker, localized: "Venture Bot koupil 65 akcií AI Lab za $975".
    const why = tr(BOT_STRUCTURE_REASON_KEY[structure]);
    const headline = tr('ticker.botBought', {
      name: player.name,
      shares: quantity,
      tile: tTile(tile.name),
      cost: money(cost),
    });
    get().logEvent(`${headline} — ${why}`, 'GAIN');
    set({ botPurchaseToast: { id: Date.now(), text: headline, color: player.color } });
    return cost;
  },

  takeStructuredLoan: (playerId, principal, termRounds, structure, rateOverride) => {
    const { players, market } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player || player.isBankrupt) return;
    const requested = Math.max(0, Math.round(principal));
    if (requested <= 0) return;

    // Market-linked pricing: the rate locks in at origination, the term is fixed.
    const baseRate = rateOverride ?? baseInterestRateFor(market.phase);
    const ratingMarkup = CREDIT_RATING_META[creditRatingOf(player.creditScore)].markup;
    const rate = structure === 'BALLOON' ? baseRate + ratingMarkup + BALLOON_RISK_PREMIUM : baseRate + ratingMarkup;
    const quote = quoteLoan(requested, rate, termRounds, structure, market.currentRound);

    // Structured loans live outside the informal credit line, so the ceiling check
    // nets out both exposures.
    const headroom = Math.max(0, get().debtCeilingOf(playerId) - player.debt - get().loanExposureOf(playerId));
    if (quote.principal > headroom) {
      get().logEvent(
        `${player.name} was refused: the ${LOAN_STRUCTURE_LABEL[structure].label} tranche exceeds the credit line (${money(headroom)} free).`,
        'RISK',
      );
      return;
    }

    const loan: Loan = {
      id: `loan-${++loanSeq}`,
      playerId,
      principal: quote.principal,
      ratePerRound: quote.ratePerRound,
      termRounds: quote.termRounds,
      structure,
      startedRound: market.currentRound,
      maturityRound: quote.maturityRound,
      roundsRemaining: quote.termRounds,
      outstanding: quote.principal,
      perRoundPayment: quote.perRoundPayment,
      totalInterest: quote.totalInterest,
      interestPaid: 0,
    };
    set({
      loans: [...get().loans, loan],
      players: withCash(players, playerId, quote.principal),
    });
    get().recordFinancial(
      playerId,
      'INCOME',
      'loanDraws',
      `${LOAN_STRUCTURE_LABEL[structure].label} @ ${(quote.ratePerRound * 100).toFixed(1)} %/round`,
      quote.principal,
    );
    get().logEvent(
      `${player.name} drew a ${money(quote.principal)} ${LOAN_STRUCTURE_LABEL[structure].label} @ ${(quote.ratePerRound * 100).toFixed(1)} %/round · ${quote.perRoundPayment > 0 ? `${money(quote.perRoundPayment)}/round` : '$0/round'} · matures R${quote.maturityRound} (overpayment ${money(quote.totalInterest)}).`,
      'INFO',
    );
  },

  loanPayoffOf: (loanId) => {
    const loan = get().loans.find((entry) => entry.id === loanId);
    if (!loan) return 0;
    // Balloon loans accrue silently, so the payoff quotes the compounded balance.
    const accrued =
      loan.structure === 'BALLOON'
        ? Math.max(0, loan.principal * Math.pow(1 + loan.ratePerRound, loan.termRounds - loan.roundsRemaining) - loan.outstanding)
        : 0;
    return earlyPayoffOf(loan.outstanding, accrued).total;
  },

  repayLoanEarly: (loanId) => {
    const { loans, players } = get();
    const loan = loans.find((entry) => entry.id === loanId);
    if (!loan) return;
    const player = players.find((p) => p.id === loan.playerId);
    if (!player) return;
    const payoff = get().loanPayoffOf(loanId);
    const fee = Math.max(0, payoff - Math.round(loan.outstanding));
    set({
      loans: loans.filter((entry) => entry.id !== loanId),
      players: withCash(players, player.id, -payoff),
    });
    get().logEvent(
      `${player.name} prepaid the ${LOAN_STRUCTURE_LABEL[loan.structure].label} on ${money(loan.principal)}: ${money(payoff)} incl. ${money(fee)} prepayment fee (2.5 %).`,
      'INFO',
    );
  },

  processLoans: () => {
    const { loans, players } = get();
    if (loans.length === 0) return;
    let nextPlayers = players;
    const survivors: Loan[] = [];
    /** Loans whose full maturity settlement must run through the strict default ladder. */
    const matured: Loan[] = [];
    const settled: { text: string; kind: GameEvent['kind'] }[] = [];

    loans.forEach((loan) => {
      const player = nextPlayers.find((p) => p.id === loan.playerId);
      if (!player || player.isBankrupt) return;
      const label = LOAN_STRUCTURE_LABEL[loan.structure].label;

      if (loan.structure === 'BALLOON') {
        // Zero-coupon: nothing per round; the compounded lump sum falls due at maturity.
        if (loan.roundsRemaining > 1) {
          survivors.push({ ...loan, roundsRemaining: loan.roundsRemaining - 1 });
          return;
        }
        matured.push({ ...loan, roundsRemaining: 0 });
        return;
      }

      if (loan.roundsRemaining <= 1) {
        // Final instalment + any remaining principal fall due together.
        matured.push({ ...loan, roundsRemaining: 0 });
        return;
      }

      const payment = loan.perRoundPayment;
      const interest = Math.round(loan.outstanding * loan.ratePerRound);
      const principalPart = Math.max(0, payment - interest);
      nextPlayers = withCash(nextPlayers, player.id, -payment);

      survivors.push({
        ...loan,
        roundsRemaining: loan.roundsRemaining - 1,
        outstanding: Math.max(0, loan.outstanding - principalPart),
        interestPaid: loan.interestPaid + interest,
      });
      settled.push({
        text: `${player.name} serviced the ${label}: ${money(payment)}/round (${money(interest)} interest, ${money(principalPart)} principal).`,
        kind: 'LOSS',
      });
      get().recordFinancial(player.id, 'EXPENSE', 'interestPaid', `${label} — úrok za kolo`, interest);
    });

    // Matured loans stay in state until the strict settlement resolves them, so a loan can
    // never be silently erased when the treasury is short.
    set({ players: nextPlayers, loans: [...survivors, ...matured] });
    settled.forEach((entry) => get().logEvent(entry.text, entry.kind));
    matured.forEach((loan) => get().resolveLoanMaturity(loan.id));
    // Debt service can push a treasury negative — the insolvency ladder must see it.
    get().runMarginCalls();
    get().checkBankruptcies();
  },

  /**
   * Strict maturity settlement. A maturing loan is resolved in exactly one of three ways —
   * never simply deleted:
   *
   *  1. **Emergency refinancing** (credit headroom covers the shortfall): the shortfall is
   *     restructured into a fresh annuity carrying a +3 % distress markup; the credit score
   *     drops by 20 points (A → B/C) so every future borrowing is priced off the new grade.
   *  2. **Bank foreclosure / asset seizure**: the bank force-sells shares worth
   *     `shortfall × 1.20` (minority stakes first, then majority holdings) and sweeps the
   *     remaining cash. Seized shares return to the public float.
   *  3. **Insolvency**: if the portfolio cannot cover the penalised shortfall the player is
   *     liquidated (bankruptcy).
   *
   * Returns the resolution that was applied.
   */
  resolveLoanMaturity: (loanId) => {
    const snapshot = get();
    const loan = snapshot.loans.find((entry) => entry.id === loanId);
    if (!loan) return 'SETTLED';
    const dropLoan = (): Loan[] => get().loans.filter((entry) => entry.id !== loanId);
    const player = snapshot.players.find((p) => p.id === loan.playerId);
    const label = LOAN_STRUCTURE_LABEL[loan.structure].label;
    if (!player || player.isBankrupt) {
      set({ loans: dropLoan() });
      return 'SETTLED';
    }

    // ---- Amount falling due at maturity -------------------------------------------
    const amountDue =
      loan.structure === 'BALLOON'
        ? Math.round(loan.principal * Math.pow(1 + loan.ratePerRound, loan.termRounds))
        : Math.round(loan.outstanding + loan.perRoundPayment);
    const maturedInterest = Math.max(0, amountDue - loan.outstanding);

    // ---- Happy path: the treasury covers the whole maturity -----------------------
    if (player.cash >= amountDue) {
      set({
        players: withCash(snapshot.players, player.id, -amountDue),
        loans: dropLoan(),
      });
      if (maturedInterest > 0) {
        get().recordFinancial(player.id, 'EXPENSE', 'interestPaid', `${label} — úrok při splatnosti`, maturedInterest);
      }
      get().logEvent(
        `${player.name}'s ${label} matured: ${money(amountDue)} settled in full${maturedInterest > 0 ? ` (${money(maturedInterest)} interest)` : ''}.`,
        'LOSS',
      );
      return 'SETTLED';
    }

    const shortfall = Math.round(amountDue - player.cash);

    // ---- Human debtor: NO silent refinancing / foreclosure — open the Resolution Desk.
    // Exception: a player whose TOTAL net worth cannot cover the payment has no options
    // left, so the strict ladder (foreclosure → bankruptcy) applies unchanged.
    if (!player.isAI && get().netWorthOf(player.id) >= amountDue) {
      get().raiseInsolvency(loanId);
      return 'PENDING';
    }

    // ---- Step 1: emergency refinancing --------------------------------------------
    const headroom = Math.max(0, snapshot.debtCeilingOf(player.id) - player.debt - snapshot.loanExposureOf(player.id));
    if (headroom >= shortfall) {
      const rate = baseInterestRateFor(snapshot.market.phase) + REFINANCE_RATE_PENALTY;
      const quote = quoteLoan(shortfall, rate, 3, 'ANNUITY', snapshot.market.currentRound);
      const restructured: Loan = {
        id: `loan-${++loanSeq}`,
        playerId: player.id,
        principal: quote.principal,
        ratePerRound: quote.ratePerRound,
        termRounds: quote.termRounds,
        structure: 'ANNUITY',
        startedRound: snapshot.market.currentRound,
        maturityRound: quote.maturityRound,
        roundsRemaining: quote.termRounds,
        outstanding: quote.principal,
        perRoundPayment: quote.perRoundPayment,
        totalInterest: quote.totalInterest,
        interestPaid: 0,
      };
      const nextCredit = Math.max(0, player.creditScore - REFINANCE_CREDIT_PENALTY);
      set({
        loans: [...dropLoan(), restructured],
        // The restructured principal exactly covers the shortfall, so the treasury drains to $0.
        players: snapshot.players.map((p) => (p.id === player.id ? { ...p, cash: 0, creditScore: nextCredit } : p)),
      });
      if (maturedInterest > 0) {
        get().recordFinancial(player.id, 'EXPENSE', 'interestPaid', `${label} — úrok při splatnosti`, maturedInterest);
      }
      get().recordFinancial(
        player.id,
        'EXPENSE',
        'bankFees',
        `Sankční úrok z refinancování (+${(REFINANCE_RATE_PENALTY * 100).toFixed(0)} %)`,
        Math.round(shortfall * REFINANCE_RATE_PENALTY),
      );
      get().logEvent(
        `⚠️ ${player.name} refinancoval nesplacenou jistinu ${money(shortfall)} novým úvěrem se sankčním úrokem ${(rate * 100).toFixed(1)} %/round. Bonita snížena na ${creditRatingOf(nextCredit)}!`,
        'RISK',
      );
      return 'REFINANCED';
    }

    // ---- Step 2: bank foreclosure / asset seizure (+20 % liquidation penalty) -----
    const requiredSeizureValue = Math.ceil(shortfall * (1 + FORECLOSURE_PENALTY));
    const holdings = snapshot.board
      .map((tile) => ({ tile, owned: sharesOf(tile, player.id) }))
      .filter((entry) => entry.owned > 0);
    // Priority 1: minority stakes (no control). Priority 2: majority holdings.
    const minority = holdings.filter((entry) => ownershipPct(entry.tile, player.id) <= 50);
    const majority = holdings.filter((entry) => ownershipPct(entry.tile, player.id) > 50);
    const seizureOrder = [...minority, ...majority];

    let nextBoard = snapshot.board;
    let seizedValue = 0;
    let remainingTarget = requiredSeizureValue;
    const seizedLines: string[] = [];

    for (const entry of seizureOrder) {
      if (remainingTarget <= 0) break;
      const unit = valuationLiveOf(entry.tile) / Math.max(1, entry.tile.totalShares);
      if (unit <= 0) continue;
      const sharesNeeded = Math.ceil(remainingTarget / unit);
      const take = Math.min(entry.owned, sharesNeeded);
      if (take <= 0) continue;
      const value = Math.round(take * unit);
      seizedValue += value;
      remainingTarget -= value;
      // Seized shares go back to the (implicit) public float / bank trust.
      nextBoard = nextBoard.map((tile) =>
        tile.id === entry.tile.id ? { ...tile, shares: { ...tile.shares, [player.id]: entry.owned - take } } : tile,
      );
      seizedLines.push(`${take}× ${entry.tile.name}`);
    }

    const penalty = Math.max(0, seizedValue - shortfall);
    const uncovered = Math.max(0, requiredSeizureValue - seizedValue);

    set({
      board: nextBoard,
      loans: dropLoan(),
      // Cash sweep: every remaining dollar goes to the bank.
      players: snapshot.players.map((p) => (p.id === player.id ? { ...p, cash: 0 } : p)),
    });

    if (maturedInterest > 0) {
      get().recordFinancial(player.id, 'EXPENSE', 'interestPaid', `${label} — úrok při splatnosti`, maturedInterest);
    }
    if (seizedValue > 0) {
      get().recordFinancial(
        player.id,
        'EXPENSE',
        'foreclosureSeized',
        `Exekuce bankou: ${seizedLines.join(', ')}`,
        seizedValue,
      );
    }
    if (penalty > 0) {
      get().recordFinancial(
        player.id,
        'EXPENSE',
        'bankFees',
        `Penále z exekuce (+${(FORECLOSURE_PENALTY * 100).toFixed(0)} %)`,
        penalty,
      );
    }

    get().logEvent(
      `🚨 EXEKUCE: Banka zabavila ${player.name} akcie v hodnotě ${money(seizedValue)} (+${(FORECLOSURE_PENALTY * 100).toFixed(0)} % penále) na úhradu nesplacené jistiny ${money(amountDue)}!`,
      'BANKRUPTCY',
    );

    // ---- Step 3: insolvency / bankruptcy check ------------------------------------
    if (uncovered > 0) {
      get().setBankrupt(player.id);
      get().logEvent(
        `${player.name} neměl dost aktiv na pokrytí exekuce (chybí ${money(uncovered)}) — insolvenční likvidace.`,
        'BANKRUPTCY',
      );
      return 'INSOLVENT';
    }
    return 'FORECLOSED';
  },

  // ---- Insolvency Resolution Desk (human debtors) -----------------------------------

  raiseInsolvency: (loanId) => {
    const snapshot = get();
    const loan = snapshot.loans.find((entry) => entry.id === loanId);
    if (!loan) return;
    const player = snapshot.players.find((p) => p.id === loan.playerId);
    if (!player || player.isBankrupt) return;
    const amountDue =
      loan.structure === 'BALLOON'
        ? Math.round(loan.principal * Math.pow(1 + loan.ratePerRound, loan.termRounds))
        : Math.round(loan.outstanding + loan.perRoundPayment);
    const label = LOAN_STRUCTURE_LABEL[loan.structure].label;
    set({
      insolvency: {
        playerId: player.id,
        playerName: player.name,
        loanId,
        amountDue,
        shortfall: Math.max(0, Math.round(amountDue - Math.max(0, player.cash))),
        label,
      },
    });
    get().logEvent(
      `🚨 ${player.name} nemá hotovost na splatnost ${label} (${money(amountDue)}) — insolvenční oddělení převzalo řízení.`,
      'BANKRUPTCY',
    );
  },

  insolvencyRefinance: () => {
    const { insolvency, players, loans, market } = get();
    if (!insolvency) return;
    const player = players.find((p) => p.id === insolvency.playerId);
    const loan = loans.find((entry) => entry.id === insolvency.loanId);
    if (!player || player.isBankrupt || !loan) {
      set({ insolvency: null });
      return;
    }
    const shortfall = Math.max(0, Math.round(insolvency.amountDue - Math.max(0, player.cash)));
    const headroom = Math.max(0, get().debtCeilingOf(player.id) - player.debt - get().loanExposureOf(player.id));
    if (headroom < shortfall) {
      get().logEvent('Banka odmítla refinancování — úvěrový strop vyčerpán.', 'RISK');
      return;
    }
    const rate = baseInterestRateFor(market.phase) + REFINANCE_RATE_PENALTY;
    const quote = quoteLoan(shortfall, rate, 3, 'ANNUITY', market.currentRound);
    const restructured: Loan = {
      id: `loan-${++loanSeq}`,
      playerId: player.id,
      principal: quote.principal,
      ratePerRound: quote.ratePerRound,
      termRounds: quote.termRounds,
      structure: 'ANNUITY',
      startedRound: market.currentRound,
      maturityRound: quote.maturityRound,
      roundsRemaining: quote.termRounds,
      outstanding: quote.principal,
      perRoundPayment: quote.perRoundPayment,
      totalInterest: quote.totalInterest,
      interestPaid: 0,
    };
    const maturedInterest = Math.max(0, insolvency.amountDue - loan.outstanding);
    const nextCredit = Math.max(0, player.creditScore - REFINANCE_CREDIT_PENALTY);
    set({
      loans: [...loans.filter((entry) => entry.id !== insolvency.loanId), restructured],
      // The restructured principal exactly covers the shortfall, so the treasury drains to $0.
      players: players.map((p) => (p.id === player.id ? { ...p, cash: 0, creditScore: nextCredit } : p)),
      insolvency: null,
    });
    if (maturedInterest > 0) {
      get().recordFinancial(player.id, 'EXPENSE', 'interestPaid', `${insolvency.label} — úrok při splatnosti`, maturedInterest);
    }
    get().recordFinancial(
      player.id,
      'EXPENSE',
      'bankFees',
      `Sankční úrok z refinancování (+${(REFINANCE_RATE_PENALTY * 100).toFixed(0)} %)`,
      Math.round(shortfall * REFINANCE_RATE_PENALTY),
    );
    get().logEvent(
      `${player.name} refinancoval splatnost ${insolvency.label}: deficit ${money(shortfall)} přestavěn na novou anuitu (rating ${creditRatingOf(nextCredit)}).`,
      'RISK',
    );
  },

  insolvencyWithdraw: (tier) => {
    const { insolvency, players, market } = get();
    if (!insolvency) return;
    const player = players.find((p) => p.id === insolvency.playerId);
    if (!player) return;
    const need = Math.max(0, insolvency.amountDue - player.cash);
    if (need <= 0) return;
    const entry = get().investmentOf(insolvency.playerId, tier);
    if (!entry || entry.value <= 0) return;
    // Early-exit penalty is charged on every withdrawal — request the GROSS amount
    // whose net still covers the shortfall (capped by the fund value).
    const heldRounds = Math.max(0, market.currentRound - entry.openedRound);
    const penaltyRate = investmentExitPenalty(tier, heldRounds);
    const gross = Math.min(Math.floor(entry.value), Math.ceil(need / Math.max(0.01, 1 - penaltyRate)));
    if (gross <= 0) return;
    get().withdrawInvestment(insolvency.playerId, tier, gross);
  },

  settleInsolvency: () => {
    const { insolvency, players, loans } = get();
    if (!insolvency) return;
    const player = players.find((p) => p.id === insolvency.playerId);
    const loan = loans.find((entry) => entry.id === insolvency.loanId);
    if (!player || !loan) {
      set({ insolvency: null });
      return;
    }
    if (player.cash < insolvency.amountDue) return; // CTA stays locked
    const maturedInterest = Math.max(0, insolvency.amountDue - loan.outstanding);
    set({
      players: withCash(players, player.id, -insolvency.amountDue),
      loans: loans.filter((entry) => entry.id !== insolvency.loanId),
      insolvency: null,
    });
    if (maturedInterest > 0) {
      get().recordFinancial(player.id, 'EXPENSE', 'interestPaid', `${insolvency.label} — úrok při splatnosti`, maturedInterest);
    }
    get().logEvent(
      `${player.name} uhradil splatnost ${insolvency.label} v plné výši ${money(insolvency.amountDue)} — insolvenční oddělení uzavřeno.`,
      'LOSS',
    );
  },

  declareInsolvencyBankruptcy: () => {
    const { insolvency, loans } = get();
    if (!insolvency) return;
    set({ loans: loans.filter((entry) => entry.id !== insolvency.loanId), insolvency: null });
    get().setBankrupt(insolvency.playerId);
    get().logEvent(
      `${insolvency.playerName} vyhlásil bankrot — čisté jmění nedosahuje splácené částky ${money(insolvency.amountDue)}.`,
      'BANKRUPTCY',
    );
  },

  // ---- Financial Desk: treasury investments ---------------------------------------

  investCash: (playerId, tier, amount) => {
    const { players, market, investments } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player || player.isBankrupt) return;
    const deposit = Math.max(0, Math.round(amount));
    if (deposit <= 0) return;
    if (player.cash < deposit) {
      get().logEvent(`${player.name} cannot fund ${money(deposit)} into the ${INVESTMENT_TIER_META[tier].name} fund — insufficient cash.`, 'RISK');
      return;
    }
    const existing = investments.find((entry) => entry.playerId === playerId && entry.tier === tier);
    const nextEntry: Investment = existing
      ? { ...existing, principal: existing.principal + deposit, value: existing.value + deposit }
      : { playerId, tier, principal: deposit, value: deposit, openedRound: market.currentRound, lastYieldRate: 0 };
    set({
      players: withCash(players, playerId, -deposit),
      investments: existing
        ? investments.map((entry) => (entry === existing ? nextEntry : entry))
        : [...investments, nextEntry],
    });
    get().logEvent(`${player.name} moved ${money(deposit)} into the ${INVESTMENT_TIER_META[tier].name} fund (${INVESTMENT_TIER_META[tier].label}).`, 'INFO');
  },

  withdrawInvestment: (playerId, tier, amount) => {
    const { players, investments, market } = get();
    const entry = investments.find((item) => item.playerId === playerId && item.tier === tier);
    const player = players.find((p) => p.id === playerId);
    if (!entry || !player || entry.value <= 0) return;
    const request = Math.max(0, Math.round(amount));
    const gross = Math.min(request, Math.floor(entry.value));
    if (gross <= 0) return;
    const heldRounds = Math.max(0, market.currentRound - entry.openedRound);
    const penaltyRate = investmentExitPenalty(tier, heldRounds);
    const penalty = Math.round(gross * penaltyRate);
    const net = gross - penalty;
    const remaining = entry.value - gross;
    set({
      players: withCash(players, playerId, net),
      investments:
        remaining <= 0.5
          ? investments.filter((item) => !(item.playerId === playerId && item.tier === tier))
          : investments.map((item) =>
              item.playerId === playerId && item.tier === tier
                ? { ...item, value: remaining, principal: Math.max(0, item.principal - gross) }
                : item,
            ),
    });
    get().logEvent(
      `${player.name} withdrew ${money(net)} from the ${INVESTMENT_TIER_META[tier].name} fund${penalty > 0 ? ` — early exit penalty ${money(penalty)} (${(penaltyRate * 100).toFixed(1)} %)` : ''}.`,
      penalty > 0 ? 'LOSS' : 'GAIN',
    );
  },

  processInvestments: () => {
    const { investments, market, players } = get();
    if (investments.length === 0) return;
    const next = investments.map((entry) => {
      const rate = rollInvestmentYield(entry.tier, market.phase);
      const value = Math.max(0, entry.value * (1 + rate));
      const holder = players.find((p) => p.id === entry.playerId);
      if (holder) {
        get().logEvent(
          `Treasury: ${holder.name}'s ${INVESTMENT_TIER_META[entry.tier].name} fund ${rate >= 0 ? 'earned' : 'lost'} ${(Math.abs(rate) * 100).toFixed(1)} % → ${money(value)} (${market.phase}).`,
          rate >= 0 ? 'GAIN' : 'LOSS',
        );
        const delta = Math.round(value - entry.value);
        if (delta > 0) {
          get().recordFinancial(holder.id, 'INCOME', 'investmentYield', `${INVESTMENT_TIER_META[entry.tier].name} — výnos`, delta);
        } else if (delta < 0) {
          get().recordFinancial(holder.id, 'EXPENSE', 'bankFees', `${INVESTMENT_TIER_META[entry.tier].name} — ztráta fondu`, -delta);
        }
      }
      return { ...entry, value, lastYieldRate: rate };
    });
    set({ investments: next });
  },

  // ---- Cloud saves (Firebase): serialize / restore the complete match ----
  hasActiveMatch: () => {
    const { gameStarted, players, market } = get();
    return gameStarted && players.length > 0 && market.currentRound > 0;
  },

  snapshotForCloud: () => {
    const s = get();
    return {
      schema: 'equity-tycoon/game',
      version: CLOUD_SNAPSHOT_VERSION,
      savedAt: Date.now(),
      gameStarted: s.gameStarted,
      lobby: s.lobby,
      victory: s.victory,
      board: s.board,
      players: s.players,
      market: s.market,
      currentPlayerIndex: s.currentPlayerIndex,
      turnPhase: s.turnPhase,
      events: s.events,
      loans: s.loans,
      investments: s.investments,
      auction: { ...s.auction, thinkingBidderId: null },
      pendingOffer: s.pendingOffer,
      emergency: s.emergency,
      proposal: s.proposal,
      takeoverTileId: s.takeoverTileId,
      lastTakeoverRound: s.lastTakeoverRound,
      expansionPending: s.expansionPending,
      gameOver: s.gameOver,
      winnerId: s.winnerId,
      metroRiddenThisTurn: s.metroRiddenThisTurn,
      selectedTileIndex: s.selectedTileIndex,
      movementPoints: s.movementPoints,
      pushLuckUsed: s.pushLuckUsed,
      crashRiskArmed: s.crashRiskArmed,
      idlePenalty: s.idlePenalty,
      reachableIndices: s.reachableIndices,
    };
  },

  hydrateFromCloud: (snapshot) => {
    const restarted = !snapshot.gameStarted || snapshot.players.length === 0;
    set({
      board: rebalanceDistricts(snapshot.board.map((tile) => tile)),
      players: snapshot.players,
      market: snapshot.market,
      currentPlayerIndex: Math.max(0, Math.min(snapshot.currentPlayerIndex, snapshot.players.length - 1)),
      turnPhase: snapshot.turnPhase === 'ATTENTION_INTERCEPT' || snapshot.turnPhase === 'TRADE_NEGOTIATION' ? 'TURN_END' : snapshot.turnPhase,
      events: snapshot.events,
      loans: Array.isArray(snapshot.loans) ? snapshot.loans : [],
      investments: Array.isArray(snapshot.investments) ? snapshot.investments : [],
      auction: { ...emptyAuction },
      pendingOffer: snapshot.pendingOffer,
      emergency: null,
      proposal: snapshot.proposal,
      takeoverTileId: snapshot.takeoverTileId,
      lastTakeoverRound: snapshot.lastTakeoverRound,
      expansionPending: snapshot.expansionPending,
      gameOver: snapshot.gameOver,
      winnerId: snapshot.winnerId,
      lobby: snapshot.lobby,
      victory: snapshot.victory,
      gameStarted: snapshot.gameStarted,
      metroRiddenThisTurn: snapshot.metroRiddenThisTurn,
      movementPoints: snapshot.movementPoints,
      pushLuckUsed: snapshot.pushLuckUsed,
      crashRiskArmed: snapshot.crashRiskArmed,
      idlePenalty: snapshot.idlePenalty,
      reachableIndices: snapshot.reachableIndices,
      selectedTileIndex: null,
      inspectTileIndex: null,
      guideTileId: null,
      financialDeskOpen: false,
      resumePhase: null,
      attention: null,
      tradeOffer: null,
      insolvency: null,
      handover: null,
      hotseatReview: null,
      botStatus: null,
      // Fresh epoch remounts the board so the restored tile positions animate from scratch.
      epoch: get().epoch + 1,
    });
    if (!restarted) {
      get().logEvent(
        tr('cloud.restored', {
          round: snapshot.market.currentRound,
          phase: snapshot.market.phase,
          players: snapshot.players.filter((p) => !p.isBankrupt).length,
        }),
        'INFO',
      );
    }
  },

  transferCash: (fromId, toId, amount) => {
    const { players } = get();
    set({ players: withCash(withCash(players, fromId, -amount), toId, amount) });
  },

  collectDividend: (playerId, amount) => set({ players: withCash(get().players, playerId, amount) }),

  addDebt: (playerId, amount) => set({ players: get().players.map((p) => (p.id === playerId ? { ...p, debt: p.debt + amount } : p)) }),

  borrow: (playerId, amount) => {
    const ceiling = get().debtCeilingOf(playerId);
    const { players } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player) return;
    const granted = Math.max(0, Math.min(amount, ceiling - player.debt));
    if (granted <= 0) {
      get().logEvent(`${player.name} is at the debt ceiling and cannot borrow.`, 'RISK');
      return;
    }
    set({ players: withCash(get().players, playerId, granted).map((p) => (p.id === playerId ? { ...p, debt: p.debt + granted } : p)) });
    get().logEvent(`${player.name} borrowed $${granted.toLocaleString()} against their portfolio.`, 'INFO');
  },

  /** In-auction emergency borrowing — only mutates the player's cash/debt, so the
   *  auction object, its turn order and its countdown are never reset. */
  takeLoan: (playerId, amount) => {
    const { auction, players } = get();
    if (!auction.isOpen) return;
    const player = players.find((p) => p.id === playerId);
    if (!player || player.isBankrupt) return;
    const ceiling = get().debtCeilingOf(playerId);
    const granted = Math.max(0, Math.min(amount, ceiling - player.debt));
    if (granted <= 0) {
      get().logEvent(`${player.name} has exhausted the credit line and cannot borrow.`, 'RISK');
      return;
    }
    set({ players: get().players.map((p) => (p.id === playerId ? { ...p, cash: p.cash + granted, debt: p.debt + granted } : p)) });
    get().logEvent(`${player.name} drew a $${granted.toLocaleString()} credit line to fund the auction bid!`, 'RISK');
  },

  nextTurn: () => {
    const { players, currentPlayerIndex } = get();
    if (players.length === 0) return;

    const alive = players.filter((p) => !p.isBankrupt);
    if (alive.length <= 1) {
      set({ gameOver: true, winnerId: alive[0]?.id ?? null, turnPhase: 'TURN_END' });
      get().logEvent(`Game over — ${alive[0]?.name ?? 'nobody'} is the last tycoon standing.`, 'INFO');
      return;
    }

    let idx = (currentPlayerIndex + 1) % players.length;
    for (let step = 0; step < players.length; step += 1) {
      const candidate = (currentPlayerIndex + 1 + step) % players.length;
      if (!players[candidate].isBankrupt) {
        idx = candidate;
        break;
      }
    }

    const nextPlayer = players[idx];
    // Hotseat pass-the-device: freeze before a human's turn whenever the previous
    // player was a bot (miss-events summary) or another human follows (≥2 humans).
    const humanCount = players.filter((p) => !p.isAI && !p.isBankrupt).length;
    const prevPlayer = players[currentPlayerIndex];
    const wantsHandover =
      Boolean(nextPlayer) && !nextPlayer.isAI && !get().gameOver && (humanCount > 1 || prevPlayer.isAI);
    const handoverSummary = wantsHandover ? get().events.slice(-6).map((e) => e.message).reverse() : [];
    set({
      currentPlayerIndex: idx,
      turnPhase: wantsHandover ? 'HANDOVER' : 'TURN_START',
      movementPoints: 0,
      selectedTileIndex: null,
      pushLuckUsed: false,
      crashRiskArmed: false,
      idlePenalty: 0,
      reachableIndices: [],
      proposal: { ...emptyProposal },
      resumePhase: null,
      // Pass-the-device payload: what the waiting human missed while bots played.
      handover: wantsHandover ? { playerId: nextPlayer.id, summary: handoverSummary } : null,
      // Phase 1 broadcast: the bot announces itself instead of skipping silently.
      botStatus: nextPlayer?.isAI
        ? { playerId: nextPlayer.id, phase: 'ROLL', text: `${nextPlayer.name} rolling movement…` }
        : null,
    });

    if (idx === 0) get().updateMarketCycle();
  },

  rollMovementPoints: (max) => {
    const { players, currentPlayerIndex, board } = get();
    const player = players[currentPlayerIndex];
    if (!player) return;

    const rolled = 1 + Math.floor(Math.random() * Math.max(1, max));
    const reachable: number[] = [];
    for (let step = 1; step <= rolled; step += 1) {
      reachable.push((player.currentTileIndex + step) % board.length);
    }

    set({
      movementPoints: rolled,
      reachableIndices: reachable,
      turnPhase: 'MOVEMENT_PREVIEW',
      selectedTileIndex: null,
      idlePenalty: 0,
      crashRiskArmed: false,
      pushLuckUsed: false,
      // Phase 2 broadcast: the actual die result is shown, never a silent skip.
      botStatus: player.isAI
        ? { playerId: player.id, phase: 'ROLLED', text: `${player.name} rolling movement… (Rolled ${rolled})` }
        : null,
    });
    get().logEvent(`${player.name} rolled ${rolled} movement points.`, 'INFO');
  },

  selectStopTile: (tileIndex) => {
    const { players, currentPlayerIndex, board, movementPoints } = get();
    const player = players[currentPlayerIndex];
    if (!player) return;

    const distance = (tileIndex - player.currentTileIndex + board.length) % board.length;
    // Only tiles inside the rolled movement budget may be chosen
    if (movementPoints <= 0 || distance === 0 || distance > movementPoints) return;

    const unused = Math.max(0, movementPoints - distance);
    set({
      selectedTileIndex: tileIndex,
      idlePenalty: unused * IDLE_PENALTY_PER_STEP,
      turnPhase: 'PUSH_LUCK_DECISION',
      // Phase 3 broadcast: the chosen destination is named.
      botStatus: player.isAI
        ? { playerId: player.id, phase: 'MOVE', text: `${player.name} moving to ${board[tileIndex].name}…` }
        : null,
    });
  },

  confirmStop: () => {
    get().resolveTile();
  },

  previewTileImpact: (tileIndex) => {
    const { players, currentPlayerIndex, board, market } = get();
    const player = players[currentPlayerIndex];
    if (!player) return { distance: 0, impact: 0, label: 'No active player', safeChance: 100 };

    const distance = (tileIndex - player.currentTileIndex + board.length) % board.length;
    const tile = board[tileIndex];
    const rivalMajority = Object.entries(tile.shares).some(
      ([ownerId, shares]) => ownerId !== player.id && shares / Math.max(1, tile.totalShares) > 0.5,
    );

    let impact = 0;
    let label = '';

    switch (tile.tileType) {
      case 'COMMERCIAL': {
        const owners = Object.keys(tile.shares);
        if (owners.length === 0) {
          label = 'Unowned plot — IPO auction opens';
        } else {
          const rent = rentFor(tile, market.phase);
          const ownPct = ownershipPct(tile, player.id);
          const due = Math.round((rent * (100 - ownPct)) / 100);
          impact = -due;
          label = `Rent exposure −$${due.toLocaleString()}`;
        }
        break;
      }
      case 'TRANSIT':
        impact = 60;
        label = 'Transit dividend +$60';
        break;
      case 'FINANCIAL':
        label = 'Financial center — borrow or float shares';
        break;
      case 'SPECIAL':
        if (tile.name.includes('Audit')) {
          const tax = Math.round(get().netWorthOf(player.id) * 0.1);
          impact = -tax;
          label = `Progressive asset tax −$${tax.toLocaleString()}`;
        } else if (tile.name.includes('Payroll')) {
          impact = START_PASS_PAYROLL;
          label = `Payroll +$${START_PASS_PAYROLL.toLocaleString()}`;
        } else {
          label = 'Special tile';
        }
        break;
      default:
        label = 'No effect';
    }

    const safeChance = rivalMajority ? 32 : 68;
    return { distance, impact, label, safeChance };
  },

  /** 🛟 Bot watchdog: force-resolve a stalled bot turn. Clears any lingering gate,
   *  desk or pending settlement, lands the FSM on TURN_END and advances the turn
   *  so the game can never deadlock on a bot. */
  botForceAdvance: () => {
    const state = get();
    const player = state.players[state.currentPlayerIndex];
    if (!player || !player.isAI || state.gameOver) return;
    get().logEvent(`🛟 Watchdog: ${player.name} stalled in ${state.turnPhase} — auto-resolving and advancing.`, 'RISK');
    set({
      pendingOffer: null,
      attention: null,
      tradeOffer: null,
      insolvency: null,
      handover: null,
      takeoverTileId: null,
      auction: { ...emptyAuction },
      proposal: state.proposal.status === 'PENDING' ? { ...emptyProposal } : state.proposal,
      selectedTileIndex: null,
      movementPoints: 0,
      reachableIndices: [],
      resumePhase: null,
      turnPhase: 'TURN_END',
      botStatus: { playerId: player.id, phase: 'GOVERN', text: `${player.name}'s stall was auto-resolved — advancing.` },
    });
    get().finishTurn();
  },

  finishTurn: () => {
    const { players, currentPlayerIndex, market } = get();
    const player = players[currentPlayerIndex];
    if (!player) return;

    // Insurance countdown ticks down each completed turn; metro flag resets.
    if (player.insuranceTurnsRemaining > 0) {
      set({
        players: get().players.map((p) => {
          if (p.id !== player.id) return p;
          const remaining = Math.max(0, p.insuranceTurnsRemaining - 1);
          return remaining === 0
            ? { ...p, insuranceTurnsRemaining: 0, insuranceCoverageRate: 0, insurancePolicy: null }
            : { ...p, insuranceTurnsRemaining: remaining };
        }),
      });
      if (player.insuranceTurnsRemaining === 1) get().logEvent('Rent insurance policy expired.', 'INFO');
    }
    if (get().metroRiddenThisTurn) set({ metroRiddenThisTurn: false });

    if (player.debt > 0) {
      const interest = Math.round(player.debt * market.interestRate);
      set({ players: withCash(get().players, player.id, -interest) });
      get().logEvent(`${player.name} paid $${interest.toLocaleString()} in debt interest.`, 'LOSS');
    }

    get().runMarginCalls();
    get().checkBankruptcies();
    get().nextTurn();
  },

  resolveTile: () => {
    const state = get();
    const { players, currentPlayerIndex, board, market, selectedTileIndex } = state;
    const player = players[currentPlayerIndex];
    if (!player || selectedTileIndex === null) return;

    const start = player.currentTileIndex;
    const target = selectedTileIndex % board.length;
    const distance = (target - start + board.length) % board.length;
    const tile = board[target];
    const passedStart = distance > 0 && start + distance >= board.length;

    const unused = Math.max(0, state.movementPoints - distance);
    const penalty = unused * IDLE_PENALTY_PER_STEP;
    const milestone = state.pushLuckUsed ? 2 : 1;

    let cashDelta = -penalty;
    const parts: string[] = [`${player.name} advanced ${distance} tile(s) to ${tile.name}.`];
    if (penalty > 0) {
      parts.push(`Idle penalty −$${penalty.toLocaleString()}.`);
      get().recordFinancial(player.id, 'EXPENSE', 'bankFees', 'Pokuta za nevyužité body pohybu', penalty);
    }

    // Phase 4 broadcast: the play-by-play bar reports concrete money movement.
    let settleText = `moved to ${tile.name}`;
    const settle = (text: string): Partial<GameStore> =>
      player.isAI
        ? { botStatus: { playerId: player.id, phase: 'SETTLE' as const, text: `${player.name} ${text}.` } }
        : { botStatus: null };

    if (passedStart) {
      const phaseScale = market.phase === 'BOOM' ? 1.25 : market.phase === 'CRISIS' ? 0.75 : 1;
      const payroll = Math.round(START_PASS_PAYROLL * phaseScale) * milestone;
      cashDelta += payroll;
      parts.push(`Payroll +$${payroll.toLocaleString()}${milestone === 2 ? ' (milestone doubled)' : ''}.`);
    }

    let nextPlayers = players.map((p) => (p.id === player.id ? { ...p, currentTileIndex: target } : p));

    if (tile.tileType === 'COMMERCIAL') {
      const owners = Object.keys(tile.shares);
      if (owners.length === 0) {
        const tranche = Math.min(25, floatSharesOf(tile));
        parts.push('Unowned plot — choose: buy directly, auction, or pass.');
        settleText = `landed on unowned ${tile.name} — ${tranche}-share IPO tranche offered`;
        set({ pendingOffer: { tileId: tile.id, tranche }, ...settle(settleText) });
      } else {
        const base = rentFor(tile, market.phase);
        const rent = Math.round(base * (state.crashRiskArmed ? CRASH_RENT_MULTIPLIER : 1));
        if (state.crashRiskArmed) parts.push(`Crash rent triggered (x${CRASH_RENT_MULTIPLIER}).`);
        // Coverage share of the ACTIVE policy (legacy saves default to the classic 70 %).
        const coverageRate = player.insuranceTurnsRemaining > 0 ? player.insuranceCoverageRate ?? 0.7 : 0;
        const insured = coverageRate > 0;

        // Pro-rata creditor split at fair live value; the active policy covers its share.
        const creditors = owners
          .map((ownerId) => ({ ownerId, share: sharesOf(tile, ownerId) }))
          .filter((c) => c.ownerId !== player.id && c.share > 0)
          .map((c) => ({
            playerId: c.ownerId,
            playerName: players.find((p) => p.id === c.ownerId)?.name ?? c.ownerId,
            amount: Math.round((rent * c.share) / Math.max(1, tile.totalShares)),
          }))
          .filter((c) => c.amount > 0);
        const rawDue = creditors.reduce((s, c) => s + c.amount, 0);
        const coverage = insured ? Math.min(rawDue, Math.round(rawDue * coverageRate)) : 0;
        const totalDue = rawDue - coverage;
        if (coverage > 0) parts.push(`Insurance covered ${money(coverage)} (${Math.round(coverageRate * 100)}%).`);

        if (totalDue <= 0) {
          parts.push('No rent due — you control this cap table outright.');
          settleText = `holds ${tile.name} outright — no rent due`;
        } else if (player.cash >= totalDue) {
          let paid = 0;
          const creditEntries = creditors
            .map((c) => {
              const full = c.amount;
              const due = insured ? Math.max(0, Math.round(full * (1 - coverageRate))) : full;
              return { playerId: c.playerId, playerName: c.playerName, amount: due };
            })
            .filter((c) => c.amount > 0);
          nextPlayers = nextPlayers.map((p) => {
            if (p.id === player.id) return p;
            const due = creditEntries.find((c) => c.playerId === p.id)?.amount ?? 0;
            if (due <= 0) return p;
            paid += due;
            return { ...p, cash: p.cash + due };
          });
          nextPlayers = nextPlayers.map((p) => (p.id === player.id ? { ...p, cash: p.cash - paid } : p));
          cashDelta -= paid;
          get().recordFinancial(player.id, 'EXPENSE', 'rentPaid', `Nájem ${tile.name}`, paid);
          creditEntries.forEach((credit) =>
            get().recordFinancial(credit.playerId, 'INCOME', 'rentCollected', `Nájem ${tile.name} (od ${player.name})`, credit.amount),
          );
          const seq = get().rentSeq + 1;
          set({
            rentSeq: seq,
            lastRentCredit: {
              tileId: tile.id,
              tileIndex: target,
              payerId: player.id,
              payerName: player.name,
              total: paid,
              credits: creditEntries,
              seq,
            },
          });
          parts.push(`Rent settled −$${paid.toLocaleString()} distributed pro-rata to ${creditors.length} shareholder(s).`);
          settleText = `paid ${money(paid)} rent on ${tile.name} to ${creditors.length} shareholder(s)`;
        } else {
          // Liquidity squeeze — pause and open the emergency ladder instead of instant ruin.
          nextPlayers = nextPlayers.map((p) => (p.id === player.id ? { ...p, currentTileIndex: target } : p));
          set({
            players: nextPlayers,
            selectedTileIndex: target,
            movementPoints: state.movementPoints,
            reachableIndices: [],
            crashRiskArmed: false,
            idlePenalty: 0,
            ...settle(`is short of rent at ${tile.name} — emergency settlement`),
          });
          get().logEvent(`${player.name} landed on ${tile.name} owing $${totalDue.toLocaleString()} with $${player.cash.toLocaleString()} cash.`, 'RISK');
          get().raiseEmergency(player.id, totalDue, creditors, `Rent on ${tile.name}`, tile.id);
          if (get().emergency) return; // human modal open — loop paused until resolution
          parts.push(`Rent settled −$${totalDue.toLocaleString()} via emergency ladder.`);
          cashDelta -= totalDue;
        }
      }
    } else if (tile.tileType === 'TRANSIT') {
      const dividend = 60;
      let distributed = 0;
      nextPlayers = nextPlayers.map((p) => {
        const share = sharesOf(tile, p.id);
        if (share <= 0) return p;
        const payout = Math.round((dividend * share) / Math.max(1, tile.totalShares));
        distributed += payout;
        return { ...p, cash: p.cash + payout };
      });
      nextPlayers = nextPlayers.map((p) => (p.id === player.id ? { ...p, cash: p.cash + (dividend - distributed) } : p));
      cashDelta += dividend;
      parts.push(`Transit dividend +$${dividend}.`);
      settleText = `collected ${money(dividend)} transit dividend`;
      nextPlayers
        .filter((p) => sharesOf(tile, p.id) > 0)
        .forEach((holder) => {
          const payout = Math.round((dividend * sharesOf(tile, holder.id)) / Math.max(1, tile.totalShares));
          if (payout > 0) get().recordFinancial(holder.id, 'INCOME', 'transitDividends', `Transit dividenda ${tile.name}`, payout);
        });

      // Metro fast-travel prompt (skipped after a ride this turn).
      if (!state.metroRiddenThisTurn) {
        set({
          players: nextPlayers,
          fastTravelFrom: target,
          turnPhase: 'FAST_TRAVEL_SELECTION',
          selectedTileIndex: null,
          movementPoints: 0,
          reachableIndices: [],
          crashRiskArmed: false,
          idlePenalty: 0,
          ...settle(settleText),
        });
        get().logEvent(parts.join(' '), cashDelta >= 0 ? 'GAIN' : 'LOSS');
        return;
      }
    } else if (tile.tileType === 'FINANCIAL') {
      parts.push('Financial center reached — governance desk open.');
      settleText = 'reached the financial desk — governance open';
      set({ turnPhase: 'GOVERNANCE_PHASE', ...settle(settleText) });
    } else if (tile.name.includes('Audit')) {
      // City Hall: lobbying actions replace the flat audit tax.
      parts.push('City Hall reached — lobbying unlocked.');
      settleText = 'is at City Hall — lobbying unlocked';
      set({
        players: nextPlayers,
        turnPhase: 'CITY_HALL_LOBBY',
        selectedTileIndex: null,
        movementPoints: 0,
        reachableIndices: [],
        crashRiskArmed: false,
        idlePenalty: 0,
        ...settle(settleText),
      });
      get().logEvent(parts.join(' '), 'INFO');
      return;
    } else if (tile.name.includes('Payroll')) {
      nextPlayers = nextPlayers.map((p) => (p.id === player.id ? { ...p, cash: p.cash + START_PASS_PAYROLL } : p));
      cashDelta += START_PASS_PAYROLL;
      parts.push(`Payroll +$${START_PASS_PAYROLL.toLocaleString()}.`);
      settleText = `collected ${money(START_PASS_PAYROLL)} payroll`;
    }

    set({
      players: nextPlayers,
      turnPhase: 'TURN_END',
      selectedTileIndex: null,
      movementPoints: 0,
      reachableIndices: [],
      crashRiskArmed: false,
      idlePenalty: 0,
      ...settle(settleText),
    });

    get().logEvent(parts.join(' '), cashDelta >= 0 ? 'GAIN' : 'LOSS');
    get().runMarginCalls();
    get().checkBankruptcies();
  },

  pushYourLuck: () => {
    const state = get();
    const { players, currentPlayerIndex, board } = state;
    const player = players[currentPlayerIndex];
    if (!player || state.pushLuckUsed) return;

    const extra = 1 + Math.floor(Math.random() * 6);
    const path: number[] = [];
    for (let step = 1; step <= extra; step += 1) {
      path.push((player.currentTileIndex + step) % board.length);
    }

    const risky = path.some((idx) => {
      const t = board[idx];
      return (
        t.tileType === 'COMMERCIAL' &&
        Object.entries(t.shares).some(
          ([ownerId, shares]) => ownerId !== player.id && shares / Math.max(1, t.totalShares) > 0.5,
        )
      );
    });

    set({
      movementPoints: state.movementPoints + extra,
      selectedTileIndex: (player.currentTileIndex + extra) % board.length,
      pushLuckUsed: true,
      crashRiskArmed: risky,
      turnPhase: 'TILE_RESOLUTION',
    });

    get().logEvent(
      risky
        ? `Push your luck: ${extra} extra steps into rival territory — crash rent armed (2x).`
        : `Push your luck: ${extra} extra steps, path clear. Payroll milestone doubled.`,
      risky ? 'RISK' : 'GAIN',
    );

    get().resolveTile();
  },

  updateMarketCycle: () => {
    const { market, board } = get();
    // Endless mode: rounds no longer cap out at maxRounds.
    const nextRound = market.currentRound + 1;
    const shift = (nextRound - 1) % 5 === 0;
    let phase = market.phase;
    if (shift) {
      const options: MarketPhase[] = ['BOOM', 'STAGNATION', 'CRISIS'];
      phase = options[Math.floor(Math.random() * options.length)];
    }
    const mults = phaseMultipliers(phase);
    // Zoning freezes last exactly one round.
    const unfrozen = board.map((tile) => (tile.isFrozen ? { ...tile, isFrozen: false } : tile));

    // Black Swan roll: 35% chance on every phase shift.
    let blackSwan: BlackSwanState = { event: null, title: '', description: '' };
    let afterSwan = unfrozen;
    let interestRate = mults.interest;
    if (shift && Math.random() < 0.35) {
      const roll = ['TECH_RALLY', 'CREDIT_CRUNCH', 'INFRASTRUCTURE_GRANT'][Math.floor(Math.random() * 3)] as BlackSwanEvent;
      if (roll === 'TECH_RALLY') {
        afterSwan = unfrozen.map((tile) => (tile.district === 'DISTRICT_C' ? { ...tile, valuation: Math.round(valuationLiveOf(tile) * 1.4) } : tile));
        blackSwan = { event: roll, title: '🦢 BLACK SWAN: TECH RALLY', description: 'Innovation Valley valuations surged +40% overnight!' };
      } else if (roll === 'CREDIT_CRUNCH') {
        interestRate = mults.interest * 2;
        blackSwan = { event: roll, title: '🦢 BLACK SWAN: CREDIT CRUNCH', description: 'Interest rates doubled — debt ceilings dropped 20%.' };
      } else {
        afterSwan = unfrozen.map((tile) => (tile.tileType === 'TRANSIT' ? { ...tile, upgradeLevel: Math.max(1, tile.upgradeLevel) } : tile));
        blackSwan = { event: roll, title: '🦢 BLACK SWAN: INFRASTRUCTURE GRANT', description: 'All Transit Hubs received a free upgrade to Level 1!' };
      }
    }

    // ROUND_END valuation engine: secular drift + noise + cycle modifier.
    // Rent yields drift independently inside the 10 %–25 % corridor, so two tiles of
    // the same tier can pay very different rent — base rent is then recomputed from
    // the fresh valuation × live yield × tier uplift.
    const drifted = afterSwan.map((tile) => {
      if (tile.tileType !== 'COMMERCIAL') return tile;
      const anchor = valuationLiveOf(tile);
      const { next, deltaPct } = driftValuation(anchor, phase);
      const rentYieldRate = driftRentYield(tile.rentYieldRate);
      const repriced: PropertyTile = {
        ...tile,
        valuation: next,
        valuationDeltaPct: Math.round(deltaPct * 10) / 10,
        rentYieldRate,
      };
      return { ...repriced, rent: Math.round(next * rentYieldRate * (1 + repriced.upgradeLevel * 0.4)) };
    });
    set({
      board: drifted,
      market: {
        ...market,
        currentRound: nextRound,
        phase,
        rentMultiplier: mults.rent,
        assetValuationMultiplier: mults.valuation,
        interestRate,
      },
      blackSwan,
    });

    const avgYield = drifted.length
      ? drifted.filter((t) => t.tileType === 'COMMERCIAL').reduce((s, t) => s + rentYieldOf(t), 0) /
        Math.max(1, drifted.filter((t) => t.tileType === 'COMMERCIAL').length)
      : 0;

    const up = drifted.filter((t) => t.tileType === 'COMMERCIAL' && t.valuationDeltaPct > 0).length;
    const down = drifted.filter((t) => t.tileType === 'COMMERCIAL' && t.valuationDeltaPct < 0).length;
    get().logEvent(`Repricing R${nextRound}: ${up} up / ${down} down (drift + noise, ${phase}).`, 'INFO');
    get().logEvent(
      `Rent yields re-priced: board average ${(avgYield * 100).toFixed(1)} % (corridor 10–25 %).`,
      'INFO',
    );

    // Round-end corporate finance: loan tranches are serviced and funds mark to market.
    get().processLoans();
    get().processInvestments();

    if (shift) {
      get().logEvent(
        `Macro shift → ${phase}. Rents x${mults.rent}, valuations x${mults.valuation}, interest ${(mults.interest * 100).toFixed(0)}%.`,
        'RISK',
      );
      get().triggerExpansion();
    }
  },

  setGameLength: (maxRounds) => {
    const safe = Math.max(0, Math.floor(maxRounds));
    set({ market: { ...get().market, maxRounds: safe } });
    get().logEvent(
      safe <= 0
        ? 'Game length set to Endless / target $100,000 — no round cap.'
        : `Game length set to Classic ${safe} rounds — highest net worth wins at settlement.`,
      'INFO',
    );
  },

  checkExpansion: () => {
    const { market, expansionPending } = get();
    if (market.currentRound % 5 === 0 && !expansionPending) get().triggerExpansion();
  },

  triggerExpansion: () => {
    const { board, market } = get();
    const round = market.currentRound;
    const pool: TileSeed[] = [
      [`Suburb Terraces ${round}`, 'DISTRICT_A', 'COMMERCIAL', 400 + round * 40, 20 + round],
      [`Industrial Park ${round}`, 'DISTRICT_B', 'COMMERCIAL', 700 + round * 50, 45 + round * 2],
      [`Tech Hub Annex ${round}`, 'DISTRICT_C', 'COMMERCIAL', 1000 + round * 60, 80 + round * 3],
      [`Skyline Annex ${round}`, 'DISTRICT_D', 'COMMERCIAL', 1400 + round * 80, 140 + round * 4],
    ];
    // ---- STRICT DISTRICT CAP: a parcel may ONLY spawn in a district that currently
    // holds fewer than MAX_DISTRICT_TILES (5) properties. The seed's preferred district
    // is used when it still has room; otherwise the annex is re-zoned into the first
    // open cluster (Expansion Belt / Outer Rim) — it is NEVER forced into a full group.
    const openGroups = DISTRICT_GROUPS.filter((district) => getDistrictTiles(board, district).length < MAX_DISTRICT_TILES);
    if (openGroups.length === 0) {
      set({ expansionPending: false });
      get().logEvent('Urban Expansion: zelené parcely nemají kam přidat — každý distrikt je plný (5/5).', 'INFO');
      return;
    }
    const picks: TileSeed[] = [pool[(round + 1) % pool.length], pool[(round + 2) % pool.length]].map((seed) => {
      const preferred: District = seed[1];
      const target = getDistrictTiles(board, preferred).length < MAX_DISTRICT_TILES ? preferred : openGroups[0];
      return [seed[0], target, seed[2], seed[3], seed[4]] as TileSeed;
    });

    // The 3–5 balancing corridor caps the board at 6 clusters × 5 properties; once
    // every colour group is full the city stops issuing new parcels.
    if (!canAnnexCommercialTiles(board, picks.length)) {
      set({ expansionPending: false });
      get().logEvent('Urban Expansion: no zoned parcels left — every colour group is balanced at 5 properties.', 'INFO');
      return;
    }

    const newTiles: PropertyTile[] = picks.map((seed) => {
      const [name, district, tileType, baseCost, baseRent] = seed;
      const rentYieldRate = DEFAULT_RENT_YIELD;
      expansionSeq += 1;
      return {
        id: `expansion-${round}-${expansionSeq}`,
        name,
        district,
        tileType,
        baseCost,
        baseRent,
        upgradeLevel: 0,
        totalShares: BASE_SHARES,
        shares: {},
        upgradeCost: Math.round(baseCost * 0.5),
        valuation: baseCost,
        valuationDeltaPct: 0,
        isFrozen: false,
        isExpansionTile: true,
        rentYieldRate,
        rent: Math.round(baseCost * rentYieldRate),
      };
    });

    // Re-balance after annexation so no colour group ever leaves the 3–5 corridor.
    // Pure APPEND ([...board, ...newTiles]) — an existing tile is NEVER overwritten,
    // never mutated in place and keeps its array index (pawn positions stay valid).
    const nextBoard = rebalanceDistricts([...board, ...newTiles]);
    set({ board: nextBoard, expansionPending: false });
    // 🏗️ New Property Spotlight: announce every freshly zoned parcel with full details.
    newTiles.forEach((tile) => get().setNewPropertyAlert(tile));
    get().logEvent(`Urban Expansion: ${newTiles.map((t) => t.name).join(' and ')} annexed. IPO tranches open.`, 'EXPANSION');
    const audit = auditDistrictBalance(nextBoard)
      .map((entry) => `${entry.district.replace('DISTRICT_', '')} ${entry.total}/5`)
      .join(' · ');
    get().logEvent(`Colour groups re-balanced → ${audit}${auditDistrictBalance(nextBoard).every((e) => e.balanced) ? ' ✓' : ' ⚠'}.`, 'INFO');
    get().openAuction(newTiles[0].id, 25);
  },

  offerShares: (propertyId, tranche) => {
    set({ pendingOffer: { tileId: propertyId, tranche } });
  },

  clearOffer: () => set({ pendingOffer: null }),

  openAuction: (propertyId, tranche) => {
    const { board, players, currentPlayerIndex } = get();
    const tile = board.find((t) => t.id === propertyId);
    if (!tile) return;
    // Symbolic starting bid: auctions open at a token $50 (not fair market value) so
    // bidders fight over the real price and bargains are possible.
    const opening = 50;
    // Hotseat sequential bidding: seating order starting from the current player.
    const ordered = [...players.slice(currentPlayerIndex), ...players.slice(0, currentPlayerIndex)];
    const turnOrder = ordered.filter((p) => !p.isBankrupt).map((p) => p.id);
    set({
      pendingOffer: null,
      auction: {
        propertyId,
        shareTranche: tranche,
        currentBid: opening,
        highestBidderId: null,
        timeRemaining: 10,
        activePlayerIds: players.filter((p) => !p.isBankrupt).map((p) => p.id),
        isOpen: true,
        bidHistory: [],
        thinkingBidderId: null,
        humanFolded: false,
        turnOrder,
        turnIndex: 0,
        foldedPlayerIds: [],
      },
      turnPhase: 'TILE_RESOLUTION',
    });
    get().logEvent(`Public auction opened for ${tranche} shares of ${tile.name} at $${opening.toLocaleString()} — ${players.find((p) => p.id === turnOrder[0])?.name ?? 'first bidder'} bids first.`, 'INFO');
  },

  foldHumanAuction: () => {
    const { auction, players } = get();
    if (!auction.isOpen) return;
    const current = players.find((p) => p.id === auction.turnOrder[auction.turnIndex]);
    if (!current || current.isAI) return;
    get().foldAuction(current.id);
  },

  /** Hotseat turn-based auction: the player whose slot it is passes. */
  foldAuction: (playerId) => {
    const { auction, players, board } = get();
    if (!auction.isOpen) return;
    if (auction.turnOrder[auction.turnIndex] !== playerId) return;
    const player = players.find((p) => p.id === playerId);
    if (!player) return;
    const tile = board.find((t) => t.id === auction.propertyId);
    set({
      auction: {
        ...auction,
        foldedPlayerIds: [...new Set([...auction.foldedPlayerIds, playerId])],
        humanFolded: auction.humanFolded || !player.isAI,
        thinkingBidderId: null,
      },
    });
    get().logEvent(`${player.name} folded${tile ? ` on ${tile.name}` : ''} — next bidder's call.`, 'INFO');
    get().advanceAuctionTurn();
  },

  /** Hand the bid to the next eligible bidder; end when nobody can challenge the leader. */
  advanceAuctionTurn: () => {
    const { auction, players } = get();
    if (!auction.isOpen) return;
    const eligible = (id: string) => {
      const p = players.find((q) => q.id === id);
      return Boolean(p) && !p!.isBankrupt && !auction.foldedPlayerIds.includes(id) && id !== auction.highestBidderId;
    };
    const candidates = auction.turnOrder.filter(eligible);
    if (candidates.length === 0) {
      get().endAuction();
      return;
    }
    let nextIdx = auction.turnIndex;
    for (let step = 1; step <= auction.turnOrder.length; step += 1) {
      const probe = (auction.turnIndex + step) % auction.turnOrder.length;
      if (eligible(auction.turnOrder[probe])) {
        nextIdx = probe;
        break;
      }
    }
    set({ auction: { ...auction, turnIndex: nextIdx, thinkingBidderId: null, timeRemaining: 10 } });
  },

  placeBid: (playerId, amount) => {
    const { auction, players, board } = get();
    if (!auction.isOpen) return;
    const bidder = players.find((p) => p.id === playerId);
    const tile = board.find((t) => t.id === auction.propertyId);
    if (!bidder || !tile || bidder.isBankrupt) return;
    if (bidder.id === auction.highestBidderId) return;

    const minimum = auction.highestBidderId === null ? auction.currentBid : auction.currentBid + 50;
    if (amount < minimum || amount > bidder.cash) return;

    const history = [...auction.bidHistory, { bidderId: playerId, bidderName: bidder.name, amount, round: get().market.currentRound }].slice(-12);
    // Anti-snipe: a fresh highest bid resets the clock to at least 3s (cap 10s).
    const timeRemaining = Math.min(10, Math.max(auction.timeRemaining, 3));
    set({ auction: { ...auction, currentBid: amount, highestBidderId: playerId, bidHistory: history, timeRemaining, thinkingBidderId: null } });
    get().logEvent(`${bidder.name} bids $${amount.toLocaleString()} for ${auction.shareTranche} shares of ${tile.name}.`, 'INFO');
    get().advanceAuctionTurn();
  },

  setThinking: (playerId) => {
    const { auction } = get();
    if (!auction.isOpen) return;
    if (auction.thinkingBidderId === playerId) return;
    set({ auction: { ...auction, thinkingBidderId: playerId } });
  },

  tickAuction: () => {
    const { auction, players } = get();
    if (!auction.isOpen) return;
    const currentBidder = players.find((p) => p.id === auction.turnOrder[auction.turnIndex]);
    // Human bidders are never auto-skipped — the clock only pressures bots.
    if (currentBidder && !currentBidder.isAI) return;
    const remaining = auction.timeRemaining - 1;
    if (remaining <= 0) {
      set({ auction: { ...auction, timeRemaining: 0 } });
      if (currentBidder) get().foldAuction(currentBidder.id);
      else get().endAuction();
      return;
    }
    set({ auction: { ...auction, timeRemaining: remaining } });
  },

  endAuction: () => {
    const { auction, board, market } = get();
    const tile = board.find((t) => t.id === auction.propertyId);
    if (tile && auction.highestBidderId) {
      const perShare = auction.currentBid / Math.max(1, auction.shareTranche);
      get().addShares(auction.highestBidderId, auction.propertyId, auction.shareTranche, perShare);
      const winner = get().players.find((p) => p.id === auction.highestBidderId);
      get().logEvent(
        `${winner?.name ?? 'Bidder'} won ${auction.shareTranche} shares of ${tile.name} for $${auction.currentBid.toLocaleString()}.`,
        'GAIN',
      );
    } else if (tile) {
      get().logEvent(`No bids for ${tile.name} — tranche returns to the public float.`, 'INFO');
    }
    set({ auction: { ...emptyAuction }, turnPhase: 'TURN_END' });
    void market;
  },

  proposeUpgrade: (propertyId, proposerId, targetLevel) => {
    const { board, players } = get();
    const tile = board.find((t) => t.id === propertyId);
    if (!tile || tile.isFrozen || targetLevel <= tile.upgradeLevel || targetLevel > 5) return;

    const cost = Math.round(tile.upgradeCost * Math.max(1, targetLevel - tile.upgradeLevel));
    const newShares = (targetLevel - tile.upgradeLevel) * 50;
    const proposer = players.find((p) => p.id === proposerId);
    const voter = players.find((p) => p.id === proposerId);
    const human = players.find((p) => !p.isAI);

    // Table the motion first — a tabled vote is what the gate is announcing.
    set({
      proposal: {
        propertyId,
        proposerId,
        targetUpgradeLevel: targetLevel,
        totalCost: cost,
        newSharesIssued: newShares,
        votesFor: 0,
        votesAgainst: 0,
        votedPlayerIds: [],
        status: 'PENDING',
      },
    });
    get().logEvent(
      `${proposer?.name ?? 'A shareholder'} tabled an upgrade of ${tile.name} to tier ${targetLevel} ($${cost.toLocaleString()}, +${newShares} shares).`,
      'INFO',
    );

    // Attention intercept: a bot tabled a vote on a tile where the human holds equity.
    // Bot ballots are deferred until the human acknowledges, so their vote is not
    // pre-empted while they are still reading the alert.
    if (voter?.isAI && human && (tile.shares[human.id] ?? 0) > 0) {
      set({
        ...suspensionOf(get(), 'ATTENTION_INTERCEPT'),
        attention: {
          kind: 'VOTE',
          title: '⚠️ ATTENTION REQUIRED: Shareholder Vote Tabled',
          body: `${voter.name} tabled an upgrade of ${tile.name} to Tier ${targetLevel} — capital call ${money(cost)}, +${newShares} new shares. You hold ${tile.shares[human.id]} shares and your ballot is weighted by share count.`,
          tileId: tile.id,
          stake: [
            { label: 'Capital call', value: money(cost) },
            { label: 'New shares issued', value: `+${newShares} (dilution)` },
            { label: 'Your shares', value: `${tile.shares[human.id]} of ${tile.totalShares}` },
            { label: 'Your pro-rata bill', value: money(Math.round(cost * ((tile.shares[human.id] ?? 0) / Math.max(1, tile.totalShares)))) },
            { label: 'Current upgrade tier', value: `${tile.upgradeLevel} → ${targetLevel}` },
          ],
        },
      });
      return;
    }

    if (ownershipPct(tile, proposerId) > 50) {
      get().executeUpgrade(propertyId, targetLevel, cost, newShares);
      return;
    }

    get().botVoteOnProposal(propertyId, cost);
  },

  botVoteOnProposal: (tileId, explicitCost) => {
    const { board, players, proposal } = get();
    const tile = board.find((t) => t.id === tileId);
    if (!tile) return;
    const cost = explicitCost ?? proposal.totalCost;
    players
      .filter((p) => p.isAI && !p.isBankrupt && !proposal.votedPlayerIds.includes(p.id))
      .forEach((bot) => {
        const weight = sharesOf(tile, bot.id) / Math.max(1, tile.totalShares);
        const affordable = bot.cash > cost * weight;
        // Archetype reserve doctrine (same table the build decisions use):
        // AGGRESSIVE votes with a $100 floor, CONSERVATIVE needs 3× its bill in reserve.
        const vote: 'FOR' | 'AGAINST' =
          bot.aiArchetype === 'CONSERVATIVE'
            ? botUpgradeVote(bot, tile, cost)
            : bot.cash - cost * weight >= profileOf(bot.aiArchetype).upgrade.voteReserve && affordable
              ? 'FOR'
              : 'AGAINST';
        get().voteOnProposal(bot.id, vote);
      });
  },

  voteOnProposal: (playerId, vote) => {
    const { proposal, board } = get();
    if (proposal.status !== 'PENDING' || proposal.votedPlayerIds.includes(playerId)) return;
    const tile = board.find((t) => t.id === proposal.propertyId);
    if (!tile) return;

    const weight = Math.max(1, sharesOf(tile, playerId));
    const votesFor = proposal.votesFor + (vote === 'FOR' ? weight : 0);
    const votesAgainst = proposal.votesAgainst + (vote === 'AGAINST' ? weight : 0);
    const votedPlayerIds = [...proposal.votedPlayerIds, playerId];
    const threshold = tile.totalShares / 2;
    const decided = votesFor > threshold || votesAgainst > threshold || votesFor + votesAgainst >= tile.totalShares;
    const status: ProposalState['status'] = decided ? (votesFor > votesAgainst ? 'PASSED' : 'REJECTED') : 'PENDING';

    set({ proposal: { ...proposal, votesFor, votesAgainst, votedPlayerIds, status } });

    if (status === 'PASSED') {
      get().executeUpgrade(proposal.propertyId, proposal.targetUpgradeLevel, proposal.totalCost, proposal.newSharesIssued);
    } else if (status === 'REJECTED') {
      get().logEvent(`Shareholders rejected the ${tile.name} upgrade (${votesFor} for / ${votesAgainst} against).`, 'INFO');
      set({ proposal: { ...emptyProposal }, turnPhase: 'TURN_END' });
    }
  },

  executeUpgrade: (propertyId, targetLevel, cost, newShares) => {
    const { board, players } = get();
    const index = board.findIndex((t) => t.id === propertyId);
    if (index === -1) return;
    const tile = board[index];
    const holders = Object.entries(tile.shares).filter(([, owned]) => owned > 0);
    const totalHeld = holders.reduce((sum, [, owned]) => sum + owned, 0) || tile.totalShares;

    let pool = players;
    let unclaimed = newShares;
    const nextShares: ShareholderRecord = { ...tile.shares };

    holders.forEach(([holderId, owned]) => {
      const proRata = Math.round((owned / totalHeld) * newShares);
      const bill = Math.round(cost * (owned / totalHeld));
      const holder = pool.find((p) => p.id === holderId);
      if (!holder || proRata <= 0) return;

      if (holder.cash >= bill) {
        pool = withCash(pool, holderId, -bill);
        nextShares[holderId] = owned + proRata;
        unclaimed -= proRata;
        get().recordFinancial(holderId, 'EXPENSE', 'buildingUpgrades', `${tile.name} → T${targetLevel} (kapitálový call)`, bill);
        return;
      }

      const rival = pool
        .filter((p) => p.id !== holderId && !p.isBankrupt)
        .sort((a, b) => b.cash - a.cash)[0];
      if (rival && rival.cash >= bill) {
        pool = withCash(pool, rival.id, -bill);
        nextShares[rival.id] = (nextShares[rival.id] ?? 0) + proRata;
        unclaimed -= proRata;
        get().logEvent(`${rival.name} covered $${bill.toLocaleString()} of ${holder.name}'s capital call — equity diluted.`, 'RISK');
      }
    });

    // Upgrade increases property valuation by ~15% per tier (investment value)
    const valuationBoost = 1 + (targetLevel - tile.upgradeLevel) * 0.15;
    // Monopoly valuation bonus: +20% valuation when a player holds monopoly in the district
    const proposerId = get().proposal.proposerId;
    const hasMono = proposerId ? hasMonopoly(board, proposerId, tile.district) : false;
    const monoValuationBoost = hasMono ? 1.2 : 1.0;
    
    const nextBoard = [...board];
    nextBoard[index] = {
      ...tile,
      upgradeLevel: targetLevel,
      totalShares: tile.totalShares + newShares,
      shares: nextShares,
      upgradeCost: Math.round(tile.upgradeCost * 1.25),
      valuation: Math.round(tile.valuation * valuationBoost * monoValuationBoost),
    };

    // Credit score boost for monopoly holders
    const poolWithBoost = hasMono
      ? pool.map((p) =>
          p.id === proposerId
            ? { ...p, creditScore: Math.min(100, p.creditScore + 10) }
            : p
        )
      : pool;
    
    set({ board: nextBoard, players: poolWithBoost, proposal: { ...emptyProposal }, turnPhase: 'TURN_END' });
    get().logEvent(
      `${tile.name} upgraded to tier ${targetLevel}. $${cost.toLocaleString()} raised, ${Math.max(0, unclaimed)} shares unclaimed.`,
      'GAIN',
    );
    get().runMarginCalls();
    get().checkBankruptcies();
  },

  resetProposal: () => set({ proposal: { ...emptyProposal } }),

  addShares: (playerId, propertyId, shares, pricePerShare) => {
    const { board, players, market } = get();
    const index = board.findIndex((t) => t.id === propertyId);
    if (index === -1 || shares <= 0) return;
    const tile = board[index];
    const owned = Object.values(tile.shares).reduce((sum, value) => sum + value, 0);
    const float = Math.max(0, tile.totalShares - owned);
    const buy = Math.min(shares, float);
    if (buy <= 0) return;

    const unit = pricePerShare > 0 ? pricePerShare : valuationOf(tile, market.phase) / Math.max(1, tile.totalShares);
    const spent = Math.round(buy * unit);
    const nextBoard = [...board];
    nextBoard[index] = { ...tile, shares: { ...tile.shares, [playerId]: sharesOf(tile, playerId) + buy } };

    const buyer = players.find((p) => p.id === playerId);
    if (buyer?.isAI) {
      // Purchase announcement: every bot acquisition surfaces a brief floating toast.
      set({
        board: nextBoard,
        players: withCash(players, playerId, -spent),
        botPurchaseToast: { id: Date.now(), text: `🤖 ${buyer.name} acquired ${buy} shares of ${tile.name}!`, color: buyer.color },
      });
    } else {
      set({ board: nextBoard, players: withCash(players, playerId, -spent) });
    }
    get().recordFinancial(playerId, 'EXPENSE', 'sharePurchases', `${buy}× ${tile.name}`, spent);
  },

  removeShares: (playerId, propertyId, shares, pricePerShare) => {
    const { board, players, market } = get();
    const index = board.findIndex((t) => t.id === propertyId);
    if (index === -1) return;
    const tile = board[index];
    const owned = sharesOf(tile, playerId);
    const sell = Math.min(shares, owned);
    if (sell <= 0) return;

    const unit = pricePerShare > 0 ? pricePerShare : valuationOf(tile, market.phase) / Math.max(1, tile.totalShares);
    const proceeds = Math.round(sell * unit);
    const nextBoard = [...board];
    nextBoard[index] = { ...tile, shares: { ...tile.shares, [playerId]: owned - sell } };

    set({ board: nextBoard, players: withCash(players, playerId, proceeds) });
    get().recordFinancial(playerId, 'INCOME', 'shareSaleProceeds', `Predáno ${sell}× ${tile.name}`, proceeds);
  },

  /** Internal forced sale for bankruptcy/foreclosure — allowed to sell to public float. */
  forceSellShares: (playerId, propertyId, shares) => {
    const { board, players } = get();
    const index = board.findIndex((t) => t.id === propertyId);
    if (index === -1 || shares <= 0) return;
    const tile = board[index];
    const owned = sharesOf(tile, playerId);
    const sell = Math.min(shares, owned);
    if (sell <= 0) return;

    const unit = valuationOf(tile, get().market.phase) / Math.max(1, tile.totalShares);
    const proceeds = Math.round(sell * unit);
    const nextBoard = [...board];
    nextBoard[index] = { ...tile, shares: { ...tile.shares, [playerId]: owned - sell } };
    const seller = players.find((p) => p.id === playerId);
    set({ board: nextBoard, players: withCash(players, playerId, proceeds) });
    get().recordFinancial(playerId, 'INCOME', 'shareSaleProceeds', `Force-sold ${sell}× ${tile.name}`, proceeds);
    get().logEvent(`${seller?.name ?? playerId} force-sold ${sell} shares of ${tile.name} for ${money(proceeds)} (5% exit fee).`, 'LOSS');
  },

  /** Shares cannot be sold to the void (public float). They must be sold to another player
   *  via P2P trade or through a public auction. Returns false if the sale cannot proceed. */
  sellShares: (playerId, propertyId, shares) => {
    const { board, players } = get();
    const index = board.findIndex((t) => t.id === propertyId);
    if (index === -1 || shares <= 0) return false;
    const tile = board[index];
    const owned = sharesOf(tile, playerId);
    const sell = Math.min(shares, owned);
    if (sell <= 0) return false;

    // Shares can only be sold to other players (via trade) or auctioned.
    // Direct sale to public float is not allowed.
    get().logEvent(`Prodej akcií do prázdna není povolen. Akcie musíš prodat jinému hráči nebo dát do aukce.`, 'RISK');
    return false;
  },

  insolvencySellShares: (tileId, shares) => {
    const { insolvency } = get();
    if (!insolvency) return;
    // Controlled asset sale during bankruptcy: allowed to sell to public float.
    get().forceSellShares(insolvency.playerId, tileId, shares);
  },
  raiseEmergency: (debtorId, amountDue, creditorShares, label, tileId) => {
    const { players } = get();
    const debtor = players.find((p) => p.id === debtorId);
    if (!debtor) return true;
    const deficit = Math.max(0, amountDue - Math.max(0, debtor.cash));
    const netWorth = get().netWorthOf(debtorId);

    // Total structural insolvency → true bankruptcy immediately.
    if (netWorth < amountDue) {
      get().setBankrupt(debtorId);
      get().logEvent(`${debtor.name} is structurally insolvent (net worth ${money(netWorth)} < ${money(amountDue)} due) and eliminated.`, 'BANKRUPTCY');
      return true;
    }

    const settlement: EmergencySettlement = {
      debtorId,
      debtorName: debtor.name,
      amountDue,
      cashAvailable: Math.max(0, debtor.cash),
      deficit,
      creditorShares: creditorShares.filter((c) => c.amount > 0),
      label,
      tileId,
    };
    set({ emergency: settlement });

    if (debtor.isAI) {
      // Bots prefer debt-to-equity swaps over distress fire-sales.
      get().autoResolveEmergency();
    }
    return true;
  },

  clearEmergency: () => set({ emergency: null }),

  // ---- Rent squeeze: borrow / treasury withdrawal / plain cash settlement ----

  /** Option A — credit line: the debtor borrows exactly the deficit within their ceiling. */
  emergencyBorrow: () => {
    const { emergency, players } = get();
    if (!emergency) return;
    const player = players.find((p) => p.id === emergency.debtorId);
    if (!player || player.isBankrupt) return;
    const deficit = Math.max(0, emergency.amountDue - Math.max(0, player.cash));
    if (deficit <= 0) return;
    const headroom = Math.max(0, get().debtCeilingOf(emergency.debtorId) - player.debt - get().loanExposureOf(emergency.debtorId));
    if (headroom < deficit) {
      get().logEvent('Banka odmítla čerpání úvěru — úvěrový strop je vyčerpán.', 'RISK');
      return;
    }
    set({
      players: get().players.map((p) => (p.id === emergency.debtorId ? { ...p, cash: p.cash + deficit, debt: p.debt + deficit } : p)),
    });
    get().logEvent(
      `${player.name} čerpal úvěrovou linku ${money(deficit)} na krytí deficitu (${emergency.label}) — dluh nyní ${money(player.debt + deficit)}.`,
      'RISK',
    );
  },

  /** Option B: withdraw treasury funds — penalty-aware, one click covers the deficit. */
  emergencyWithdraw: (tier) => {
    const { emergency, players, market } = get();
    if (!emergency) return;
    const player = players.find((p) => p.id === emergency.debtorId);
    if (!player) return;
    const need = Math.max(0, emergency.amountDue - player.cash);
    if (need <= 0) return;
    const entry = get().investmentOf(emergency.debtorId, tier);
    if (!entry || entry.value <= 0) return;
    // Early-exit penalty is charged on every withdrawal — request the GROSS amount
    // whose net still covers the deficit (capped by the fund value), so one click
    // always suffices instead of chasing a shrinking remainder.
    const heldRounds = Math.max(0, market.currentRound - entry.openedRound);
    const penaltyRate = investmentExitPenalty(tier, heldRounds);
    const gross = Math.min(Math.floor(entry.value), Math.ceil(need / Math.max(0.01, 1 - penaltyRate)));
    if (gross <= 0) return;
    get().withdrawInvestment(emergency.debtorId, tier, gross);
  },

  /** CTA — pay the liability in cash: creditors are credited pro-rata, turn resumes. */
  emergencyPayCash: () => {
    const { emergency, players } = get();
    if (!emergency) return;
    const player = players.find((p) => p.id === emergency.debtorId);
    if (!player || player.cash < emergency.amountDue) return; // CTA stays locked

    let nextPlayers = withCash(players, emergency.debtorId, -emergency.amountDue);
    emergency.creditorShares.forEach((credit) => {
      if (credit.amount <= 0) return;
      nextPlayers = withCash(nextPlayers, credit.playerId, credit.amount);
      get().triggerMoneyTransfer({ fromId: emergency.debtorId, toId: credit.playerId, amount: credit.amount, reason: `Rent settled in cash — ${emergency.label}` });
    });
    set({ players: nextPlayers });
    get().logEvent(
      `${player.name} uhradil ${emergency.label} ${money(emergency.amountDue)} v hotovosti (úvěr/investice kryly deficit) — věřitelé proplaceni.`,
      'LOSS',
    );
    get().resumeAfterEmergency();
  },

  emergencyFireSale: () => {
    const { emergency, board, players } = get();
    if (!emergency) return;
    let cash = players.find((p) => p.id === emergency.debtorId)?.cash ?? 0;
    let need = Math.max(0, emergency.amountDue - cash);
    let nextBoard = board;

    // Sell lowest-yielding positions first at a 10% distress discount.
    const holdings = board
      .map((tile, index) => ({ tile, index, owned: sharesOf(tile, emergency.debtorId) }))
      .filter((h) => h.owned > 0)
      .sort((a, b) => (valuationLiveOf(a.tile) / a.tile.totalShares) - (valuationLiveOf(b.tile) / b.tile.totalShares));

    holdings.forEach(({ tile, index, owned }) => {
      if (need <= 0) return;
      const unit = fireSaleQuoteOf(tile, 1).unit;
      const sold = Math.min(owned, Math.ceil(need / Math.max(1, unit)));
      const proceeds = Math.round(sold * unit);
      const copy = [...nextBoard];
      copy[index] = { ...tile, shares: { ...tile.shares, [emergency.debtorId]: owned - sold } };
      nextBoard = copy;
      cash += proceeds;
      need -= proceeds;
      get().logEvent(`Fire-sale: ${emergency.debtorName} sold ${sold} shares of ${tile.name} for ${money(proceeds)} (10% distress discount).`, 'RISK');
    });

    set({ board: nextBoard, players: withCash(players, emergency.debtorId, 0) });
  },

  emergencySwap: (allocations) => {
    const { emergency, board, players } = get();
    if (!emergency) return;
    let nextBoard = board;

    allocations.forEach(({ tileId, shares, creditorId }) => {
      const index = nextBoard.findIndex((t) => t.id === tileId);
      if (index === -1 || shares <= 0) return;
      const tile = nextBoard[index];
      const owned = sharesOf(tile, emergency.debtorId);
      const move = Math.min(shares, owned);
      if (move <= 0) return;
      const nextShares = { ...tile.shares };
      nextShares[emergency.debtorId] = owned - move;
      nextShares[creditorId] = (nextShares[creditorId] ?? 0) + move;
      const copy = [...nextBoard];
      copy[index] = { ...tile, shares: nextShares };
      nextBoard = copy;
      const creditor = players.find((p) => p.id === creditorId);
      get().logEvent(`Debt-for-equity: ${creditor?.name ?? creditorId} received ${move} shares of ${tile.name} from ${emergency.debtorName} at fair value.`, 'RISK');
    });

    set({ board: nextBoard });
  },

  autoResolveEmergency: () => {
    const { emergency } = get();
    if (!emergency) return;

    // 1) Debt-to-equity swap first (no discount). Priority: tile of origin, then rest.
    let remaining = emergency.amountDue;
    const { board, players } = get();
    const holdings = board
      .map((tile) => ({ tile, owned: sharesOf(tile, emergency.debtorId) }))
      .filter((h) => h.owned > 0)
      .sort((a, b) => {
        const priority = (t: PropertyTile) => (emergency.tileId && t.id === emergency.tileId ? 0 : 1);
        return priority(a.tile) - priority(b.tile);
      });

    const allocations: { tileId: string; shares: number; creditorId: string }[] = [];
    holdings.forEach(({ tile, owned }) => {
      if (remaining <= 0) return;
      const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
      let left = remaining;
      // Pro-rata allocation across the creditors of this liability.
      emergency.creditorShares.forEach((c) => {
        if (left <= 0) return;
        const share = Math.min(owned - allocations.filter((a) => a.tileId === tile.id).reduce((s, a) => s + a.shares, 0), Math.ceil((c.amount / Math.max(1, emergency.amountDue)) * owned));
        const swap = Math.min(share, Math.ceil(left / Math.max(1, unit)));
        if (swap > 0) {
          allocations.push({ tileId: tile.id, shares: swap, creditorId: c.playerId });
          left -= swap * unit;
        }
      });
      remaining = Math.max(0, left);
    });

    if (allocations.length > 0) get().emergencySwap(allocations);

    // 2) Fire-sale for any remaining deficit.
    const debtor = players.find((p) => p.id === emergency.debtorId);
    const settledValue = emergency.amountDue - remaining;
    if (remaining > 0 && debtor && debtor.cash < remaining) get().emergencyFireSale();

    set({ emergency: null });
    get().logEvent(`${emergency.debtorName} settled ${emergency.label} of ${money(emergency.amountDue)} (swaps covered ~${money(Math.min(settledValue, emergency.amountDue))}).`, 'RISK');
  },

  resumeAfterEmergency: () => {
    set({
      emergency: null,
      turnPhase: 'TURN_END',
      movementPoints: 0,
      reachableIndices: [],
      crashRiskArmed: false,
      idlePenalty: 0,
    });
    get().logEvent('Emergency settlement complete — turn continues.', 'INFO');
    get().runMarginCalls();
    get().checkBankruptcies();
  },

  startTakeover: (tileId) => {
    const { board, players, currentPlayerIndex, market, lastTakeoverRound } = get();
    if (lastTakeoverRound >= market.currentRound) {
      get().logEvent('Hostile takeover already attempted this round.', 'RISK');
      return;
    }
    const buyer = players[currentPlayerIndex];
    const tile = board.find((t) => t.id === tileId);
    if (!buyer || !tile) return;
    if (sharesOf(tile, buyer.id) <= 0) {
      get().logEvent('You need a stake in the property to launch a takeover.', 'RISK');
      return;
    }
    const rivalShares = tile.totalShares - sharesOf(tile, buyer.id) - floatSharesOf(tile);
    if (rivalShares <= 0) {
      get().logEvent('No rival equity to acquire here.', 'RISK');
      return;
    }
    // Executive Shield (VIP policy): shielded shareholders cannot be bought out.
    if (players.some((p) => p.id !== buyer.id && (tile.shares[p.id] ?? 0) > 0 && p.insurancePolicy === 'VIP' && p.insuranceTurnsRemaining > 0)) {
      get().logEvent('Rival shareholders are protected by the Executive Shield — takeover blocked.', 'RISK');
      return;
    }
    const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
    const cost = Math.ceil(rivalShares * unit * 1.5);
    if (buyer.cash < cost) {
      get().logEvent(`Takeover needs ${money(cost)} (150% premium) — you have ${money(buyer.cash)}.`, 'RISK');
      return;
    }
    set({ takeoverTileId: tileId, turnPhase: 'TAKEOVER_MODAL' });
  },

  executeTakeover: (buyerId, tileId) => {
    const { board, players, market } = get();
    const index = board.findIndex((t) => t.id === tileId);
    const buyer = players.find((p) => p.id === buyerId);
    const tile = board.find((t) => t.id === tileId);
    if (!buyer || !tile) return;
    // Executive Shield backstop: a VIP policyholder's shares are never force-bought.
    if (players.some((p) => p.id !== buyerId && (tile.shares[p.id] ?? 0) > 0 && p.insurancePolicy === 'VIP' && p.insuranceTurnsRemaining > 0)) {
      get().logEvent(`${tile.name} is protected — the Executive Shield blocked the hostile takeover.`, 'RISK');
      set({ takeoverTileId: null });
      return;
    }
    // Attention intercept: a bot is buying out shares the human holds.
    // `attention` non-null means the human already acknowledged via the modal, so
    // skip the intercept and let the buyout settle.
    const human = players.find((p) => !p.isAI);
    if (buyer.isAI && human && (tile.shares[human.id] ?? 0) > 0 && !get().attention) {
      const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
      const humanShares = tile.shares[human.id] ?? 0;
      const payout = money(Math.ceil(humanShares * unit * 1.5));
      set({
        ...suspensionOf(get(), 'ATTENTION_INTERCEPT'),
        attention: {
          kind: 'TAKEOVER',
          title: '⚠️ ATTENTION REQUIRED: Hostile Takeover Targeting Your Shares',
          body: `${buyer.name} bids a 150% premium for every rival share of ${tile.name}. Your ${humanShares} shares are in the crosshairs — acknowledge to allow the buyout and collect ${payout}.`,
          tileId,
          buyerId: buyer.id,
          stake: [
            { label: 'Your shares', value: `${humanShares} of ${tile.totalShares}` },
            { label: 'Your payout (150%)', value: payout },
            { label: 'Market value of stake', value: money(Math.round(humanShares * unit)) },
            { label: 'Bidder', value: `${buyer.name} · ${money(buyer.cash)} cash` },
            { label: 'Tile rent / visit', value: money(rentFor(tile, market.phase)) },
          ],
        },
      });
      return;
    }
    const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
    let cost = 0;
    const nextShares = { ...tile.shares };
    const acquired = Object.entries(tile.shares)
      .filter(([id, s]) => id !== buyerId && s > 0)
      .map(([id, s]) => ({ id, s }));
    const nextPlayers = [...players];
    acquired.forEach(({ id, s }) => {
      const payout = Math.ceil(s * unit * 1.5);
      cost += payout;
      const i = players.findIndex((p) => p.id === id);
      if (i !== -1) nextPlayers[i] = { ...players[i], cash: players[i].cash + payout };
      nextShares[id] = 0;
    });
    const buyerIdx = players.findIndex((p) => p.id === buyerId);
    if (buyerIdx === -1 || cost <= 0 || buyer.cash < cost) return;
    nextPlayers[buyerIdx] = { ...buyer, cash: buyer.cash - cost };
    nextShares[buyerId] = (nextShares[buyerId] ?? 0) + acquired.reduce((sum, a) => sum + a.s, 0);
    const nextBoard = [...board];
    nextBoard[index] = { ...tile, shares: nextShares };
    set({ board: nextBoard, players: nextPlayers, takeoverTileId: null, turnPhase: 'TURN_END', lastTakeoverRound: market.currentRound });
    get().logEvent(`${buyer.name} executed a HOSTILE TAKEOVER of ${tile.name} for ${money(cost)} (150% premium).`, 'RISK');
  },

  enactZoningFreeze: (district) => {
    const { board, players, currentPlayerIndex } = get();
    const player = players[currentPlayerIndex];
    if (!player || player.cash < 1500) {
      get().logEvent('Zoning freeze costs $1,500 — insufficient cash.', 'RISK');
      return;
    }
    let frozen = 0;
    const nextBoard = board.map((tile) => {
      if (tile.district === district && tile.tileType === 'COMMERCIAL') {
        frozen += 1;
        return { ...tile, isFrozen: true };
      }
      return tile;
    });
    set({
      board: nextBoard,
      players: withCash(players, player.id, -1500),
      turnPhase: 'TURN_END',
      selectedTileIndex: null,
      movementPoints: 0,
      crashRiskArmed: false,
      idlePenalty: 0,
      botStatus: player.isAI
        ? { playerId: player.id, phase: 'GOVERN', text: `${player.name} lobbied City Hall — ZONING FREEZE on ${district} (${frozen} tiles)` }
        : null,
    });
    get().logEvent(`${player.name} enacted a ZONING FREEZE on ${district} — ${frozen} tiles frozen for 1 round.`, 'RISK');
  },

  triggerAuditOnLeader: () => {
    const { players, currentPlayerIndex } = get();
    const player = players[currentPlayerIndex];
    if (!player || player.cash < 1000) {
      get().logEvent('Audit filing costs $1,000 — insufficient cash.', 'RISK');
      return;
    }
    const leader = [...players].filter((p) => !p.isBankrupt).sort((a, b) => b.cash - a.cash)[0];
    if (!leader) return;
    const tax = Math.round(leader.cash * 0.1);
    let nextPlayers = withCash(players, player.id, -1000);
    nextPlayers = withCash(nextPlayers, leader.id, -tax);
    set({
      players: nextPlayers,
      turnPhase: 'TURN_END',
      selectedTileIndex: null,
      movementPoints: 0,
      crashRiskArmed: false,
      idlePenalty: 0,
      botStatus: player.isAI
        ? { playerId: player.id, phase: 'GOVERN', text: `${player.name} triggered an AUDIT on ${leader.name} (−${money(tax)})` }
        : null,
    });
    get().logEvent(`${player.name} triggered an AUDIT on ${leader.name} — 10% liquidity tax of ${money(tax)}.`, 'RISK');
  },

  skipLobby: () => {
    set({ turnPhase: 'TURN_END', selectedTileIndex: null, movementPoints: 0, crashRiskArmed: false, idlePenalty: 0 });
    get().logEvent('Walked out of City Hall empty-handed.', 'INFO');
  },

  setBankrupt: (playerId) => {
    const { board, players } = get();
    const nextBoard = board.map((tile) => {
      if (!(playerId in tile.shares)) return tile;
      const nextShares = { ...tile.shares };
      delete nextShares[playerId];
      return { ...tile, shares: nextShares };
    });
    set({
      board: nextBoard,
      players: players.map((p) => (p.id === playerId ? { ...p, isBankrupt: true, cash: 0, debt: 0 } : p)),
    });
  },

  runMarginCalls: () => {
    const { players, board, market } = get();
    if (!players.some((p) => !p.isBankrupt && p.cash < 0)) return;

    let pool = players;
    let nextBoard = board;

    players
      .filter((p) => !p.isBankrupt && p.cash < 0)
      .forEach((player) => {
        let cash = player.cash;
        const holdings = nextBoard
          .map((tile, index) => ({ tile, index, owned: sharesOf(tile, player.id) }))
          .filter((h) => h.owned > 0)
          .sort((a, b) => valuationOf(b.tile, market.phase) - valuationOf(a.tile, market.phase));

        holdings.forEach(({ tile, index, owned }) => {
          if (cash >= 0) return;
          const unit = (valuationOf(tile, market.phase) / Math.max(1, tile.totalShares)) * (1 - MARGIN_CALL_DISCOUNT);
          const needed = Math.ceil(-cash / Math.max(1, unit));
          const sold = Math.min(owned, Math.max(1, needed));
          cash += Math.round(sold * unit);
          const copy = [...nextBoard];
          copy[index] = { ...tile, shares: { ...tile.shares, [player.id]: owned - sold } };
          nextBoard = copy;
        });

        pool = pool.map((p) => (p.id === player.id ? { ...p, cash, debt: p.debt + Math.max(0, -cash) } : p));
        get().logEvent(
          `Margin call: ${player.name} liquidated holdings at a ${MARGIN_CALL_DISCOUNT * 100}% distress discount.`,
          'RISK',
        );
      });

    set({ players: pool, board: nextBoard });
  },

  checkBankruptcies: () => {
    const { players, market, victory } = get();
    // Lobby victory condition drives the net-worth target.
    const winThreshold = victory === 'NET_WORTH_100K' ? WIN_NET_WORTH : victory === 'NET_WORTH_150K' ? 150000 : null;

    players
      .filter((p) => !p.isBankrupt)
      .forEach((p) => {
        const net = get().netWorthOf(p.id);
        if (net <= 0) {
          get().setBankrupt(p.id);
          get().logEvent(`${p.name} is insolvent (net worth $${net.toLocaleString()}) and eliminated.`, 'BANKRUPTCY');
          return;
        }
        if (winThreshold !== null && net >= winThreshold && !get().gameOver) {
          set({ gameOver: true, winnerId: p.id, turnPhase: 'TURN_END' });
          get().logEvent(`${p.name} hit $${winThreshold.toLocaleString()} net worth — capitalist dominance achieved.`, 'GAIN');
        }
      });

    const alive = get().players.filter((p) => !p.isBankrupt);
    if (alive.length <= 1 && !get().gameOver) {
      set({ gameOver: true, winnerId: alive[0]?.id ?? null, turnPhase: 'TURN_END' });
      get().logEvent(`Last tycoon standing: ${alive[0]?.name ?? 'nobody'}.`, 'INFO');
      return;
    }
    // Classic length mode: settle by net worth once the cap is reached.
    if (market.maxRounds > 0 && market.currentRound >= market.maxRounds && !get().gameOver) {
      const ranked = get()
        .players.filter((p) => !p.isBankrupt)
        .sort((a, b) => get().netWorthOf(b.id) - get().netWorthOf(a.id));
      set({ gameOver: true, winnerId: ranked[0]?.id ?? null, turnPhase: 'TURN_END' });
      get().logEvent(`Round cap reached (R${market.maxRounds}) — ${ranked[0]?.name ?? 'no one'} wins on net worth.`, 'INFO');
    }
  },

  resetGame: () => {
    lastBotBotTradeRound = -1;
    lastCartelIntelRound = -1;
    set({
      board: buildBoard(),
      players: buildPlayers(),
      market: { ...INITIAL_MARKET },
      currentPlayerIndex: 0,
      turnPhase: 'TURN_START',
      events: [{ id: 1, round: 1, message: 'New session started. Market opens in Stagnation.', kind: 'INFO' }],
      selectedTileIndex: null,
      inspectTileIndex: null,
      movementPoints: 0,
      pushLuckUsed: false,
      crashRiskArmed: false,
      idlePenalty: 0,
      reachableIndices: [],
      auction: { ...emptyAuction },
      pendingOffer: null,
      emergency: null,
      fastTravelFrom: null,
      metroRiddenThisTurn: false,
      takeoverTileId: null,
      lastTakeoverRound: -1,
      blackSwan: { event: null, title: '', description: '' },
      tradeOffer: null,
      insolvency: null,
      attention: null,
      resumePhase: null,
      botStatus: null,
      tradeToast: null,
      newPropertyAlerts: [],
      spotlightTileId: null,
      lastRentCredit: null,
      rentSeq: 0,
      lastBotTradeRound: -1,
      proposal: { ...emptyProposal },
      expansionPending: false,
      gameOver: false,
      winnerId: null,
      handover: null,
      hotseatReview: null,
      loans: [],
      investments: [],
      financialDeskOpen: false,
      gameStarted: true,
      epoch: get().epoch + 1,
    });
  },

  /** Lobby CTA: build the roster from the config and open the first turn. */
  startGame: (config) => {
    const players = buildPlayersFromConfig(config);
    lastBotBotTradeRound = -1;
    lastCartelIntelRound = -1;
    set({
      board: buildBoard(),
      players,
      market: { ...INITIAL_MARKET },
      currentPlayerIndex: 0,
      turnPhase: 'TURN_START',
      victory: config.victory,
      lobby: config,
      gameStarted: true,
      events: [
        {
          id: 1,
          round: 1,
          message: `New match: ${players.filter((p) => !p.isAI).length} human(s) + ${players.filter((p) => p.isAI).length} bot(s) · start capital $${config.startingCash.toLocaleString()}. Market opens in Stagnation.`,
          kind: 'INFO',
        },
      ],
      selectedTileIndex: null,
      inspectTileIndex: null,
      movementPoints: 0,
      pushLuckUsed: false,
      crashRiskArmed: false,
      idlePenalty: 0,
      reachableIndices: [],
      auction: { ...emptyAuction },
      pendingOffer: null,
      emergency: null,
      fastTravelFrom: null,
      metroRiddenThisTurn: false,
      takeoverTileId: null,
      lastTakeoverRound: -1,
      blackSwan: { event: null, title: '', description: '' },
      tradeOffer: null,
      insolvency: null,
      attention: null,
      resumePhase: null,
      botStatus: null,
      tradeToast: null,
      newPropertyAlerts: [],
      spotlightTileId: null,
      lastRentCredit: null,
      rentSeq: 0,
      lastBotTradeRound: -1,
      proposal: { ...emptyProposal },
      expansionPending: false,
      gameOver: false,
      winnerId: null,
      handover: null,
      hotseatReview: null,
      loans: [],
      investments: [],
      financialDeskOpen: false,
      epoch: get().epoch + 1,
    });
  },

  /** New Game header button: tear the match down and return to the setup lobby. */
  openLobby: () => {
    lastBotBotTradeRound = -1;
    lastCartelIntelRound = -1;
    set({
      gameStarted: false,
      turnPhase: 'TURN_START',
      auction: { ...emptyAuction },
      pendingOffer: null,
      emergency: null,
      tradeOffer: null,
      insolvency: null,
      attention: null,
      resumePhase: null,
      botStatus: null,
      handover: null,
      hotseatReview: null,
      loans: [],
      investments: [],
      financialDeskOpen: false,
      gameOver: false,
      winnerId: null,
      epoch: get().epoch + 1,
    });
  },

  /** Pass-the-device CTA: the waiting human is at the controls. */
  beginHandoverTurn: () => set({ handover: null, turnPhase: 'TURN_START' }),

  /** Hotseat review CTA: the receiving human decided at the same PC. */
  respondHotseatTrade: (accept) => {
    set({ hotseatReview: null });
    get().respondToTrade(accept);
  },

  // AI Heuristics — advanced strategic mechanics used by the bot loop.

  /**
   * Bot rent hedging: scans the rolling window (2–12 tiles, per ARCHETYPE_PROFILES)
   * for rival properties; buys the persona's preferred policy when the worst
   * upcoming rent exceeds `insurance.exposurePct` (35 %) of liquid cash.
   *  - CONSERVATIVE → BASIC (50 % coverage), AGGRESSIVE → VIP (90 % + takeover immunity),
   *  - OPPORTUNISTIC → STANDARD.
   */
  aiPurchaseInsurance: (playerId) => {
    const { players, board, market, fastTravelFrom, metroRiddenThisTurn, turnPhase } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player || !player.isAI || player.isBankrupt) return;
    if (player.insuranceTurnsRemaining > 0) return;
    if (fastTravelFrom !== null || metroRiddenThisTurn || turnPhase === 'FAST_TRAVEL_SELECTION') return;

    const profile = profileOf(player.aiArchetype);
    // Shared scan (2–12 tiles ahead) — the worst upcoming rival rent decides the hedge.
    const { worstRent, worstTile } = insuranceScanOf(board, player, market.phase, profile.insurance.scanAhead, rentFor);
    if (!insuranceTriggered(worstRent, player.cash)) return;

    const policy = profile.insurance.policy;
    const meta = INSURANCE_POLICY_META[policy];
    const premium = Math.round(get().netWorthOf(playerId) * meta.premiumPct);
    if (premium <= 0 || player.cash < premium) return;
    get().buyInsurance(playerId, policy);
    get().logEvent(
      `${player.name} pojistil riziko ${worstTile ? worstTile.name : 'vysokého nájemného'}: expozice ${money(worstRent)} > ${Math.round(profile.insurance.exposurePct * 100)} % hotovosti (${money(player.cash)}) → ${meta.label}.`,
      'INFO',
    );
  },

  /**
   * Autonomous treasury management driven by the shared `planTreasury` heuristic:
   *  - CONSERVATIVE: surplus above $1,200 → Money Market Fund (fixed 3 %/round).
   *  - OPPORTUNISTIC: surplus above $1,500 → Real Estate REITs.
   *  - AGGRESSIVE: up to 40 % of cash → Venture Fund during a BOOM; liquidated otherwise.
   * Funds are drained the moment cash drops under the operating threshold.
   */
  aiManageTreasury: (playerId) => {
    const { players, market, investments } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player || !player.isAI || player.isBankrupt) return;
    const positions = investments
      .filter((entry) => entry.playerId === playerId)
      .map((entry) => ({ tier: entry.tier, value: entry.value }));
    const plan = planTreasury(player, market.phase, positions);

    if (plan.withdraw) {
      const tier = plan.withdraw.tier;
      const current = positions.find((entry) => entry.tier === tier)?.value ?? 0;
      const amount = Math.min(plan.withdraw.amount, current);
      if (amount > 0) {
        get().withdrawInvestment(playerId, tier, amount);
        get().setBotStatus({
          playerId,
          phase: 'GOVERN',
          text: `${player.name} vybírá ${INVESTMENT_TIER_META[tier].name} fond kvůli nízké hotovosti.`,
        });
      }
      return;
    }

    if (plan.deposit && plan.deposit.amount > 0) {
      const { tier, amount } = plan.deposit;
      get().investCash(playerId, tier, amount);
      get().setBotStatus({
        playerId,
        phase: 'GOVERN',
        text: `${player.name} parkuje ${money(amount)} volné hotovosti do ${INVESTMENT_TIER_META[tier].name} (${market.phase}).`,
      });
    }
  },

  /**
   * Autonomous B2B negotiation (the bot "trading table" hook):
   *  1) chance gate (35 % base / 75 % when one tile from a monopoly or in a cash crisis),
   *  2) synergy scan for win-win swaps across complementary split districts,
   *  3) partner verdict via `evaluateBotTrade`; on a near-miss (0.85×–1.04×) the partner
   *     counters with a cash sweetener ("add $150") and the initiator re-evaluates it,
   *  4) accepted deals settle automatically with a ticker line + money-transfer animation.
   * Anti-leader cartels: no deal that completes the market hegemon's district ever closes,
   * and two trailing bots accept mutually completing swaps at a discounted 0.95× ratio.
   */
  executeBotTradingPhase: (playerId, options) => {
    const force = options?.force ?? false;
    const { players, board, market } = get();
    if (!force && market.currentRound <= lastBotBotTradeRound) return null;
    const initiator = players.find((p) => p.id === playerId);
    if (!initiator || !initiator.isAI || initiator.isBankrupt) return null;

    const leader = detectMarketLeader(board, players, get().netWorthOf);
    const leaderId = leader?.isLeader ? leader.id : null;

    let candidate: BotDeal | null = options?.deal ?? null;
    if (!candidate) {
      if (!force && !shouldBotTrade(board, initiator)) return null;
      candidate = findSynergyDeals(board, players, initiator, leaderId)[0] ?? null;
    }
    if (!candidate) return null;
    const deal: BotDeal = candidate;

    const partner = players.find((p) => p.id === deal.partnerId);
    if (!partner || !partner.isAI || partner.isBankrupt || partner.id === initiator.id) return null;

    const initiatorReserve = profileOf(initiator.aiArchetype).trade.cashBuffer;
    let offeredCash = Math.max(0, Math.round(deal.offeredCash));
    const requestedCash = Math.max(0, Math.round(deal.requestedCash));
    if (offeredCash > initiator.cash - initiatorReserve) return null;

    const offer: TradeOffer = {
      id: `b2b-${Date.now()}`,
      senderId: initiator.id,
      receiverId: partner.id,
      offeredCash,
      offeredShares: deal.give,
      requestedCash,
      requestedShares: deal.take,
      status: 'PENDING',
    };

    let verdict = evaluateBotTrade(board, players, offer, leaderId);
    let counterRequested = 0;
    const bothTrailing = !leaderId || (initiator.id !== leaderId && partner.id !== leaderId);
    const cartelSwap = deal.mutual && bothTrailing;

    // Cartel sweetheart swap: mutually completing deals close at a discounted 0.95× ratio.
    if (!verdict.accept && cartelSwap && describeDealMeter(board, offer).ratio >= CARTEL_SWEETHEART_RATIO) {
      verdict = {
        accept: true,
        reason: 'ACCEPT_SET',
        ratio: describeDealMeter(board, offer).ratio,
        note: '🤝 Kartelní dohoda: vzájemná výměna distriktů proti lídrovi.',
      };
    }

    // Counter-offer: a near-miss asks for a cash sweetener instead of rejecting outright.
    // Only when the initiator is the cash payer (swap / cash purchase), never on a liquidity sale.
    if (!verdict.accept && !deal.mutual && requestedCash === 0) {
      const negotiation = resolveCounterOffer({
        ratio: describeDealMeter(board, offer).ratio,
        shortfall: tradeShortfall(board, players, offer),
        initiatorCash: initiator.cash,
        initiatorReserve,
        embargoMultiplier: leaderId && (leaderId === initiator.id || leaderId === partner.id) ? LEADER_EMBARGO_MULTIPLIER : 1,
      });
      if (negotiation.counter) {
        offeredCash += negotiation.cashAdded;
        counterRequested = negotiation.cashAdded;
        offer.offeredCash = offeredCash;
        get().logEvent(`${partner.name} protinabídl: „Přidej ${money(negotiation.cashAdded)} a je to."`, 'INFO');
        verdict = evaluateBotTrade(board, players, offer, leaderId);
      }
    }
    if (!verdict.accept) return null;
    // ---- Settlement: shares move first, then cash with a paired flying-cash event ----
    set({
      board: board.map((tile) => {
        let next = tile;
        const giveLot = deal.give.find((lot) => lot.tileId === tile.id);
        if (giveLot) {
          next = {
            ...next,
            shares: {
              ...next.shares,
              [initiator.id]: (next.shares[initiator.id] ?? 0) - giveLot.shares,
              [partner.id]: (next.shares[partner.id] ?? 0) + giveLot.shares,
            },
          };
        }
        const takeLot = deal.take.find((lot) => lot.tileId === tile.id);
        if (takeLot) {
          next = {
            ...next,
            shares: {
              ...next.shares,
              [partner.id]: (next.shares[partner.id] ?? 0) - takeLot.shares,
              [initiator.id]: (next.shares[initiator.id] ?? 0) + takeLot.shares,
            },
          };
        }
        return next;
      }),
    });

    if (requestedCash > 0) {
      set({ players: withCash(withCash(get().players, partner.id, -requestedCash), initiator.id, requestedCash) });
      get().triggerMoneyTransfer({ fromId: partner.id, toId: initiator.id, amount: requestedCash, reason: 'B2B settlement' });
    }
    if (offeredCash > 0) {
      set({ players: withCash(withCash(get().players, initiator.id, -offeredCash), partner.id, offeredCash) });
      get().triggerMoneyTransfer({ fromId: initiator.id, toId: partner.id, amount: offeredCash, reason: 'B2B settlement' });
    }

    lastBotBotTradeRound = market.currentRound;
    const nameOf = (tileId: string) => board.find((t) => t.id === tileId)?.name ?? tileId;
    const giveName = deal.give.map((lot) => nameOf(lot.tileId)).join(' + ') || '—';
    const takeName = deal.take.map((lot) => nameOf(lot.tileId)).join(' + ') || '—';
    const cashTag = offeredCash > 0 ? ` + ${money(offeredCash)}` : requestedCash > 0 ? ` − ${money(requestedCash)}` : '';
    const message = `🤝 ${initiator.name} a ${partner.name} vyjednali směnu: ${giveName} ↔ ${takeName}${cashTag}`;
    get().logEvent(message, 'GAIN');
    set({
      tradeToast: {
        id: Date.now(),
        title: `🤝 ${initiator.name} ⇄ ${partner.name}`,
        lines: [
          `${initiator.name} předává: ${giveName}`,
          `${partner.name} předává: ${takeName}`,
          ...(offeredCash > 0 ? [`Hotovostní dorovnání: ${money(offeredCash)}`] : []),
          ...(requestedCash > 0 ? [`${partner.name} doplácí ${money(requestedCash)}`] : []),
          ...(counterRequested > 0 ? [`Vyjednáno protinabídkou: +${money(counterRequested)}`] : []),
          verdict.note,
        ],
      },
    });
    get().setBotStatus({ playerId: initiator.id, phase: 'GOVERN', text: `${initiator.name} dojednal směnu s ${partner.name}.` });

    return {
      executed: true,
      initiatorId: initiator.id,
      partnerId: partner.id,
      give: deal.give,
      take: deal.take,
      cashPaid: offeredCash,
      counterRequested,
      cartel: cartelSwap,
      message,
    };
  },
  /** Back-compat wrapper: the bot loop calls the full negotiation hook. */
  aiBotToBotTrade: (playerId) => {
    get().executeBotTradingPhase(playerId);
  },
  /** Growth leverage: aggressive/opportunistic bots draw a structured loan for a live gap. */
  aiConsiderGrowthLoan: (playerId) => {
    const { players, board } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player || !player.isAI || player.isBankrupt) return;
    const headroom = Math.max(0, get().debtCeilingOf(playerId) - player.debt - get().loanExposureOf(playerId));
    const plan = planGrowthLoan({
      player,
      board,
      headroom,
      priceOf: (tile) => Math.max(1, pricePerShareOf(tile)),
      floatOf: floatSharesOf,
      upgradeCostOf: (tile) => tile.upgradeCost,
    });
    if (!plan || plan.principal < 1) return;
    get().takeStructuredLoan(playerId, plan.principal, 5, plan.structure);
    const purpose = plan.purpose === 'LANDING' ? 'akviziční tranši na neobsazené parcele' : 'rekonstrukci kontrolované nemovitosti';
    get().setBotStatus({
      playerId,
      phase: 'GOVERN',
      text: `${player.name} si bere ${plan.structure === 'BALLOON' ? 'balónový' : 'anuitní'} úvěr ${money(plan.principal)} na ${purpose}.`,
    });
    // The landing loan is immediately deployed so the leverage actually buys the stake.
    if (plan.purpose === 'LANDING') {
      const tile = get().board.find((entry) => entry.id === plan.tileId);
      const fresh = get().players.find((p) => p.id === playerId);
      if (tile && fresh) {
        const buy = botEvaluatePurchase(get().board, fresh, tile);
        if (buy.shares > 0) get().botExecutePurchase(playerId, tile.id, buy.shares, 'MAX_AFFORDABLE', buy.note);
      }
    }
  },
  /**
   * Complete Financial Desk routine for a bot: dark cash drag (funds), proactive leverage
   * and rent insurance — in one ordered pass so a bot never hoards dead cash.
   */
  botFinancePhase: (playerId) => {
    get().aiManageTreasury(playerId);
    get().aiConsiderGrowthLoan(playerId);
    get().aiPurchaseInsurance(playerId);
  },

  /**
   * Cartel City Hall tactic: while someone is dominating, a trailing bot votes to freeze the
   * leader's prime district (highest live rent) or to audit the hegemon's liquidity.
   */
  aiCityHallTactic: (playerId) => {
    const { players, board, market } = get();
    const player = players.find((p) => p.id === playerId);
    if (!player || !player.isAI || player.isBankrupt) return;
    const leader = detectMarketLeader(board, players, get().netWorthOf);
    if (!leader || !leader.isLeader || leader.id === player.id) {
      get().skipLobby();
      return;
    }

    // Prime district = the leader's colour group with the highest live rent.
    const district = [...new Set(board.filter((t) => t.tileType === 'COMMERCIAL').map((t) => t.district))]
      .map((entry) => ({
        entry,
        rent: board
          .filter((t) => t.district === entry)
          .reduce((sum, t) => sum + ((t.shares[leader.id] ?? 0) / Math.max(1, t.totalShares)) * rentFor(t, market.phase), 0),
      }))
      .sort((a, b) => b.rent - a.rent)[0];

    // Lobbying costs read `currentPlayerIndex`, so keep the acting bot at the controls.
    const index = players.findIndex((p) => p.id === player.id);
    if (index !== -1 && get().currentPlayerIndex !== index) set({ currentPlayerIndex: index });

    if (player.cash >= 2600 && district && district.rent > 0) {
      get().enactZoningFreeze(district.entry);
    } else if (player.cash >= 1400) {
      get().triggerAuditOnLeader();
    } else {
      get().skipLobby();
      return;
    }
    if (lastCartelIntelRound < market.currentRound) {
      lastCartelIntelRound = market.currentRound;
      get().logEvent('🕵️ Zákulisní kartel: Boti uzavřeli tichou dohodu o blokování lídra trhu!', 'RISK');
    }
  },

  /**
   * Corporate Raider: aggressive bots spend excess cash (> $5,000) on a hostile takeover,
   * force-buying rival equity at a 150% premium. One attempt per round, governance only.
   */
  aiInitiateHostileTakeover: (playerId) => {
    const { players, board, market, lastTakeoverRound, turnPhase, takeoverTileId, attention } = get();
    if (turnPhase !== 'GOVERNANCE_PHASE' && turnPhase !== 'TURN_END') return;
    if (takeoverTileId || attention) return;
    const player = players.find((p) => p.id === playerId);
    if (!player || !player.isAI || player.isBankrupt) return;
    if (player.aiArchetype !== 'AGGRESSIVE') return;
    if (market.currentRound <= lastTakeoverRound) return;

    const EXCESS_CASH = 5000;
    if (player.cash <= EXCESS_CASH) return;

    const candidates = board
      .map((tile) => {
        const ownShares = sharesOf(tile, playerId);
        if (ownShares <= 0 || tile.isFrozen) return null;
        const rivalShares = tile.totalShares - ownShares - floatSharesOf(tile);
        if (rivalShares <= 0) return null;
        // Executive Shield: never waste a bid on a VIP-protected shareholder.
        if (players.some((p) => p.id !== playerId && (tile.shares[p.id] ?? 0) > 0 && p.insurancePolicy === 'VIP' && p.insuranceTurnsRemaining > 0)) return null;
        const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
        const cost = Math.ceil(rivalShares * unit * 1.5);
        if (cost > player.cash) return null;
        return { tile, cost, rivalShares };
      })
      .filter((entry): entry is { tile: PropertyTile; cost: number; rivalShares: number } => entry !== null)
      .sort((a, b) => b.rivalShares - a.rivalShares);

    const target = candidates.find((entry) => entry.cost <= player.cash - EXCESS_CASH) ?? candidates[0];
    if (!target || target.cost > player.cash) return;

    get().logEvent(`${player.name} launches a hostile takeover bid on ${target.tile.name} (${money(target.cost)}).`, 'RISK');
    get().executeTakeover(playerId, target.tile.id);
  },

    }),
    {
      name: SAVE_KEY,
      // Persist the world; transient auction timer resolves safely on load.
      partialize: (s) => ({
        board: s.board,
        players: s.players,
        market: s.market,
        currentPlayerIndex: s.currentPlayerIndex,
        turnPhase: s.turnPhase,
        events: s.events,
        selectedTileIndex: s.selectedTileIndex,
        movementPoints: s.movementPoints,
        pushLuckUsed: s.pushLuckUsed,
        crashRiskArmed: s.crashRiskArmed,
        idlePenalty: s.idlePenalty,
        reachableIndices: s.reachableIndices,
        auction: { ...s.auction, thinkingBidderId: null },
        pendingOffer: s.pendingOffer,
        emergency: s.emergency,
        fastTravelFrom: s.fastTravelFrom,
        metroRiddenThisTurn: s.metroRiddenThisTurn,
        takeoverTileId: s.takeoverTileId,
        lastTakeoverRound: s.lastTakeoverRound,
        blackSwan: s.blackSwan,
        tradeOffer: s.tradeOffer,
        attention: s.attention,
        resumePhase: s.resumePhase,
        botStatus: s.botStatus,
        lastBotTradeRound: s.lastBotTradeRound,
        proposal: s.proposal,
        expansionPending: s.expansionPending,
        gameOver: s.gameOver,
        winnerId: s.winnerId,
        epoch: s.epoch,
        gameStarted: s.gameStarted,
        lobby: s.lobby,
        victory: s.victory,
        handover: s.handover,
        hotseatReview: s.hotseatReview,
        loans: s.loans,
        investments: s.investments,
      }),
      version: 1,
      // A save captured mid-gate must never reload frozen: release the gate on rehydrate.
      merge: (persistedState, currentState) => {
        const merged: GameStore = { ...currentState, ...(persistedState as Partial<GameStore>) };
        if (merged.turnPhase === 'ATTENTION_INTERCEPT' || merged.turnPhase === 'TRADE_NEGOTIATION') {
          merged.turnPhase = merged.resumePhase ?? 'TURN_END';
        }
        merged.resumePhase = null;
        merged.attention = null;
        merged.tradeOffer = null;
        merged.insolvency = null;
        merged.newPropertyAlerts = [];
        merged.spotlightTileId = null;
        merged.tradeToast = null;
        merged.botPurchaseToast = null;
        merged.botStatus = null;
        merged.moneyTransfers = [];
        merged.financialDeskOpen = false;
        merged.loans = Array.isArray(merged.loans) ? merged.loans : [];
        merged.investments = Array.isArray(merged.investments) ? merged.investments : [];
        // Legacy saves: backfill the independent rent yield and re-balance the colour
        // groups so the 3–5 corridor holds for boards created before the audit.
        if (Array.isArray(merged.board) && merged.board.length > 0) {
          merged.board = rebalanceDistricts(
            merged.board.map((tile) => {
              if (tile.tileType !== 'COMMERCIAL') return tile;
              const hasRate = typeof tile.rentYieldRate === 'number' && tile.rentYieldRate > 0;
              const normalized: PropertyTile = hasRate ? tile : { ...tile, rentYieldRate: yieldRateOf(tile.upgradeLevel) };
              return { ...normalized, rent: baseRentOf(normalized) };
            }),
          );
        }
        if (merged.gameStarted === undefined) merged.gameStarted = false;
        merged.hotseatReview = null;
        return merged;
      },
    },
  ),
);

export const clearSave = () => {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* storage unavailable */
  }
};

// ---- Flying-cash telemetry ------------------------------------------------------------
// ONE place that observes every cash movement: any mutation that changes a player's cash
// produces payer → recipient transfer events (multiple creditors are paired greedily).
// This covers rent, payroll, dividends, taxes, penalties, trades, loans and share trades
// without touching each individual action.
let moneyTransferSeq = 0;

const pruneMoneyTransfers = (list: MoneyTransfer[]): MoneyTransfer[] => {
  const cutoff = Date.now() - MONEY_TRANSFER_TTL_MS;
  return list.filter((t) => t.ts >= cutoff).slice(-32);
};

const latestEventLabel = (events: GameEvent[]): string => {
  const newest = events[events.length - 1]?.message ?? 'Hotovost';
  return newest.length > 64 ? `${newest.slice(0, 61)}…` : newest;
};

useGameStore.subscribe((state, prev) => {
  if (!prev || !state.gameStarted || !prev.gameStarted) return;
  if (state.players === prev.players) return;
  if (state.players.length !== prev.players.length) return;
  if (state.epoch !== prev.epoch) return; // reset — not a transaction

  const losers: { id: string; left: number }[] = [];
  const gainers: { id: string; left: number }[] = [];
  for (const player of state.players) {
    const before = prev.players.find((p) => p.id === player.id);
    if (!before) continue;
    const delta = player.cash - before.cash;
    if (delta < 0) losers.push({ id: player.id, left: -delta });
    else if (delta > 0) gainers.push({ id: player.id, left: delta });
  }
  if (losers.length === 0 && gainers.length === 0) return;
  losers.sort((a, b) => b.left - a.left);
  gainers.sort((a, b) => b.left - a.left);

  const reason = latestEventLabel(state.events);
  const events: MoneyTransfer[] = [];
  let li = 0;
  let gi = 0;
  // Greedily pair the biggest debtor with the biggest creditor (rent splits, trades…).
  while (li < losers.length && gi < gainers.length) {
    const amount = Math.min(losers[li].left, gainers[gi].left);
    if (amount >= 1) {
      events.push({ id: ++moneyTransferSeq, fromId: losers[li].id, toId: gainers[gi].id, amount, reason, ts: Date.now() });
    }
    losers[li].left -= amount;
    gainers[gi].left -= amount;
    if (losers[li].left < 1) li += 1;
    if (gainers[gi].left < 1) gi += 1;
  }
  // Anything left over moved to / from the bank and the board.
  for (; li < losers.length; li += 1) {
    if (losers[li].left >= 1) events.push({ id: ++moneyTransferSeq, fromId: losers[li].id, toId: BANK_ID, amount: losers[li].left, reason, ts: Date.now() });
  }
  for (; gi < gainers.length; gi += 1) {
    if (gainers[gi].left >= 1) events.push({ id: ++moneyTransferSeq, fromId: BANK_ID, toId: gainers[gi].id, amount: gainers[gi].left, reason, ts: Date.now() });
  }
  if (events.length === 0) return;
  // Writing ONLY moneyTransfers keeps the diff watcher out of its own recursion.
  useGameStore.setState({ moneyTransfers: pruneMoneyTransfers([...state.moneyTransfers, ...events]) });
});

export default useGameStore;








